import { _ as _extends } from './_rollupPluginBabelHelpers-e8fb2e5c.js';
import React__default, { createContext, memo, useEffect, useLayoutEffect, useState, useCallback, useMemo, isValidElement, cloneElement, useContext, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { D as Dropdown, I as InfiniteLoader, W as WindowScroller, A as AutoSizer, L as List, C as CellMeasurer } from './index-ed0d05f0.js';
import { C as CellMeasurerCache } from './CellMeasurerCache-343a8451.js';
import { c as classnames } from './index-031ff73c.js';
import { s as stopEvent, o as oneIsRequired, n as noop } from './index-a0e4e333.js';
import useMount from './hooks/useMount.js';
import useUpdate from './hooks/useUpdate.js';
import './configs-1fb8eed2.js';
import useThrottle from './hooks/useThrottle.js';
import Empty from './Empty/index.js';
import CustomScrollbar from './Scrollbar/index.js';
import BusyLoader from './BusyLoader/index.js';
import useMutationObserver from './hooks/useMutationObserver.js';
import Icon from './Icon/index.js';
import { a as createStore, r as reactIsExports } from './index-b1e429a7.js';
import Button from './Button/index.js';
import { s as styleInject } from './style-inject.es-746bb8ed.js';
import './dateValidation-67caec66.js';
import { g as guid } from './guid-8ddf77b3.js';
import ReactDOM__default from 'react-dom';
import SkeletonLoader from './SkeletonLoader/index.js';
import Copy from './Copy/index.js';
import { c as callAfterDelay } from './callAfterDelay-7272faca.js';
import Popover from './Popover/index.js';
import ExtendedInput from './ExtendedInput/index.js';
import Menu from './Menu/index.js';
import { d as debounce } from './debounce-4419bc2f.js';
import { j as jsxRuntimeExports } from './index-7b2f38c9.js';

/**
 * Create the React Context
 */ const DndContext = createContext({
    dragDropManager: undefined
});

/**
 * Use invariant() to assert state which your program assumes to be true.
 *
 * Provide sprintf-style format (only %s is supported) and arguments
 * to provide information about what broke and what you were
 * expecting.
 *
 * The invariant message will be stripped in production, but the invariant
 * will remain to ensure logic does not differ in production.
 */ function invariant(condition, format, ...args) {
    if (isProduction()) {
        if (format === undefined) {
            throw new Error('invariant requires an error message argument');
        }
    }
    if (!condition) {
        let error;
        if (format === undefined) {
            error = new Error('Minified exception occurred; use the non-minified dev environment ' + 'for the full error message and additional helpful warnings.');
        } else {
            let argIndex = 0;
            error = new Error(format.replace(/%s/g, function() {
                return args[argIndex++];
            }));
            error.name = 'Invariant Violation';
        }
        error.framesToPop = 1 // we don't care about invariant's own frame
        ;
        throw error;
    }
}
function isProduction() {
    return typeof process !== 'undefined' && process.env['NODE_ENV'] === 'production';
}

// cheap lodash replacements
/**
 * drop-in replacement for _.get
 * @param obj
 * @param path
 * @param defaultValue
 */ function get(obj, path, defaultValue) {
    return path.split('.').reduce((a, c)=>a && a[c] ? a[c] : defaultValue || null
    , obj);
}
/**
 * drop-in replacement for _.without
 */ function without$1(items, item) {
    return items.filter((i)=>i !== item
    );
}
/**
 * drop-in replacement for _.isString
 * @param input
 */ function isObject(input) {
    return typeof input === 'object';
}
/**
 * replacement for _.xor
 * @param itemsA
 * @param itemsB
 */ function xor(itemsA, itemsB) {
    const map = new Map();
    const insertItem = (item)=>{
        map.set(item, map.has(item) ? map.get(item) + 1 : 1);
    };
    itemsA.forEach(insertItem);
    itemsB.forEach(insertItem);
    const result = [];
    map.forEach((count, key)=>{
        if (count === 1) {
            result.push(key);
        }
    });
    return result;
}
/**
 * replacement for _.intersection
 * @param itemsA
 * @param itemsB
 */ function intersection(itemsA, itemsB) {
    return itemsA.filter((t)=>itemsB.indexOf(t) > -1
    );
}

const INIT_COORDS = 'dnd-core/INIT_COORDS';
const BEGIN_DRAG = 'dnd-core/BEGIN_DRAG';
const PUBLISH_DRAG_SOURCE = 'dnd-core/PUBLISH_DRAG_SOURCE';
const HOVER = 'dnd-core/HOVER';
const DROP = 'dnd-core/DROP';
const END_DRAG = 'dnd-core/END_DRAG';

function setClientOffset(clientOffset, sourceClientOffset) {
    return {
        type: INIT_COORDS,
        payload: {
            sourceClientOffset: sourceClientOffset || null,
            clientOffset: clientOffset || null
        }
    };
}

const ResetCoordinatesAction = {
    type: INIT_COORDS,
    payload: {
        clientOffset: null,
        sourceClientOffset: null
    }
};
function createBeginDrag(manager) {
    return function beginDrag(sourceIds = [], options = {
        publishSource: true
    }) {
        const { publishSource =true , clientOffset , getSourceClientOffset ,  } = options;
        const monitor = manager.getMonitor();
        const registry = manager.getRegistry();
        // Initialize the coordinates using the client offset
        manager.dispatch(setClientOffset(clientOffset));
        verifyInvariants$1(sourceIds, monitor, registry);
        // Get the draggable source
        const sourceId = getDraggableSource(sourceIds, monitor);
        if (sourceId == null) {
            manager.dispatch(ResetCoordinatesAction);
            return;
        }
        // Get the source client offset
        let sourceClientOffset = null;
        if (clientOffset) {
            if (!getSourceClientOffset) {
                throw new Error('getSourceClientOffset must be defined');
            }
            verifyGetSourceClientOffsetIsFunction(getSourceClientOffset);
            sourceClientOffset = getSourceClientOffset(sourceId);
        }
        // Initialize the full coordinates
        manager.dispatch(setClientOffset(clientOffset, sourceClientOffset));
        const source = registry.getSource(sourceId);
        const item = source.beginDrag(monitor, sourceId);
        // If source.beginDrag returns null, this is an indicator to cancel the drag
        if (item == null) {
            return undefined;
        }
        verifyItemIsObject(item);
        registry.pinSource(sourceId);
        const itemType = registry.getSourceType(sourceId);
        return {
            type: BEGIN_DRAG,
            payload: {
                itemType,
                item,
                sourceId,
                clientOffset: clientOffset || null,
                sourceClientOffset: sourceClientOffset || null,
                isSourcePublic: !!publishSource
            }
        };
    };
}
function verifyInvariants$1(sourceIds, monitor, registry) {
    invariant(!monitor.isDragging(), 'Cannot call beginDrag while dragging.');
    sourceIds.forEach(function(sourceId) {
        invariant(registry.getSource(sourceId), 'Expected sourceIds to be registered.');
    });
}
function verifyGetSourceClientOffsetIsFunction(getSourceClientOffset) {
    invariant(typeof getSourceClientOffset === 'function', 'When clientOffset is provided, getSourceClientOffset must be a function.');
}
function verifyItemIsObject(item) {
    invariant(isObject(item), 'Item must be an object.');
}
function getDraggableSource(sourceIds, monitor) {
    let sourceId = null;
    for(let i = sourceIds.length - 1; i >= 0; i--){
        if (monitor.canDragSource(sourceIds[i])) {
            sourceId = sourceIds[i];
            break;
        }
    }
    return sourceId;
}

function _defineProperty$4(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$4(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$4(target, key, source[key]);
        });
    }
    return target;
}
function createDrop(manager) {
    return function drop(options = {}) {
        const monitor = manager.getMonitor();
        const registry = manager.getRegistry();
        verifyInvariants(monitor);
        const targetIds = getDroppableTargets(monitor);
        // Multiple actions are dispatched here, which is why this doesn't return an action
        targetIds.forEach((targetId, index)=>{
            const dropResult = determineDropResult(targetId, index, registry, monitor);
            const action = {
                type: DROP,
                payload: {
                    dropResult: _objectSpread$4({}, options, dropResult)
                }
            };
            manager.dispatch(action);
        });
    };
}
function verifyInvariants(monitor) {
    invariant(monitor.isDragging(), 'Cannot call drop while not dragging.');
    invariant(!monitor.didDrop(), 'Cannot call drop twice during one drag operation.');
}
function determineDropResult(targetId, index, registry, monitor) {
    const target = registry.getTarget(targetId);
    let dropResult = target ? target.drop(monitor, targetId) : undefined;
    verifyDropResultType(dropResult);
    if (typeof dropResult === 'undefined') {
        dropResult = index === 0 ? {} : monitor.getDropResult();
    }
    return dropResult;
}
function verifyDropResultType(dropResult) {
    invariant(typeof dropResult === 'undefined' || isObject(dropResult), 'Drop result must either be an object or undefined.');
}
function getDroppableTargets(monitor) {
    const targetIds = monitor.getTargetIds().filter(monitor.canDropOnTarget, monitor);
    targetIds.reverse();
    return targetIds;
}

function createEndDrag(manager) {
    return function endDrag() {
        const monitor = manager.getMonitor();
        const registry = manager.getRegistry();
        verifyIsDragging(monitor);
        const sourceId = monitor.getSourceId();
        if (sourceId != null) {
            const source = registry.getSource(sourceId, true);
            source.endDrag(monitor, sourceId);
            registry.unpinSource();
        }
        return {
            type: END_DRAG
        };
    };
}
function verifyIsDragging(monitor) {
    invariant(monitor.isDragging(), 'Cannot call endDrag while not dragging.');
}

function matchesType(targetType, draggedItemType) {
    if (draggedItemType === null) {
        return targetType === null;
    }
    return Array.isArray(targetType) ? targetType.some((t)=>t === draggedItemType
    ) : targetType === draggedItemType;
}

function createHover(manager) {
    return function hover(targetIdsArg, { clientOffset  } = {}) {
        verifyTargetIdsIsArray(targetIdsArg);
        const targetIds = targetIdsArg.slice(0);
        const monitor = manager.getMonitor();
        const registry = manager.getRegistry();
        const draggedItemType = monitor.getItemType();
        removeNonMatchingTargetIds(targetIds, registry, draggedItemType);
        checkInvariants(targetIds, monitor, registry);
        hoverAllTargets(targetIds, monitor, registry);
        return {
            type: HOVER,
            payload: {
                targetIds,
                clientOffset: clientOffset || null
            }
        };
    };
}
function verifyTargetIdsIsArray(targetIdsArg) {
    invariant(Array.isArray(targetIdsArg), 'Expected targetIds to be an array.');
}
function checkInvariants(targetIds, monitor, registry) {
    invariant(monitor.isDragging(), 'Cannot call hover while not dragging.');
    invariant(!monitor.didDrop(), 'Cannot call hover after drop.');
    for(let i = 0; i < targetIds.length; i++){
        const targetId = targetIds[i];
        invariant(targetIds.lastIndexOf(targetId) === i, 'Expected targetIds to be unique in the passed array.');
        const target = registry.getTarget(targetId);
        invariant(target, 'Expected targetIds to be registered.');
    }
}
function removeNonMatchingTargetIds(targetIds, registry, draggedItemType) {
    // Remove those targetIds that don't match the targetType.  This
    // fixes shallow isOver which would only be non-shallow because of
    // non-matching targets.
    for(let i = targetIds.length - 1; i >= 0; i--){
        const targetId = targetIds[i];
        const targetType = registry.getTargetType(targetId);
        if (!matchesType(targetType, draggedItemType)) {
            targetIds.splice(i, 1);
        }
    }
}
function hoverAllTargets(targetIds, monitor, registry) {
    // Finally call hover on all matching targets.
    targetIds.forEach(function(targetId) {
        const target = registry.getTarget(targetId);
        target.hover(monitor, targetId);
    });
}

function createPublishDragSource(manager) {
    return function publishDragSource() {
        const monitor = manager.getMonitor();
        if (monitor.isDragging()) {
            return {
                type: PUBLISH_DRAG_SOURCE
            };
        }
        return;
    };
}

function createDragDropActions(manager) {
    return {
        beginDrag: createBeginDrag(manager),
        publishDragSource: createPublishDragSource(manager),
        hover: createHover(manager),
        drop: createDrop(manager),
        endDrag: createEndDrag(manager)
    };
}

class DragDropManagerImpl {
    receiveBackend(backend) {
        this.backend = backend;
    }
    getMonitor() {
        return this.monitor;
    }
    getBackend() {
        return this.backend;
    }
    getRegistry() {
        return this.monitor.registry;
    }
    getActions() {
        /* eslint-disable-next-line @typescript-eslint/no-this-alias */ const manager = this;
        const { dispatch  } = this.store;
        function bindActionCreator(actionCreator) {
            return (...args)=>{
                const action = actionCreator.apply(manager, args);
                if (typeof action !== 'undefined') {
                    dispatch(action);
                }
            };
        }
        const actions = createDragDropActions(this);
        return Object.keys(actions).reduce((boundActions, key)=>{
            const action = actions[key];
            boundActions[key] = bindActionCreator(action);
            return boundActions;
        }, {});
    }
    dispatch(action) {
        this.store.dispatch(action);
    }
    constructor(store, monitor){
        this.isSetUp = false;
        this.handleRefCountChange = ()=>{
            const shouldSetUp = this.store.getState().refCount > 0;
            if (this.backend) {
                if (shouldSetUp && !this.isSetUp) {
                    this.backend.setup();
                    this.isSetUp = true;
                } else if (!shouldSetUp && this.isSetUp) {
                    this.backend.teardown();
                    this.isSetUp = false;
                }
            }
        };
        this.store = store;
        this.monitor = monitor;
        store.subscribe(this.handleRefCountChange);
    }
}

/**
 * Coordinate addition
 * @param a The first coordinate
 * @param b The second coordinate
 */ function add(a, b) {
    return {
        x: a.x + b.x,
        y: a.y + b.y
    };
}
/**
 * Coordinate subtraction
 * @param a The first coordinate
 * @param b The second coordinate
 */ function subtract(a, b) {
    return {
        x: a.x - b.x,
        y: a.y - b.y
    };
}
/**
 * Returns the cartesian distance of the drag source component's position, based on its position
 * at the time when the current drag operation has started, and the movement difference.
 *
 * Returns null if no item is being dragged.
 *
 * @param state The offset state to compute from
 */ function getSourceClientOffset(state) {
    const { clientOffset , initialClientOffset , initialSourceClientOffset  } = state;
    if (!clientOffset || !initialClientOffset || !initialSourceClientOffset) {
        return null;
    }
    return subtract(add(clientOffset, initialSourceClientOffset), initialClientOffset);
}
/**
 * Determines the x,y offset between the client offset and the initial client offset
 *
 * @param state The offset state to compute from
 */ function getDifferenceFromInitialOffset(state) {
    const { clientOffset , initialClientOffset  } = state;
    if (!clientOffset || !initialClientOffset) {
        return null;
    }
    return subtract(clientOffset, initialClientOffset);
}

const NONE = [];
const ALL = [];
NONE.__IS_NONE__ = true;
ALL.__IS_ALL__ = true;
/**
 * Determines if the given handler IDs are dirty or not.
 *
 * @param dirtyIds The set of dirty handler ids
 * @param handlerIds The set of handler ids to check
 */ function areDirty(dirtyIds, handlerIds) {
    if (dirtyIds === NONE) {
        return false;
    }
    if (dirtyIds === ALL || typeof handlerIds === 'undefined') {
        return true;
    }
    const commonIds = intersection(handlerIds, dirtyIds);
    return commonIds.length > 0;
}

class DragDropMonitorImpl {
    subscribeToStateChange(listener, options = {}) {
        const { handlerIds  } = options;
        invariant(typeof listener === 'function', 'listener must be a function.');
        invariant(typeof handlerIds === 'undefined' || Array.isArray(handlerIds), 'handlerIds, when specified, must be an array of strings.');
        let prevStateId = this.store.getState().stateId;
        const handleChange = ()=>{
            const state = this.store.getState();
            const currentStateId = state.stateId;
            try {
                const canSkipListener = currentStateId === prevStateId || currentStateId === prevStateId + 1 && !areDirty(state.dirtyHandlerIds, handlerIds);
                if (!canSkipListener) {
                    listener();
                }
            } finally{
                prevStateId = currentStateId;
            }
        };
        return this.store.subscribe(handleChange);
    }
    subscribeToOffsetChange(listener) {
        invariant(typeof listener === 'function', 'listener must be a function.');
        let previousState = this.store.getState().dragOffset;
        const handleChange = ()=>{
            const nextState = this.store.getState().dragOffset;
            if (nextState === previousState) {
                return;
            }
            previousState = nextState;
            listener();
        };
        return this.store.subscribe(handleChange);
    }
    canDragSource(sourceId) {
        if (!sourceId) {
            return false;
        }
        const source = this.registry.getSource(sourceId);
        invariant(source, `Expected to find a valid source. sourceId=${sourceId}`);
        if (this.isDragging()) {
            return false;
        }
        return source.canDrag(this, sourceId);
    }
    canDropOnTarget(targetId) {
        // undefined on initial render
        if (!targetId) {
            return false;
        }
        const target = this.registry.getTarget(targetId);
        invariant(target, `Expected to find a valid target. targetId=${targetId}`);
        if (!this.isDragging() || this.didDrop()) {
            return false;
        }
        const targetType = this.registry.getTargetType(targetId);
        const draggedItemType = this.getItemType();
        return matchesType(targetType, draggedItemType) && target.canDrop(this, targetId);
    }
    isDragging() {
        return Boolean(this.getItemType());
    }
    isDraggingSource(sourceId) {
        // undefined on initial render
        if (!sourceId) {
            return false;
        }
        const source = this.registry.getSource(sourceId, true);
        invariant(source, `Expected to find a valid source. sourceId=${sourceId}`);
        if (!this.isDragging() || !this.isSourcePublic()) {
            return false;
        }
        const sourceType = this.registry.getSourceType(sourceId);
        const draggedItemType = this.getItemType();
        if (sourceType !== draggedItemType) {
            return false;
        }
        return source.isDragging(this, sourceId);
    }
    isOverTarget(targetId, options = {
        shallow: false
    }) {
        // undefined on initial render
        if (!targetId) {
            return false;
        }
        const { shallow  } = options;
        if (!this.isDragging()) {
            return false;
        }
        const targetType = this.registry.getTargetType(targetId);
        const draggedItemType = this.getItemType();
        if (draggedItemType && !matchesType(targetType, draggedItemType)) {
            return false;
        }
        const targetIds = this.getTargetIds();
        if (!targetIds.length) {
            return false;
        }
        const index = targetIds.indexOf(targetId);
        if (shallow) {
            return index === targetIds.length - 1;
        } else {
            return index > -1;
        }
    }
    getItemType() {
        return this.store.getState().dragOperation.itemType;
    }
    getItem() {
        return this.store.getState().dragOperation.item;
    }
    getSourceId() {
        return this.store.getState().dragOperation.sourceId;
    }
    getTargetIds() {
        return this.store.getState().dragOperation.targetIds;
    }
    getDropResult() {
        return this.store.getState().dragOperation.dropResult;
    }
    didDrop() {
        return this.store.getState().dragOperation.didDrop;
    }
    isSourcePublic() {
        return Boolean(this.store.getState().dragOperation.isSourcePublic);
    }
    getInitialClientOffset() {
        return this.store.getState().dragOffset.initialClientOffset;
    }
    getInitialSourceClientOffset() {
        return this.store.getState().dragOffset.initialSourceClientOffset;
    }
    getClientOffset() {
        return this.store.getState().dragOffset.clientOffset;
    }
    getSourceClientOffset() {
        return getSourceClientOffset(this.store.getState().dragOffset);
    }
    getDifferenceFromInitialOffset() {
        return getDifferenceFromInitialOffset(this.store.getState().dragOffset);
    }
    constructor(store, registry){
        this.store = store;
        this.registry = registry;
    }
}

// Safari 6 and 6.1 for desktop, iPad, and iPhone are the only browsers that
// have WebKitMutationObserver but not un-prefixed MutationObserver.
// Must use `global` or `self` instead of `window` to work in both frames and web
// workers. `global` is a provision of Browserify, Mr, Mrs, or Mop.
/* globals self */ const scope = typeof global !== 'undefined' ? global : self;
const BrowserMutationObserver = scope.MutationObserver || scope.WebKitMutationObserver;
function makeRequestCallFromTimer(callback) {
    return function requestCall() {
        // We dispatch a timeout with a specified delay of 0 for engines that
        // can reliably accommodate that request. This will usually be snapped
        // to a 4 milisecond delay, but once we're flushing, there's no delay
        // between events.
        const timeoutHandle = setTimeout(handleTimer, 0);
        // However, since this timer gets frequently dropped in Firefox
        // workers, we enlist an interval handle that will try to fire
        // an event 20 times per second until it succeeds.
        const intervalHandle = setInterval(handleTimer, 50);
        function handleTimer() {
            // Whichever timer succeeds will cancel both timers and
            // execute the callback.
            clearTimeout(timeoutHandle);
            clearInterval(intervalHandle);
            callback();
        }
    };
}
// To request a high priority event, we induce a mutation observer by toggling
// the text of a text node between "1" and "-1".
function makeRequestCallFromMutationObserver(callback) {
    let toggle = 1;
    const observer = new BrowserMutationObserver(callback);
    const node = document.createTextNode('');
    observer.observe(node, {
        characterData: true
    });
    return function requestCall() {
        toggle = -toggle;
        node.data = toggle;
    };
}
const makeRequestCall = typeof BrowserMutationObserver === 'function' ? // reliably everywhere they are implemented.
// They are implemented in all modern browsers.
//
// - Android 4-4.3
// - Chrome 26-34
// - Firefox 14-29
// - Internet Explorer 11
// - iPad Safari 6-7.1
// - iPhone Safari 7-7.1
// - Safari 6-7
makeRequestCallFromMutationObserver : // task queue, are implemented in Internet Explorer 10, Safari 5.0-1, and Opera
// 11-12, and in web workers in many engines.
// Although message channels yield to any queued rendering and IO tasks, they
// would be better than imposing the 4ms delay of timers.
// However, they do not work reliably in Internet Explorer or Safari.
// Internet Explorer 10 is the only browser that has setImmediate but does
// not have MutationObservers.
// Although setImmediate yields to the browser's renderer, it would be
// preferrable to falling back to setTimeout since it does not have
// the minimum 4ms penalty.
// Unfortunately there appears to be a bug in Internet Explorer 10 Mobile (and
// Desktop to a lesser extent) that renders both setImmediate and
// MessageChannel useless for the purposes of ASAP.
// https://github.com/kriskowal/q/issues/396
// Timers are implemented universally.
// We fall back to timers in workers in most engines, and in foreground
// contexts in the following browsers.
// However, note that even this simple case requires nuances to operate in a
// broad spectrum of browsers.
//
// - Firefox 3-13
// - Internet Explorer 6-9
// - iPad Safari 4.3
// - Lynx 2.8.7
makeRequestCallFromTimer;

class AsapQueue {
    // Use the fastest means possible to execute a task in its own turn, with
    // priority over other events including IO, animation, reflow, and redraw
    // events in browsers.
    //
    // An exception thrown by a task will permanently interrupt the processing of
    // subsequent tasks. The higher level `asap` function ensures that if an
    // exception is thrown by a task, that the task queue will continue flushing as
    // soon as possible, but if you use `rawAsap` directly, you are responsible to
    // either ensure that no exceptions are thrown from your task, or to manually
    // call `rawAsap.requestFlush` if an exception is thrown.
    enqueueTask(task) {
        const { queue: q , requestFlush  } = this;
        if (!q.length) {
            requestFlush();
            this.flushing = true;
        }
        // Equivalent to push, but avoids a function call.
        q[q.length] = task;
    }
    constructor(){
        this.queue = [];
        // We queue errors to ensure they are thrown in right order (FIFO).
        // Array-as-queue is good enough here, since we are just dealing with exceptions.
        this.pendingErrors = [];
        // Once a flush has been requested, no further calls to `requestFlush` are
        // necessary until the next `flush` completes.
        // @ts-ignore
        this.flushing = false;
        // The position of the next task to execute in the task queue. This is
        // preserved between calls to `flush` so that it can be resumed if
        // a task throws an exception.
        this.index = 0;
        // If a task schedules additional tasks recursively, the task queue can grow
        // unbounded. To prevent memory exhaustion, the task queue will periodically
        // truncate already-completed tasks.
        this.capacity = 1024;
        // The flush function processes all tasks that have been scheduled with
        // `rawAsap` unless and until one of those tasks throws an exception.
        // If a task throws an exception, `flush` ensures that its state will remain
        // consistent and will resume where it left off when called again.
        // However, `flush` does not make any arrangements to be called again if an
        // exception is thrown.
        this.flush = ()=>{
            const { queue: q  } = this;
            while(this.index < q.length){
                const currentIndex = this.index;
                // Advance the index before calling the task. This ensures that we will
                // begin flushing on the next task the task throws an error.
                this.index++;
                q[currentIndex].call();
                // Prevent leaking memory for long chains of recursive calls to `asap`.
                // If we call `asap` within tasks scheduled by `asap`, the queue will
                // grow, but to avoid an O(n) walk for every task we execute, we don't
                // shift tasks off the queue after they have been executed.
                // Instead, we periodically shift 1024 tasks off the queue.
                if (this.index > this.capacity) {
                    // Manually shift all values starting at the index back to the
                    // beginning of the queue.
                    for(let scan = 0, newLength = q.length - this.index; scan < newLength; scan++){
                        q[scan] = q[scan + this.index];
                    }
                    q.length -= this.index;
                    this.index = 0;
                }
            }
            q.length = 0;
            this.index = 0;
            this.flushing = false;
        };
        // In a web browser, exceptions are not fatal. However, to avoid
        // slowing down the queue of pending tasks, we rethrow the error in a
        // lower priority turn.
        this.registerPendingError = (err)=>{
            this.pendingErrors.push(err);
            this.requestErrorThrow();
        };
        // `requestFlush` requests that the high priority event queue be flushed as
        // soon as possible.
        // This is useful to prevent an error thrown in a task from stalling the event
        // queue if the exception handled by Node.js’s
        // `process.on("uncaughtException")` or by a domain.
        // `requestFlush` is implemented using a strategy based on data collected from
        // every available SauceLabs Selenium web driver worker at time of writing.
        // https://docs.google.com/spreadsheets/d/1mG-5UYGup5qxGdEMWkhP6BWCz053NUb2E1QoUTU16uA/edit#gid=783724593
        this.requestFlush = makeRequestCall(this.flush);
        this.requestErrorThrow = makeRequestCallFromTimer(()=>{
            // Throw first error
            if (this.pendingErrors.length) {
                throw this.pendingErrors.shift();
            }
        });
    }
} // The message channel technique was discovered by Malte Ubl and was the
 // original foundation for this library.
 // http://www.nonblocking.io/2011/06/windownexttick.html
 // Safari 6.0.5 (at least) intermittently fails to create message ports on a
 // page's first load. Thankfully, this version of Safari supports
 // MutationObservers, so we don't need to fall back in that case.
 // function makeRequestCallFromMessageChannel(callback) {
 //     var channel = new MessageChannel();
 //     channel.port1.onmessage = callback;
 //     return function requestCall() {
 //         channel.port2.postMessage(0);
 //     };
 // }
 // For reasons explained above, we are also unable to use `setImmediate`
 // under any circumstances.
 // Even if we were, there is another bug in Internet Explorer 10.
 // It is not sufficient to assign `setImmediate` to `requestFlush` because
 // `setImmediate` must be called *by name* and therefore must be wrapped in a
 // closure.
 // Never forget.
 // function makeRequestCallFromSetImmediate(callback) {
 //     return function requestCall() {
 //         setImmediate(callback);
 //     };
 // }
 // Safari 6.0 has a problem where timers will get lost while the user is
 // scrolling. This problem does not impact ASAP because Safari 6.0 supports
 // mutation observers, so that implementation is used instead.
 // However, if we ever elect to use timers in Safari, the prevalent work-around
 // is to add a scroll event listener that calls for a flush.
 // `setTimeout` does not call the passed callback if the delay is less than
 // approximately 7 in web workers in Firefox 8 through 18, and sometimes not
 // even then.
 // This is for `asap.js` only.
 // Its name will be periodically randomized to break any code that depends on
 // // its existence.
 // rawAsap.makeRequestCallFromTimer = makeRequestCallFromTimer
 // ASAP was originally a nextTick shim included in Q. This was factored out
 // into this ASAP package. It was later adapted to RSVP which made further
 // amendments. These decisions, particularly to marginalize MessageChannel and
 // to capture the MutationObserver implementation in a closure, were integrated
 // back into ASAP proper.
 // https://github.com/tildeio/rsvp.js/blob/cddf7232546a9cf858524b75cde6f9edf72620a7/lib/rsvp/asap.js

// `call`, just like a function.
class RawTask {
    call() {
        try {
            this.task && this.task();
        } catch (error) {
            this.onError(error);
        } finally{
            this.task = null;
            this.release(this);
        }
    }
    constructor(onError, release){
        this.onError = onError;
        this.release = release;
        this.task = null;
    }
}

class TaskFactory {
    create(task) {
        const tasks = this.freeTasks;
        const t1 = tasks.length ? tasks.pop() : new RawTask(this.onError, (t)=>tasks[tasks.length] = t
        );
        t1.task = task;
        return t1;
    }
    constructor(onError){
        this.onError = onError;
        this.freeTasks = [];
    }
}

const asapQueue = new AsapQueue();
const taskFactory = new TaskFactory(asapQueue.registerPendingError);
/**
 * Calls a task as soon as possible after returning, in its own event, with priority
 * over other events like animation, reflow, and repaint. An error thrown from an
 * event will not interrupt, nor even substantially slow down the processing of
 * other events, but will be rather postponed to a lower priority event.
 * @param {{call}} task A callable object, typically a function that takes no
 * arguments.
 */ function asap(task) {
    asapQueue.enqueueTask(taskFactory.create(task));
}

const ADD_SOURCE = 'dnd-core/ADD_SOURCE';
const ADD_TARGET = 'dnd-core/ADD_TARGET';
const REMOVE_SOURCE = 'dnd-core/REMOVE_SOURCE';
const REMOVE_TARGET = 'dnd-core/REMOVE_TARGET';
function addSource(sourceId) {
    return {
        type: ADD_SOURCE,
        payload: {
            sourceId
        }
    };
}
function addTarget(targetId) {
    return {
        type: ADD_TARGET,
        payload: {
            targetId
        }
    };
}
function removeSource(sourceId) {
    return {
        type: REMOVE_SOURCE,
        payload: {
            sourceId
        }
    };
}
function removeTarget(targetId) {
    return {
        type: REMOVE_TARGET,
        payload: {
            targetId
        }
    };
}

function validateSourceContract(source) {
    invariant(typeof source.canDrag === 'function', 'Expected canDrag to be a function.');
    invariant(typeof source.beginDrag === 'function', 'Expected beginDrag to be a function.');
    invariant(typeof source.endDrag === 'function', 'Expected endDrag to be a function.');
}
function validateTargetContract(target) {
    invariant(typeof target.canDrop === 'function', 'Expected canDrop to be a function.');
    invariant(typeof target.hover === 'function', 'Expected hover to be a function.');
    invariant(typeof target.drop === 'function', 'Expected beginDrag to be a function.');
}
function validateType(type, allowArray) {
    if (allowArray && Array.isArray(type)) {
        type.forEach((t)=>validateType(t, false)
        );
        return;
    }
    invariant(typeof type === 'string' || typeof type === 'symbol', allowArray ? 'Type can only be a string, a symbol, or an array of either.' : 'Type can only be a string or a symbol.');
}

var HandlerRole;
(function(HandlerRole) {
    HandlerRole["SOURCE"] = "SOURCE";
    HandlerRole["TARGET"] = "TARGET";
})(HandlerRole || (HandlerRole = {}));

let nextUniqueId = 0;
function getNextUniqueId() {
    return nextUniqueId++;
}

function getNextHandlerId(role) {
    const id = getNextUniqueId().toString();
    switch(role){
        case HandlerRole.SOURCE:
            return `S${id}`;
        case HandlerRole.TARGET:
            return `T${id}`;
        default:
            throw new Error(`Unknown Handler Role: ${role}`);
    }
}
function parseRoleFromHandlerId(handlerId) {
    switch(handlerId[0]){
        case 'S':
            return HandlerRole.SOURCE;
        case 'T':
            return HandlerRole.TARGET;
        default:
            throw new Error(`Cannot parse handler ID: ${handlerId}`);
    }
}
function mapContainsValue(map, searchValue) {
    const entries = map.entries();
    let isDone = false;
    do {
        const { done , value: [, value] ,  } = entries.next();
        if (value === searchValue) {
            return true;
        }
        isDone = !!done;
    }while (!isDone)
    return false;
}
class HandlerRegistryImpl {
    addSource(type, source) {
        validateType(type);
        validateSourceContract(source);
        const sourceId = this.addHandler(HandlerRole.SOURCE, type, source);
        this.store.dispatch(addSource(sourceId));
        return sourceId;
    }
    addTarget(type, target) {
        validateType(type, true);
        validateTargetContract(target);
        const targetId = this.addHandler(HandlerRole.TARGET, type, target);
        this.store.dispatch(addTarget(targetId));
        return targetId;
    }
    containsHandler(handler) {
        return mapContainsValue(this.dragSources, handler) || mapContainsValue(this.dropTargets, handler);
    }
    getSource(sourceId, includePinned = false) {
        invariant(this.isSourceId(sourceId), 'Expected a valid source ID.');
        const isPinned = includePinned && sourceId === this.pinnedSourceId;
        const source = isPinned ? this.pinnedSource : this.dragSources.get(sourceId);
        return source;
    }
    getTarget(targetId) {
        invariant(this.isTargetId(targetId), 'Expected a valid target ID.');
        return this.dropTargets.get(targetId);
    }
    getSourceType(sourceId) {
        invariant(this.isSourceId(sourceId), 'Expected a valid source ID.');
        return this.types.get(sourceId);
    }
    getTargetType(targetId) {
        invariant(this.isTargetId(targetId), 'Expected a valid target ID.');
        return this.types.get(targetId);
    }
    isSourceId(handlerId) {
        const role = parseRoleFromHandlerId(handlerId);
        return role === HandlerRole.SOURCE;
    }
    isTargetId(handlerId) {
        const role = parseRoleFromHandlerId(handlerId);
        return role === HandlerRole.TARGET;
    }
    removeSource(sourceId) {
        invariant(this.getSource(sourceId), 'Expected an existing source.');
        this.store.dispatch(removeSource(sourceId));
        asap(()=>{
            this.dragSources.delete(sourceId);
            this.types.delete(sourceId);
        });
    }
    removeTarget(targetId) {
        invariant(this.getTarget(targetId), 'Expected an existing target.');
        this.store.dispatch(removeTarget(targetId));
        this.dropTargets.delete(targetId);
        this.types.delete(targetId);
    }
    pinSource(sourceId) {
        const source = this.getSource(sourceId);
        invariant(source, 'Expected an existing source.');
        this.pinnedSourceId = sourceId;
        this.pinnedSource = source;
    }
    unpinSource() {
        invariant(this.pinnedSource, 'No source is pinned at the time.');
        this.pinnedSourceId = null;
        this.pinnedSource = null;
    }
    addHandler(role, type, handler) {
        const id = getNextHandlerId(role);
        this.types.set(id, type);
        if (role === HandlerRole.SOURCE) {
            this.dragSources.set(id, handler);
        } else if (role === HandlerRole.TARGET) {
            this.dropTargets.set(id, handler);
        }
        return id;
    }
    constructor(store){
        this.types = new Map();
        this.dragSources = new Map();
        this.dropTargets = new Map();
        this.pinnedSourceId = null;
        this.pinnedSource = null;
        this.store = store;
    }
}

const strictEquality = (a, b)=>a === b
;
/**
 * Determine if two cartesian coordinate offsets are equal
 * @param offsetA
 * @param offsetB
 */ function areCoordsEqual(offsetA, offsetB) {
    if (!offsetA && !offsetB) {
        return true;
    } else if (!offsetA || !offsetB) {
        return false;
    } else {
        return offsetA.x === offsetB.x && offsetA.y === offsetB.y;
    }
}
/**
 * Determines if two arrays of items are equal
 * @param a The first array of items
 * @param b The second array of items
 */ function areArraysEqual(a, b, isEqual = strictEquality) {
    if (a.length !== b.length) {
        return false;
    }
    for(let i = 0; i < a.length; ++i){
        if (!isEqual(a[i], b[i])) {
            return false;
        }
    }
    return true;
}

function reduce$5(// eslint-disable-next-line @typescript-eslint/no-unused-vars
_state = NONE, action) {
    switch(action.type){
        case HOVER:
            break;
        case ADD_SOURCE:
        case ADD_TARGET:
        case REMOVE_TARGET:
        case REMOVE_SOURCE:
            return NONE;
        case BEGIN_DRAG:
        case PUBLISH_DRAG_SOURCE:
        case END_DRAG:
        case DROP:
        default:
            return ALL;
    }
    const { targetIds =[] , prevTargetIds =[]  } = action.payload;
    const result = xor(targetIds, prevTargetIds);
    const didChange = result.length > 0 || !areArraysEqual(targetIds, prevTargetIds);
    if (!didChange) {
        return NONE;
    }
    // Check the target ids at the innermost position. If they are valid, add them
    // to the result
    const prevInnermostTargetId = prevTargetIds[prevTargetIds.length - 1];
    const innermostTargetId = targetIds[targetIds.length - 1];
    if (prevInnermostTargetId !== innermostTargetId) {
        if (prevInnermostTargetId) {
            result.push(prevInnermostTargetId);
        }
        if (innermostTargetId) {
            result.push(innermostTargetId);
        }
    }
    return result;
}

function _defineProperty$3(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$3(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$3(target, key, source[key]);
        });
    }
    return target;
}
const initialState$1 = {
    initialSourceClientOffset: null,
    initialClientOffset: null,
    clientOffset: null
};
function reduce$4(state = initialState$1, action) {
    const { payload  } = action;
    switch(action.type){
        case INIT_COORDS:
        case BEGIN_DRAG:
            return {
                initialSourceClientOffset: payload.sourceClientOffset,
                initialClientOffset: payload.clientOffset,
                clientOffset: payload.clientOffset
            };
        case HOVER:
            if (areCoordsEqual(state.clientOffset, payload.clientOffset)) {
                return state;
            }
            return _objectSpread$3({}, state, {
                clientOffset: payload.clientOffset
            });
        case END_DRAG:
        case DROP:
            return initialState$1;
        default:
            return state;
    }
}

function _defineProperty$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$2(target, key, source[key]);
        });
    }
    return target;
}
const initialState = {
    itemType: null,
    item: null,
    sourceId: null,
    targetIds: [],
    dropResult: null,
    didDrop: false,
    isSourcePublic: null
};
function reduce$3(state = initialState, action) {
    const { payload  } = action;
    switch(action.type){
        case BEGIN_DRAG:
            return _objectSpread$2({}, state, {
                itemType: payload.itemType,
                item: payload.item,
                sourceId: payload.sourceId,
                isSourcePublic: payload.isSourcePublic,
                dropResult: null,
                didDrop: false
            });
        case PUBLISH_DRAG_SOURCE:
            return _objectSpread$2({}, state, {
                isSourcePublic: true
            });
        case HOVER:
            return _objectSpread$2({}, state, {
                targetIds: payload.targetIds
            });
        case REMOVE_TARGET:
            if (state.targetIds.indexOf(payload.targetId) === -1) {
                return state;
            }
            return _objectSpread$2({}, state, {
                targetIds: without$1(state.targetIds, payload.targetId)
            });
        case DROP:
            return _objectSpread$2({}, state, {
                dropResult: payload.dropResult,
                didDrop: true,
                targetIds: []
            });
        case END_DRAG:
            return _objectSpread$2({}, state, {
                itemType: null,
                item: null,
                sourceId: null,
                dropResult: null,
                didDrop: false,
                isSourcePublic: null,
                targetIds: []
            });
        default:
            return state;
    }
}

function reduce$2(state = 0, action) {
    switch(action.type){
        case ADD_SOURCE:
        case ADD_TARGET:
            return state + 1;
        case REMOVE_SOURCE:
        case REMOVE_TARGET:
            return state - 1;
        default:
            return state;
    }
}

function reduce$1(state = 0) {
    return state + 1;
}

function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
function reduce(state = {}, action) {
    return {
        dirtyHandlerIds: reduce$5(state.dirtyHandlerIds, {
            type: action.type,
            payload: _objectSpread$1({}, action.payload, {
                prevTargetIds: get(state, 'dragOperation.targetIds', [])
            })
        }),
        dragOffset: reduce$4(state.dragOffset, action),
        refCount: reduce$2(state.refCount, action),
        dragOperation: reduce$3(state.dragOperation, action),
        stateId: reduce$1(state.stateId)
    };
}

function createDragDropManager(backendFactory, globalContext = undefined, backendOptions = {}, debugMode = false) {
    const store = makeStoreInstance(debugMode);
    const monitor = new DragDropMonitorImpl(store, new HandlerRegistryImpl(store));
    const manager = new DragDropManagerImpl(store, monitor);
    const backend = backendFactory(manager, globalContext, backendOptions);
    manager.receiveBackend(backend);
    return manager;
}
function makeStoreInstance(debugMode) {
    // TODO: if we ever make a react-native version of this,
    // we'll need to consider how to pull off dev-tooling
    const reduxDevTools = typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION__;
    return createStore(reduce, debugMode && reduxDevTools && reduxDevTools({
        name: 'dnd-core',
        instanceId: 'dnd-core'
    }));
}

function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
let refCount = 0;
const INSTANCE_SYM = Symbol.for('__REACT_DND_CONTEXT_INSTANCE__');
var DndProvider = /*#__PURE__*/ memo(function DndProvider(_param) {
    var { children  } = _param, props = _objectWithoutProperties(_param, [
        "children"
    ]);
    const [manager, isGlobalInstance] = getDndContextValue(props) // memoized from props
    ;
    /**
		 * If the global context was used to store the DND context
		 * then where theres no more references to it we should
		 * clean it up to avoid memory leaks
		 */ useEffect(()=>{
        if (isGlobalInstance) {
            const context = getGlobalContext();
            ++refCount;
            return ()=>{
                if (--refCount === 0) {
                    context[INSTANCE_SYM] = null;
                }
            };
        }
        return;
    }, []);
    return /*#__PURE__*/ jsxRuntimeExports.jsx(DndContext.Provider, {
        value: manager,
        children: children
    });
});
function getDndContextValue(props) {
    if ('manager' in props) {
        const manager = {
            dragDropManager: props.manager
        };
        return [
            manager,
            false
        ];
    }
    const manager = createSingletonDndContext(props.backend, props.context, props.options, props.debugMode);
    const isGlobalInstance = !props.context;
    return [
        manager,
        isGlobalInstance
    ];
}
function createSingletonDndContext(backend, context = getGlobalContext(), options, debugMode) {
    const ctx = context;
    if (!ctx[INSTANCE_SYM]) {
        ctx[INSTANCE_SYM] = {
            dragDropManager: createDragDropManager(backend, context, options, debugMode)
        };
    }
    return ctx[INSTANCE_SYM];
}
function getGlobalContext() {
    return typeof global !== 'undefined' ? global : window;
}

// do not edit .js files directly - edit src/index.jst



var fastDeepEqual = function equal(a, b) {
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;

    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (!equal(a[i], b[i])) return false;
      return true;
    }



    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;)
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;

    for (i = length; i-- !== 0;) {
      var key = keys[i];

      if (!equal(a[key], b[key])) return false;
    }

    return true;
  }

  // true if both NaN, false otherwise
  return a!==a && b!==b;
};

// suppress the useLayoutEffect warning on server side.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 *
 * @param monitor The monitor to collect state from
 * @param collect The collecting function
 * @param onUpdate A method to invoke when updates occur
 */ function useCollector(monitor, collect, onUpdate) {
    const [collected, setCollected] = useState(()=>collect(monitor)
    );
    const updateCollected = useCallback(()=>{
        const nextValue = collect(monitor);
        // This needs to be a deep-equality check because some monitor-collected values
        // include XYCoord objects that may be equivalent, but do not have instance equality.
        if (!fastDeepEqual(collected, nextValue)) {
            setCollected(nextValue);
            if (onUpdate) {
                onUpdate();
            }
        }
    }, [
        collected,
        monitor,
        onUpdate
    ]);
    // update the collected properties after react renders.
    // Note that the "Dustbin Stress Test" fails if this is not
    // done when the component updates
    useIsomorphicLayoutEffect(updateCollected);
    return [
        collected,
        updateCollected
    ];
}

function useMonitorOutput(monitor, collect, onCollect) {
    const [collected, updateCollected] = useCollector(monitor, collect, onCollect);
    useIsomorphicLayoutEffect(function subscribeToMonitorStateChange() {
        const handlerId = monitor.getHandlerId();
        if (handlerId == null) {
            return;
        }
        return monitor.subscribeToStateChange(updateCollected, {
            handlerIds: [
                handlerId
            ]
        });
    }, [
        monitor,
        updateCollected
    ]);
    return collected;
}

function useCollectedProps(collector, monitor, connector) {
    return useMonitorOutput(monitor, collector || (()=>({})
    ), ()=>connector.reconnect()
    );
}

function useOptionalFactory(arg, deps) {
    const memoDeps = [
        ...deps || []
    ];
    if (deps == null && typeof arg !== 'function') {
        memoDeps.push(arg);
    }
    return useMemo(()=>{
        return typeof arg === 'function' ? arg() : arg;
    }, memoDeps);
}

function useConnectDragSource(connector) {
    return useMemo(()=>connector.hooks.dragSource()
    , [
        connector
    ]);
}
function useConnectDragPreview(connector) {
    return useMemo(()=>connector.hooks.dragPreview()
    , [
        connector
    ]);
}

let isCallingCanDrag = false;
let isCallingIsDragging = false;
class DragSourceMonitorImpl {
    receiveHandlerId(sourceId) {
        this.sourceId = sourceId;
    }
    getHandlerId() {
        return this.sourceId;
    }
    canDrag() {
        invariant(!isCallingCanDrag, 'You may not call monitor.canDrag() inside your canDrag() implementation. ' + 'Read more: http://react-dnd.github.io/react-dnd/docs/api/drag-source-monitor');
        try {
            isCallingCanDrag = true;
            return this.internalMonitor.canDragSource(this.sourceId);
        } finally{
            isCallingCanDrag = false;
        }
    }
    isDragging() {
        if (!this.sourceId) {
            return false;
        }
        invariant(!isCallingIsDragging, 'You may not call monitor.isDragging() inside your isDragging() implementation. ' + 'Read more: http://react-dnd.github.io/react-dnd/docs/api/drag-source-monitor');
        try {
            isCallingIsDragging = true;
            return this.internalMonitor.isDraggingSource(this.sourceId);
        } finally{
            isCallingIsDragging = false;
        }
    }
    subscribeToStateChange(listener, options) {
        return this.internalMonitor.subscribeToStateChange(listener, options);
    }
    isDraggingSource(sourceId) {
        return this.internalMonitor.isDraggingSource(sourceId);
    }
    isOverTarget(targetId, options) {
        return this.internalMonitor.isOverTarget(targetId, options);
    }
    getTargetIds() {
        return this.internalMonitor.getTargetIds();
    }
    isSourcePublic() {
        return this.internalMonitor.isSourcePublic();
    }
    getSourceId() {
        return this.internalMonitor.getSourceId();
    }
    subscribeToOffsetChange(listener) {
        return this.internalMonitor.subscribeToOffsetChange(listener);
    }
    canDragSource(sourceId) {
        return this.internalMonitor.canDragSource(sourceId);
    }
    canDropOnTarget(targetId) {
        return this.internalMonitor.canDropOnTarget(targetId);
    }
    getItemType() {
        return this.internalMonitor.getItemType();
    }
    getItem() {
        return this.internalMonitor.getItem();
    }
    getDropResult() {
        return this.internalMonitor.getDropResult();
    }
    didDrop() {
        return this.internalMonitor.didDrop();
    }
    getInitialClientOffset() {
        return this.internalMonitor.getInitialClientOffset();
    }
    getInitialSourceClientOffset() {
        return this.internalMonitor.getInitialSourceClientOffset();
    }
    getSourceClientOffset() {
        return this.internalMonitor.getSourceClientOffset();
    }
    getClientOffset() {
        return this.internalMonitor.getClientOffset();
    }
    getDifferenceFromInitialOffset() {
        return this.internalMonitor.getDifferenceFromInitialOffset();
    }
    constructor(manager){
        this.sourceId = null;
        this.internalMonitor = manager.getMonitor();
    }
}

let isCallingCanDrop = false;
class DropTargetMonitorImpl {
    receiveHandlerId(targetId) {
        this.targetId = targetId;
    }
    getHandlerId() {
        return this.targetId;
    }
    subscribeToStateChange(listener, options) {
        return this.internalMonitor.subscribeToStateChange(listener, options);
    }
    canDrop() {
        // Cut out early if the target id has not been set. This should prevent errors
        // where the user has an older version of dnd-core like in
        // https://github.com/react-dnd/react-dnd/issues/1310
        if (!this.targetId) {
            return false;
        }
        invariant(!isCallingCanDrop, 'You may not call monitor.canDrop() inside your canDrop() implementation. ' + 'Read more: http://react-dnd.github.io/react-dnd/docs/api/drop-target-monitor');
        try {
            isCallingCanDrop = true;
            return this.internalMonitor.canDropOnTarget(this.targetId);
        } finally{
            isCallingCanDrop = false;
        }
    }
    isOver(options) {
        if (!this.targetId) {
            return false;
        }
        return this.internalMonitor.isOverTarget(this.targetId, options);
    }
    getItemType() {
        return this.internalMonitor.getItemType();
    }
    getItem() {
        return this.internalMonitor.getItem();
    }
    getDropResult() {
        return this.internalMonitor.getDropResult();
    }
    didDrop() {
        return this.internalMonitor.didDrop();
    }
    getInitialClientOffset() {
        return this.internalMonitor.getInitialClientOffset();
    }
    getInitialSourceClientOffset() {
        return this.internalMonitor.getInitialSourceClientOffset();
    }
    getSourceClientOffset() {
        return this.internalMonitor.getSourceClientOffset();
    }
    getClientOffset() {
        return this.internalMonitor.getClientOffset();
    }
    getDifferenceFromInitialOffset() {
        return this.internalMonitor.getDifferenceFromInitialOffset();
    }
    constructor(manager){
        this.targetId = null;
        this.internalMonitor = manager.getMonitor();
    }
}

function registerTarget(type, target, manager) {
    const registry = manager.getRegistry();
    const targetId = registry.addTarget(type, target);
    return [
        targetId,
        ()=>registry.removeTarget(targetId)
    ];
}
function registerSource(type, source, manager) {
    const registry = manager.getRegistry();
    const sourceId = registry.addSource(type, source);
    return [
        sourceId,
        ()=>registry.removeSource(sourceId)
    ];
}

function shallowEqual(objA, objB, compare, compareContext) {
    let compareResult = compare ? compare.call(compareContext, objA, objB) : void 0;
    if (compareResult !== void 0) {
        return !!compareResult;
    }
    if (objA === objB) {
        return true;
    }
    if (typeof objA !== 'object' || !objA || typeof objB !== 'object' || !objB) {
        return false;
    }
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) {
        return false;
    }
    const bHasOwnProperty = Object.prototype.hasOwnProperty.bind(objB);
    // Test for A's keys different from B.
    for(let idx = 0; idx < keysA.length; idx++){
        const key = keysA[idx];
        if (!bHasOwnProperty(key)) {
            return false;
        }
        const valueA = objA[key];
        const valueB = objB[key];
        compareResult = compare ? compare.call(compareContext, valueA, valueB, key) : void 0;
        if (compareResult === false || compareResult === void 0 && valueA !== valueB) {
            return false;
        }
    }
    return true;
}

function isRef(obj) {
    return(// eslint-disable-next-line no-prototype-builtins
    obj !== null && typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, 'current'));
}

function throwIfCompositeComponentElement(element) {
    // Custom components can no longer be wrapped directly in React DnD 2.0
    // so that we don't need to depend on findDOMNode() from react-dom.
    if (typeof element.type === 'string') {
        return;
    }
    const displayName = element.type.displayName || element.type.name || 'the component';
    throw new Error('Only native element nodes can now be passed to React DnD connectors.' + `You can either wrap ${displayName} into a <div>, or turn it into a ` + 'drag source or a drop target itself.');
}
function wrapHookToRecognizeElement(hook) {
    return (elementOrNode = null, options = null)=>{
        // When passed a node, call the hook straight away.
        if (!isValidElement(elementOrNode)) {
            const node = elementOrNode;
            hook(node, options);
            // return the node so it can be chained (e.g. when within callback refs
            // <div ref={node => connectDragSource(connectDropTarget(node))}/>
            return node;
        }
        // If passed a ReactElement, clone it and attach this function as a ref.
        // This helps us achieve a neat API where user doesn't even know that refs
        // are being used under the hood.
        const element = elementOrNode;
        throwIfCompositeComponentElement(element);
        // When no options are passed, use the hook directly
        const ref = options ? (node)=>hook(node, options)
         : hook;
        return cloneWithRef(element, ref);
    };
}
function wrapConnectorHooks(hooks) {
    const wrappedHooks = {};
    Object.keys(hooks).forEach((key)=>{
        const hook = hooks[key];
        // ref objects should be passed straight through without wrapping
        if (key.endsWith('Ref')) {
            wrappedHooks[key] = hooks[key];
        } else {
            const wrappedHook = wrapHookToRecognizeElement(hook);
            wrappedHooks[key] = ()=>wrappedHook
            ;
        }
    });
    return wrappedHooks;
}
function setRef(ref, node) {
    if (typeof ref === 'function') {
        ref(node);
    } else {
        ref.current = node;
    }
}
function cloneWithRef(element, newRef) {
    const previousRef = element.ref;
    invariant(typeof previousRef !== 'string', 'Cannot connect React DnD to an element with an existing string ref. ' + 'Please convert it to use a callback ref instead, or wrap it into a <span> or <div>. ' + 'Read more: https://reactjs.org/docs/refs-and-the-dom.html#callback-refs');
    if (!previousRef) {
        // When there is no ref on the element, use the new ref directly
        return cloneElement(element, {
            ref: newRef
        });
    } else {
        return cloneElement(element, {
            ref: (node)=>{
                setRef(previousRef, node);
                setRef(newRef, node);
            }
        });
    }
}

class SourceConnector {
    receiveHandlerId(newHandlerId) {
        if (this.handlerId === newHandlerId) {
            return;
        }
        this.handlerId = newHandlerId;
        this.reconnect();
    }
    get connectTarget() {
        return this.dragSource;
    }
    get dragSourceOptions() {
        return this.dragSourceOptionsInternal;
    }
    set dragSourceOptions(options) {
        this.dragSourceOptionsInternal = options;
    }
    get dragPreviewOptions() {
        return this.dragPreviewOptionsInternal;
    }
    set dragPreviewOptions(options) {
        this.dragPreviewOptionsInternal = options;
    }
    reconnect() {
        const didChange = this.reconnectDragSource();
        this.reconnectDragPreview(didChange);
    }
    reconnectDragSource() {
        const dragSource = this.dragSource;
        // if nothing has changed then don't resubscribe
        const didChange = this.didHandlerIdChange() || this.didConnectedDragSourceChange() || this.didDragSourceOptionsChange();
        if (didChange) {
            this.disconnectDragSource();
        }
        if (!this.handlerId) {
            return didChange;
        }
        if (!dragSource) {
            this.lastConnectedDragSource = dragSource;
            return didChange;
        }
        if (didChange) {
            this.lastConnectedHandlerId = this.handlerId;
            this.lastConnectedDragSource = dragSource;
            this.lastConnectedDragSourceOptions = this.dragSourceOptions;
            this.dragSourceUnsubscribe = this.backend.connectDragSource(this.handlerId, dragSource, this.dragSourceOptions);
        }
        return didChange;
    }
    reconnectDragPreview(forceDidChange = false) {
        const dragPreview = this.dragPreview;
        // if nothing has changed then don't resubscribe
        const didChange = forceDidChange || this.didHandlerIdChange() || this.didConnectedDragPreviewChange() || this.didDragPreviewOptionsChange();
        if (didChange) {
            this.disconnectDragPreview();
        }
        if (!this.handlerId) {
            return;
        }
        if (!dragPreview) {
            this.lastConnectedDragPreview = dragPreview;
            return;
        }
        if (didChange) {
            this.lastConnectedHandlerId = this.handlerId;
            this.lastConnectedDragPreview = dragPreview;
            this.lastConnectedDragPreviewOptions = this.dragPreviewOptions;
            this.dragPreviewUnsubscribe = this.backend.connectDragPreview(this.handlerId, dragPreview, this.dragPreviewOptions);
        }
    }
    didHandlerIdChange() {
        return this.lastConnectedHandlerId !== this.handlerId;
    }
    didConnectedDragSourceChange() {
        return this.lastConnectedDragSource !== this.dragSource;
    }
    didConnectedDragPreviewChange() {
        return this.lastConnectedDragPreview !== this.dragPreview;
    }
    didDragSourceOptionsChange() {
        return !shallowEqual(this.lastConnectedDragSourceOptions, this.dragSourceOptions);
    }
    didDragPreviewOptionsChange() {
        return !shallowEqual(this.lastConnectedDragPreviewOptions, this.dragPreviewOptions);
    }
    disconnectDragSource() {
        if (this.dragSourceUnsubscribe) {
            this.dragSourceUnsubscribe();
            this.dragSourceUnsubscribe = undefined;
        }
    }
    disconnectDragPreview() {
        if (this.dragPreviewUnsubscribe) {
            this.dragPreviewUnsubscribe();
            this.dragPreviewUnsubscribe = undefined;
            this.dragPreviewNode = null;
            this.dragPreviewRef = null;
        }
    }
    get dragSource() {
        return this.dragSourceNode || this.dragSourceRef && this.dragSourceRef.current;
    }
    get dragPreview() {
        return this.dragPreviewNode || this.dragPreviewRef && this.dragPreviewRef.current;
    }
    clearDragSource() {
        this.dragSourceNode = null;
        this.dragSourceRef = null;
    }
    clearDragPreview() {
        this.dragPreviewNode = null;
        this.dragPreviewRef = null;
    }
    constructor(backend){
        this.hooks = wrapConnectorHooks({
            dragSource: (node, options)=>{
                this.clearDragSource();
                this.dragSourceOptions = options || null;
                if (isRef(node)) {
                    this.dragSourceRef = node;
                } else {
                    this.dragSourceNode = node;
                }
                this.reconnectDragSource();
            },
            dragPreview: (node, options)=>{
                this.clearDragPreview();
                this.dragPreviewOptions = options || null;
                if (isRef(node)) {
                    this.dragPreviewRef = node;
                } else {
                    this.dragPreviewNode = node;
                }
                this.reconnectDragPreview();
            }
        });
        this.handlerId = null;
        // The drop target may either be attached via ref or connect function
        this.dragSourceRef = null;
        this.dragSourceOptionsInternal = null;
        // The drag preview may either be attached via ref or connect function
        this.dragPreviewRef = null;
        this.dragPreviewOptionsInternal = null;
        this.lastConnectedHandlerId = null;
        this.lastConnectedDragSource = null;
        this.lastConnectedDragSourceOptions = null;
        this.lastConnectedDragPreview = null;
        this.lastConnectedDragPreviewOptions = null;
        this.backend = backend;
    }
}

class TargetConnector {
    get connectTarget() {
        return this.dropTarget;
    }
    reconnect() {
        // if nothing has changed then don't resubscribe
        const didChange = this.didHandlerIdChange() || this.didDropTargetChange() || this.didOptionsChange();
        if (didChange) {
            this.disconnectDropTarget();
        }
        const dropTarget = this.dropTarget;
        if (!this.handlerId) {
            return;
        }
        if (!dropTarget) {
            this.lastConnectedDropTarget = dropTarget;
            return;
        }
        if (didChange) {
            this.lastConnectedHandlerId = this.handlerId;
            this.lastConnectedDropTarget = dropTarget;
            this.lastConnectedDropTargetOptions = this.dropTargetOptions;
            this.unsubscribeDropTarget = this.backend.connectDropTarget(this.handlerId, dropTarget, this.dropTargetOptions);
        }
    }
    receiveHandlerId(newHandlerId) {
        if (newHandlerId === this.handlerId) {
            return;
        }
        this.handlerId = newHandlerId;
        this.reconnect();
    }
    get dropTargetOptions() {
        return this.dropTargetOptionsInternal;
    }
    set dropTargetOptions(options) {
        this.dropTargetOptionsInternal = options;
    }
    didHandlerIdChange() {
        return this.lastConnectedHandlerId !== this.handlerId;
    }
    didDropTargetChange() {
        return this.lastConnectedDropTarget !== this.dropTarget;
    }
    didOptionsChange() {
        return !shallowEqual(this.lastConnectedDropTargetOptions, this.dropTargetOptions);
    }
    disconnectDropTarget() {
        if (this.unsubscribeDropTarget) {
            this.unsubscribeDropTarget();
            this.unsubscribeDropTarget = undefined;
        }
    }
    get dropTarget() {
        return this.dropTargetNode || this.dropTargetRef && this.dropTargetRef.current;
    }
    clearDropTarget() {
        this.dropTargetRef = null;
        this.dropTargetNode = null;
    }
    constructor(backend){
        this.hooks = wrapConnectorHooks({
            dropTarget: (node, options)=>{
                this.clearDropTarget();
                this.dropTargetOptions = options;
                if (isRef(node)) {
                    this.dropTargetRef = node;
                } else {
                    this.dropTargetNode = node;
                }
                this.reconnect();
            }
        });
        this.handlerId = null;
        // The drop target may either be attached via ref or connect function
        this.dropTargetRef = null;
        this.dropTargetOptionsInternal = null;
        this.lastConnectedHandlerId = null;
        this.lastConnectedDropTarget = null;
        this.lastConnectedDropTargetOptions = null;
        this.backend = backend;
    }
}

/**
 * A hook to retrieve the DragDropManager from Context
 */ function useDragDropManager() {
    const { dragDropManager  } = useContext(DndContext);
    invariant(dragDropManager != null, 'Expected drag drop context');
    return dragDropManager;
}

function useDragSourceConnector(dragSourceOptions, dragPreviewOptions) {
    const manager = useDragDropManager();
    const connector = useMemo(()=>new SourceConnector(manager.getBackend())
    , [
        manager
    ]);
    useIsomorphicLayoutEffect(()=>{
        connector.dragSourceOptions = dragSourceOptions || null;
        connector.reconnect();
        return ()=>connector.disconnectDragSource()
        ;
    }, [
        connector,
        dragSourceOptions
    ]);
    useIsomorphicLayoutEffect(()=>{
        connector.dragPreviewOptions = dragPreviewOptions || null;
        connector.reconnect();
        return ()=>connector.disconnectDragPreview()
        ;
    }, [
        connector,
        dragPreviewOptions
    ]);
    return connector;
}

function useDragSourceMonitor() {
    const manager = useDragDropManager();
    return useMemo(()=>new DragSourceMonitorImpl(manager)
    , [
        manager
    ]);
}

class DragSourceImpl {
    beginDrag() {
        const spec = this.spec;
        const monitor = this.monitor;
        let result = null;
        if (typeof spec.item === 'object') {
            result = spec.item;
        } else if (typeof spec.item === 'function') {
            result = spec.item(monitor);
        } else {
            result = {};
        }
        return result !== null && result !== void 0 ? result : null;
    }
    canDrag() {
        const spec = this.spec;
        const monitor = this.monitor;
        if (typeof spec.canDrag === 'boolean') {
            return spec.canDrag;
        } else if (typeof spec.canDrag === 'function') {
            return spec.canDrag(monitor);
        } else {
            return true;
        }
    }
    isDragging(globalMonitor, target) {
        const spec = this.spec;
        const monitor = this.monitor;
        const { isDragging  } = spec;
        return isDragging ? isDragging(monitor) : target === globalMonitor.getSourceId();
    }
    endDrag() {
        const spec = this.spec;
        const monitor = this.monitor;
        const connector = this.connector;
        const { end  } = spec;
        if (end) {
            end(monitor.getItem(), monitor);
        }
        connector.reconnect();
    }
    constructor(spec, monitor, connector){
        this.spec = spec;
        this.monitor = monitor;
        this.connector = connector;
    }
}

function useDragSource(spec, monitor, connector) {
    const handler = useMemo(()=>new DragSourceImpl(spec, monitor, connector)
    , [
        monitor,
        connector
    ]);
    useEffect(()=>{
        handler.spec = spec;
    }, [
        spec
    ]);
    return handler;
}

function useDragType(spec) {
    return useMemo(()=>{
        const result = spec.type;
        invariant(result != null, 'spec.type must be defined');
        return result;
    }, [
        spec
    ]);
}

function useRegisteredDragSource(spec, monitor, connector) {
    const manager = useDragDropManager();
    const handler = useDragSource(spec, monitor, connector);
    const itemType = useDragType(spec);
    useIsomorphicLayoutEffect(function registerDragSource() {
        if (itemType != null) {
            const [handlerId, unregister] = registerSource(itemType, handler, manager);
            monitor.receiveHandlerId(handlerId);
            connector.receiveHandlerId(handlerId);
            return unregister;
        }
        return;
    }, [
        manager,
        monitor,
        connector,
        handler,
        itemType
    ]);
}

/**
 * useDragSource hook
 * @param sourceSpec The drag source specification (object or function, function preferred)
 * @param deps The memoization deps array to use when evaluating spec changes
 */ function useDrag(specArg, deps) {
    const spec = useOptionalFactory(specArg, deps);
    invariant(!spec.begin, `useDrag::spec.begin was deprecated in v14. Replace spec.begin() with spec.item(). (see more here - https://react-dnd.github.io/react-dnd/docs/api/use-drag)`);
    const monitor = useDragSourceMonitor();
    const connector = useDragSourceConnector(spec.options, spec.previewOptions);
    useRegisteredDragSource(spec, monitor, connector);
    return [
        useCollectedProps(spec.collect, monitor, connector),
        useConnectDragSource(connector),
        useConnectDragPreview(connector), 
    ];
}

function useConnectDropTarget(connector) {
    return useMemo(()=>connector.hooks.dropTarget()
    , [
        connector
    ]);
}

function useDropTargetConnector(options) {
    const manager = useDragDropManager();
    const connector = useMemo(()=>new TargetConnector(manager.getBackend())
    , [
        manager
    ]);
    useIsomorphicLayoutEffect(()=>{
        connector.dropTargetOptions = options || null;
        connector.reconnect();
        return ()=>connector.disconnectDropTarget()
        ;
    }, [
        options
    ]);
    return connector;
}

function useDropTargetMonitor() {
    const manager = useDragDropManager();
    return useMemo(()=>new DropTargetMonitorImpl(manager)
    , [
        manager
    ]);
}

/**
 * Internal utility hook to get an array-version of spec.accept.
 * The main utility here is that we aren't creating a new array on every render if a non-array spec.accept is passed in.
 * @param spec
 */ function useAccept(spec) {
    const { accept  } = spec;
    return useMemo(()=>{
        invariant(spec.accept != null, 'accept must be defined');
        return Array.isArray(accept) ? accept : [
            accept
        ];
    }, [
        accept
    ]);
}

class DropTargetImpl {
    canDrop() {
        const spec = this.spec;
        const monitor = this.monitor;
        return spec.canDrop ? spec.canDrop(monitor.getItem(), monitor) : true;
    }
    hover() {
        const spec = this.spec;
        const monitor = this.monitor;
        if (spec.hover) {
            spec.hover(monitor.getItem(), monitor);
        }
    }
    drop() {
        const spec = this.spec;
        const monitor = this.monitor;
        if (spec.drop) {
            return spec.drop(monitor.getItem(), monitor);
        }
        return;
    }
    constructor(spec, monitor){
        this.spec = spec;
        this.monitor = monitor;
    }
}

function useDropTarget(spec, monitor) {
    const dropTarget = useMemo(()=>new DropTargetImpl(spec, monitor)
    , [
        monitor
    ]);
    useEffect(()=>{
        dropTarget.spec = spec;
    }, [
        spec
    ]);
    return dropTarget;
}

function useRegisteredDropTarget(spec, monitor, connector) {
    const manager = useDragDropManager();
    const dropTarget = useDropTarget(spec, monitor);
    const accept = useAccept(spec);
    useIsomorphicLayoutEffect(function registerDropTarget() {
        const [handlerId, unregister] = registerTarget(accept, dropTarget, manager);
        monitor.receiveHandlerId(handlerId);
        connector.receiveHandlerId(handlerId);
        return unregister;
    }, [
        manager,
        monitor,
        dropTarget,
        connector,
        accept.map((a)=>a.toString()
        ).join('|'), 
    ]);
}

/**
 * useDropTarget Hook
 * @param spec The drop target specification (object or function, function preferred)
 * @param deps The memoization deps array to use when evaluating spec changes
 */ function useDrop(specArg, deps) {
    const spec = useOptionalFactory(specArg, deps);
    const monitor = useDropTargetMonitor();
    const connector = useDropTargetConnector(spec.options);
    useRegisteredDropTarget(spec, monitor, connector);
    return [
        useCollectedProps(spec.collect, monitor, connector),
        useConnectDropTarget(connector), 
    ];
}

// cheap lodash replacements
function memoize(fn) {
    let result = null;
    const memoized = ()=>{
        if (result == null) {
            result = fn();
        }
        return result;
    };
    return memoized;
}
/**
 * drop-in replacement for _.without
 */ function without(items, item) {
    return items.filter((i)=>i !== item
    );
}
function union(itemsA, itemsB) {
    const set = new Set();
    const insertItem = (item)=>set.add(item)
    ;
    itemsA.forEach(insertItem);
    itemsB.forEach(insertItem);
    const result = [];
    set.forEach((key)=>result.push(key)
    );
    return result;
}

class EnterLeaveCounter {
    enter(enteringNode) {
        const previousLength = this.entered.length;
        const isNodeEntered = (node)=>this.isNodeInDocument(node) && (!node.contains || node.contains(enteringNode))
        ;
        this.entered = union(this.entered.filter(isNodeEntered), [
            enteringNode
        ]);
        return previousLength === 0 && this.entered.length > 0;
    }
    leave(leavingNode) {
        const previousLength = this.entered.length;
        this.entered = without(this.entered.filter(this.isNodeInDocument), leavingNode);
        return previousLength > 0 && this.entered.length === 0;
    }
    reset() {
        this.entered = [];
    }
    constructor(isNodeInDocument){
        this.entered = [];
        this.isNodeInDocument = isNodeInDocument;
    }
}

class NativeDragSource {
    initializeExposedProperties() {
        Object.keys(this.config.exposeProperties).forEach((property)=>{
            Object.defineProperty(this.item, property, {
                configurable: true,
                enumerable: true,
                get () {
                    // eslint-disable-next-line no-console
                    console.warn(`Browser doesn't allow reading "${property}" until the drop event.`);
                    return null;
                }
            });
        });
    }
    loadDataTransfer(dataTransfer) {
        if (dataTransfer) {
            const newProperties = {};
            Object.keys(this.config.exposeProperties).forEach((property)=>{
                const propertyFn = this.config.exposeProperties[property];
                if (propertyFn != null) {
                    newProperties[property] = {
                        value: propertyFn(dataTransfer, this.config.matchesTypes),
                        configurable: true,
                        enumerable: true
                    };
                }
            });
            Object.defineProperties(this.item, newProperties);
        }
    }
    canDrag() {
        return true;
    }
    beginDrag() {
        return this.item;
    }
    isDragging(monitor, handle) {
        return handle === monitor.getSourceId();
    }
    endDrag() {
    // empty
    }
    constructor(config){
        this.config = config;
        this.item = {};
        this.initializeExposedProperties();
    }
}

const FILE = '__NATIVE_FILE__';
const URL = '__NATIVE_URL__';
const TEXT = '__NATIVE_TEXT__';
const HTML = '__NATIVE_HTML__';

var NativeTypes = /*#__PURE__*/Object.freeze({
    __proto__: null,
    FILE: FILE,
    HTML: HTML,
    TEXT: TEXT,
    URL: URL
});

function getDataFromDataTransfer(dataTransfer, typesToTry, defaultValue) {
    const result = typesToTry.reduce((resultSoFar, typeToTry)=>resultSoFar || dataTransfer.getData(typeToTry)
    , '');
    return result != null ? result : defaultValue;
}

const nativeTypesConfig = {
    [FILE]: {
        exposeProperties: {
            files: (dataTransfer)=>Array.prototype.slice.call(dataTransfer.files)
            ,
            items: (dataTransfer)=>dataTransfer.items
            ,
            dataTransfer: (dataTransfer)=>dataTransfer
        },
        matchesTypes: [
            'Files'
        ]
    },
    [HTML]: {
        exposeProperties: {
            html: (dataTransfer, matchesTypes)=>getDataFromDataTransfer(dataTransfer, matchesTypes, '')
            ,
            dataTransfer: (dataTransfer)=>dataTransfer
        },
        matchesTypes: [
            'Html',
            'text/html'
        ]
    },
    [URL]: {
        exposeProperties: {
            urls: (dataTransfer, matchesTypes)=>getDataFromDataTransfer(dataTransfer, matchesTypes, '').split('\n')
            ,
            dataTransfer: (dataTransfer)=>dataTransfer
        },
        matchesTypes: [
            'Url',
            'text/uri-list'
        ]
    },
    [TEXT]: {
        exposeProperties: {
            text: (dataTransfer, matchesTypes)=>getDataFromDataTransfer(dataTransfer, matchesTypes, '')
            ,
            dataTransfer: (dataTransfer)=>dataTransfer
        },
        matchesTypes: [
            'Text',
            'text/plain'
        ]
    }
};

function createNativeDragSource(type, dataTransfer) {
    const config = nativeTypesConfig[type];
    if (!config) {
        throw new Error(`native type ${type} has no configuration`);
    }
    const result = new NativeDragSource(config);
    result.loadDataTransfer(dataTransfer);
    return result;
}
function matchNativeItemType(dataTransfer) {
    if (!dataTransfer) {
        return null;
    }
    const dataTransferTypes = Array.prototype.slice.call(dataTransfer.types || []);
    return Object.keys(nativeTypesConfig).filter((nativeItemType)=>{
        const typeConfig = nativeTypesConfig[nativeItemType];
        if (!(typeConfig === null || typeConfig === void 0 ? void 0 : typeConfig.matchesTypes)) {
            return false;
        }
        return typeConfig.matchesTypes.some((t)=>dataTransferTypes.indexOf(t) > -1
        );
    })[0] || null;
}

const isFirefox = memoize(()=>/firefox/i.test(navigator.userAgent)
);
const isSafari = memoize(()=>Boolean(window.safari)
);

class MonotonicInterpolant {
    interpolate(x) {
        const { xs , ys , c1s , c2s , c3s  } = this;
        // The rightmost point in the dataset should give an exact result
        let i = xs.length - 1;
        if (x === xs[i]) {
            return ys[i];
        }
        // Search for the interval x is in, returning the corresponding y if x is one of the original xs
        let low = 0;
        let high = c3s.length - 1;
        let mid;
        while(low <= high){
            mid = Math.floor(0.5 * (low + high));
            const xHere = xs[mid];
            if (xHere < x) {
                low = mid + 1;
            } else if (xHere > x) {
                high = mid - 1;
            } else {
                return ys[mid];
            }
        }
        i = Math.max(0, high);
        // Interpolate
        const diff = x - xs[i];
        const diffSq = diff * diff;
        return ys[i] + c1s[i] * diff + c2s[i] * diffSq + c3s[i] * diff * diffSq;
    }
    constructor(xs, ys){
        const { length  } = xs;
        // Rearrange xs and ys so that xs is sorted
        const indexes = [];
        for(let i = 0; i < length; i++){
            indexes.push(i);
        }
        indexes.sort((a, b)=>xs[a] < xs[b] ? -1 : 1
        );
        const dxs = [];
        const ms = [];
        let dx;
        let dy;
        for(let i1 = 0; i1 < length - 1; i1++){
            dx = xs[i1 + 1] - xs[i1];
            dy = ys[i1 + 1] - ys[i1];
            dxs.push(dx);
            ms.push(dy / dx);
        }
        // Get degree-1 coefficients
        const c1s = [
            ms[0]
        ];
        for(let i2 = 0; i2 < dxs.length - 1; i2++){
            const m2 = ms[i2];
            const mNext = ms[i2 + 1];
            if (m2 * mNext <= 0) {
                c1s.push(0);
            } else {
                dx = dxs[i2];
                const dxNext = dxs[i2 + 1];
                const common = dx + dxNext;
                c1s.push(3 * common / ((common + dxNext) / m2 + (common + dx) / mNext));
            }
        }
        c1s.push(ms[ms.length - 1]);
        // Get degree-2 and degree-3 coefficients
        const c2s = [];
        const c3s = [];
        let m;
        for(let i3 = 0; i3 < c1s.length - 1; i3++){
            m = ms[i3];
            const c1 = c1s[i3];
            const invDx = 1 / dxs[i3];
            const common = c1 + c1s[i3 + 1] - m - m;
            c2s.push((m - c1 - common) * invDx);
            c3s.push(common * invDx * invDx);
        }
        this.xs = xs;
        this.ys = ys;
        this.c1s = c1s;
        this.c2s = c2s;
        this.c3s = c3s;
    }
}

const ELEMENT_NODE = 1;
function getNodeClientOffset(node) {
    const el = node.nodeType === ELEMENT_NODE ? node : node.parentElement;
    if (!el) {
        return null;
    }
    const { top , left  } = el.getBoundingClientRect();
    return {
        x: left,
        y: top
    };
}
function getEventClientOffset(e) {
    return {
        x: e.clientX,
        y: e.clientY
    };
}
function isImageNode(node) {
    var ref;
    return node.nodeName === 'IMG' && (isFirefox() || !((ref = document.documentElement) === null || ref === void 0 ? void 0 : ref.contains(node)));
}
function getDragPreviewSize(isImage, dragPreview, sourceWidth, sourceHeight) {
    let dragPreviewWidth = isImage ? dragPreview.width : sourceWidth;
    let dragPreviewHeight = isImage ? dragPreview.height : sourceHeight;
    // Work around @2x coordinate discrepancies in browsers
    if (isSafari() && isImage) {
        dragPreviewHeight /= window.devicePixelRatio;
        dragPreviewWidth /= window.devicePixelRatio;
    }
    return {
        dragPreviewWidth,
        dragPreviewHeight
    };
}
function getDragPreviewOffset(sourceNode, dragPreview, clientOffset, anchorPoint, offsetPoint) {
    // The browsers will use the image intrinsic size under different conditions.
    // Firefox only cares if it's an image, but WebKit also wants it to be detached.
    const isImage = isImageNode(dragPreview);
    const dragPreviewNode = isImage ? sourceNode : dragPreview;
    const dragPreviewNodeOffsetFromClient = getNodeClientOffset(dragPreviewNode);
    const offsetFromDragPreview = {
        x: clientOffset.x - dragPreviewNodeOffsetFromClient.x,
        y: clientOffset.y - dragPreviewNodeOffsetFromClient.y
    };
    const { offsetWidth: sourceWidth , offsetHeight: sourceHeight  } = sourceNode;
    const { anchorX , anchorY  } = anchorPoint;
    const { dragPreviewWidth , dragPreviewHeight  } = getDragPreviewSize(isImage, dragPreview, sourceWidth, sourceHeight);
    const calculateYOffset = ()=>{
        const interpolantY = new MonotonicInterpolant([
            0,
            0.5,
            1
        ], [
            // Dock to the top
            offsetFromDragPreview.y,
            // Align at the center
            (offsetFromDragPreview.y / sourceHeight) * dragPreviewHeight,
            // Dock to the bottom
            offsetFromDragPreview.y + dragPreviewHeight - sourceHeight, 
        ]);
        let y = interpolantY.interpolate(anchorY);
        // Work around Safari 8 positioning bug
        if (isSafari() && isImage) {
            // We'll have to wait for @3x to see if this is entirely correct
            y += (window.devicePixelRatio - 1) * dragPreviewHeight;
        }
        return y;
    };
    const calculateXOffset = ()=>{
        // Interpolate coordinates depending on anchor point
        // If you know a simpler way to do this, let me know
        const interpolantX = new MonotonicInterpolant([
            0,
            0.5,
            1
        ], [
            // Dock to the left
            offsetFromDragPreview.x,
            // Align at the center
            (offsetFromDragPreview.x / sourceWidth) * dragPreviewWidth,
            // Dock to the right
            offsetFromDragPreview.x + dragPreviewWidth - sourceWidth, 
        ]);
        return interpolantX.interpolate(anchorX);
    };
    // Force offsets if specified in the options.
    const { offsetX , offsetY  } = offsetPoint;
    const isManualOffsetX = offsetX === 0 || offsetX;
    const isManualOffsetY = offsetY === 0 || offsetY;
    return {
        x: isManualOffsetX ? offsetX : calculateXOffset(),
        y: isManualOffsetY ? offsetY : calculateYOffset()
    };
}

class OptionsReader {
    get window() {
        if (this.globalContext) {
            return this.globalContext;
        } else if (typeof window !== 'undefined') {
            return window;
        }
        return undefined;
    }
    get document() {
        var ref;
        if ((ref = this.globalContext) === null || ref === void 0 ? void 0 : ref.document) {
            return this.globalContext.document;
        } else if (this.window) {
            return this.window.document;
        } else {
            return undefined;
        }
    }
    get rootElement() {
        var ref;
        return ((ref = this.optionsArgs) === null || ref === void 0 ? void 0 : ref.rootElement) || this.window;
    }
    constructor(globalContext, options){
        this.ownerDocument = null;
        this.globalContext = globalContext;
        this.optionsArgs = options;
    }
}

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
class HTML5BackendImpl {
    /**
	 * Generate profiling statistics for the HTML5Backend.
	 */ profile() {
        var ref, ref1;
        return {
            sourcePreviewNodes: this.sourcePreviewNodes.size,
            sourcePreviewNodeOptions: this.sourcePreviewNodeOptions.size,
            sourceNodeOptions: this.sourceNodeOptions.size,
            sourceNodes: this.sourceNodes.size,
            dragStartSourceIds: ((ref = this.dragStartSourceIds) === null || ref === void 0 ? void 0 : ref.length) || 0,
            dropTargetIds: this.dropTargetIds.length,
            dragEnterTargetIds: this.dragEnterTargetIds.length,
            dragOverTargetIds: ((ref1 = this.dragOverTargetIds) === null || ref1 === void 0 ? void 0 : ref1.length) || 0
        };
    }
    // public for test
    get window() {
        return this.options.window;
    }
    get document() {
        return this.options.document;
    }
    /**
	 * Get the root element to use for event subscriptions
	 */ get rootElement() {
        return this.options.rootElement;
    }
    setup() {
        const root = this.rootElement;
        if (root === undefined) {
            return;
        }
        if (root.__isReactDndBackendSetUp) {
            throw new Error('Cannot have two HTML5 backends at the same time.');
        }
        root.__isReactDndBackendSetUp = true;
        this.addEventListeners(root);
    }
    teardown() {
        const root = this.rootElement;
        if (root === undefined) {
            return;
        }
        root.__isReactDndBackendSetUp = false;
        this.removeEventListeners(this.rootElement);
        this.clearCurrentDragSourceNode();
        if (this.asyncEndDragFrameId) {
            var ref;
            (ref = this.window) === null || ref === void 0 ? void 0 : ref.cancelAnimationFrame(this.asyncEndDragFrameId);
        }
    }
    connectDragPreview(sourceId, node, options) {
        this.sourcePreviewNodeOptions.set(sourceId, options);
        this.sourcePreviewNodes.set(sourceId, node);
        return ()=>{
            this.sourcePreviewNodes.delete(sourceId);
            this.sourcePreviewNodeOptions.delete(sourceId);
        };
    }
    connectDragSource(sourceId, node, options) {
        this.sourceNodes.set(sourceId, node);
        this.sourceNodeOptions.set(sourceId, options);
        const handleDragStart = (e)=>this.handleDragStart(e, sourceId)
        ;
        const handleSelectStart = (e)=>this.handleSelectStart(e)
        ;
        node.setAttribute('draggable', 'true');
        node.addEventListener('dragstart', handleDragStart);
        node.addEventListener('selectstart', handleSelectStart);
        return ()=>{
            this.sourceNodes.delete(sourceId);
            this.sourceNodeOptions.delete(sourceId);
            node.removeEventListener('dragstart', handleDragStart);
            node.removeEventListener('selectstart', handleSelectStart);
            node.setAttribute('draggable', 'false');
        };
    }
    connectDropTarget(targetId, node) {
        const handleDragEnter = (e)=>this.handleDragEnter(e, targetId)
        ;
        const handleDragOver = (e)=>this.handleDragOver(e, targetId)
        ;
        const handleDrop = (e)=>this.handleDrop(e, targetId)
        ;
        node.addEventListener('dragenter', handleDragEnter);
        node.addEventListener('dragover', handleDragOver);
        node.addEventListener('drop', handleDrop);
        return ()=>{
            node.removeEventListener('dragenter', handleDragEnter);
            node.removeEventListener('dragover', handleDragOver);
            node.removeEventListener('drop', handleDrop);
        };
    }
    addEventListeners(target) {
        // SSR Fix (https://github.com/react-dnd/react-dnd/pull/813
        if (!target.addEventListener) {
            return;
        }
        target.addEventListener('dragstart', this.handleTopDragStart);
        target.addEventListener('dragstart', this.handleTopDragStartCapture, true);
        target.addEventListener('dragend', this.handleTopDragEndCapture, true);
        target.addEventListener('dragenter', this.handleTopDragEnter);
        target.addEventListener('dragenter', this.handleTopDragEnterCapture, true);
        target.addEventListener('dragleave', this.handleTopDragLeaveCapture, true);
        target.addEventListener('dragover', this.handleTopDragOver);
        target.addEventListener('dragover', this.handleTopDragOverCapture, true);
        target.addEventListener('drop', this.handleTopDrop);
        target.addEventListener('drop', this.handleTopDropCapture, true);
    }
    removeEventListeners(target) {
        // SSR Fix (https://github.com/react-dnd/react-dnd/pull/813
        if (!target.removeEventListener) {
            return;
        }
        target.removeEventListener('dragstart', this.handleTopDragStart);
        target.removeEventListener('dragstart', this.handleTopDragStartCapture, true);
        target.removeEventListener('dragend', this.handleTopDragEndCapture, true);
        target.removeEventListener('dragenter', this.handleTopDragEnter);
        target.removeEventListener('dragenter', this.handleTopDragEnterCapture, true);
        target.removeEventListener('dragleave', this.handleTopDragLeaveCapture, true);
        target.removeEventListener('dragover', this.handleTopDragOver);
        target.removeEventListener('dragover', this.handleTopDragOverCapture, true);
        target.removeEventListener('drop', this.handleTopDrop);
        target.removeEventListener('drop', this.handleTopDropCapture, true);
    }
    getCurrentSourceNodeOptions() {
        const sourceId = this.monitor.getSourceId();
        const sourceNodeOptions = this.sourceNodeOptions.get(sourceId);
        return _objectSpread({
            dropEffect: this.altKeyPressed ? 'copy' : 'move'
        }, sourceNodeOptions || {});
    }
    getCurrentDropEffect() {
        if (this.isDraggingNativeItem()) {
            // It makes more sense to default to 'copy' for native resources
            return 'copy';
        }
        return this.getCurrentSourceNodeOptions().dropEffect;
    }
    getCurrentSourcePreviewNodeOptions() {
        const sourceId = this.monitor.getSourceId();
        const sourcePreviewNodeOptions = this.sourcePreviewNodeOptions.get(sourceId);
        return _objectSpread({
            anchorX: 0.5,
            anchorY: 0.5,
            captureDraggingState: false
        }, sourcePreviewNodeOptions || {});
    }
    isDraggingNativeItem() {
        const itemType = this.monitor.getItemType();
        return Object.keys(NativeTypes).some((key)=>NativeTypes[key] === itemType
        );
    }
    beginDragNativeItem(type, dataTransfer) {
        this.clearCurrentDragSourceNode();
        this.currentNativeSource = createNativeDragSource(type, dataTransfer);
        this.currentNativeHandle = this.registry.addSource(type, this.currentNativeSource);
        this.actions.beginDrag([
            this.currentNativeHandle
        ]);
    }
    setCurrentDragSourceNode(node) {
        this.clearCurrentDragSourceNode();
        this.currentDragSourceNode = node;
        // A timeout of > 0 is necessary to resolve Firefox issue referenced
        // See:
        //   * https://github.com/react-dnd/react-dnd/pull/928
        //   * https://github.com/react-dnd/react-dnd/issues/869
        const MOUSE_MOVE_TIMEOUT = 1000;
        // Receiving a mouse event in the middle of a dragging operation
        // means it has ended and the drag source node disappeared from DOM,
        // so the browser didn't dispatch the dragend event.
        //
        // We need to wait before we start listening for mousemove events.
        // This is needed because the drag preview needs to be drawn or else it fires an 'mousemove' event
        // immediately in some browsers.
        //
        // See:
        //   * https://github.com/react-dnd/react-dnd/pull/928
        //   * https://github.com/react-dnd/react-dnd/issues/869
        //
        this.mouseMoveTimeoutTimer = setTimeout(()=>{
            var ref;
            return (ref = this.rootElement) === null || ref === void 0 ? void 0 : ref.addEventListener('mousemove', this.endDragIfSourceWasRemovedFromDOM, true);
        }, MOUSE_MOVE_TIMEOUT);
    }
    clearCurrentDragSourceNode() {
        if (this.currentDragSourceNode) {
            this.currentDragSourceNode = null;
            if (this.rootElement) {
                var ref;
                (ref = this.window) === null || ref === void 0 ? void 0 : ref.clearTimeout(this.mouseMoveTimeoutTimer || undefined);
                this.rootElement.removeEventListener('mousemove', this.endDragIfSourceWasRemovedFromDOM, true);
            }
            this.mouseMoveTimeoutTimer = null;
            return true;
        }
        return false;
    }
    handleDragStart(e, sourceId) {
        if (e.defaultPrevented) {
            return;
        }
        if (!this.dragStartSourceIds) {
            this.dragStartSourceIds = [];
        }
        this.dragStartSourceIds.unshift(sourceId);
    }
    handleDragEnter(_e, targetId) {
        this.dragEnterTargetIds.unshift(targetId);
    }
    handleDragOver(_e, targetId) {
        if (this.dragOverTargetIds === null) {
            this.dragOverTargetIds = [];
        }
        this.dragOverTargetIds.unshift(targetId);
    }
    handleDrop(_e, targetId) {
        this.dropTargetIds.unshift(targetId);
    }
    constructor(manager, globalContext, options){
        this.sourcePreviewNodes = new Map();
        this.sourcePreviewNodeOptions = new Map();
        this.sourceNodes = new Map();
        this.sourceNodeOptions = new Map();
        this.dragStartSourceIds = null;
        this.dropTargetIds = [];
        this.dragEnterTargetIds = [];
        this.currentNativeSource = null;
        this.currentNativeHandle = null;
        this.currentDragSourceNode = null;
        this.altKeyPressed = false;
        this.mouseMoveTimeoutTimer = null;
        this.asyncEndDragFrameId = null;
        this.dragOverTargetIds = null;
        this.lastClientOffset = null;
        this.hoverRafId = null;
        this.getSourceClientOffset = (sourceId)=>{
            const source = this.sourceNodes.get(sourceId);
            return source && getNodeClientOffset(source) || null;
        };
        this.endDragNativeItem = ()=>{
            if (!this.isDraggingNativeItem()) {
                return;
            }
            this.actions.endDrag();
            if (this.currentNativeHandle) {
                this.registry.removeSource(this.currentNativeHandle);
            }
            this.currentNativeHandle = null;
            this.currentNativeSource = null;
        };
        this.isNodeInDocument = (node)=>{
            // Check the node either in the main document or in the current context
            return Boolean(node && this.document && this.document.body && this.document.body.contains(node));
        };
        this.endDragIfSourceWasRemovedFromDOM = ()=>{
            const node = this.currentDragSourceNode;
            if (node == null || this.isNodeInDocument(node)) {
                return;
            }
            if (this.clearCurrentDragSourceNode() && this.monitor.isDragging()) {
                this.actions.endDrag();
            }
            this.cancelHover();
        };
        this.scheduleHover = (dragOverTargetIds)=>{
            if (this.hoverRafId === null && typeof requestAnimationFrame !== 'undefined') {
                this.hoverRafId = requestAnimationFrame(()=>{
                    if (this.monitor.isDragging()) {
                        this.actions.hover(dragOverTargetIds || [], {
                            clientOffset: this.lastClientOffset
                        });
                    }
                    this.hoverRafId = null;
                });
            }
        };
        this.cancelHover = ()=>{
            if (this.hoverRafId !== null && typeof cancelAnimationFrame !== 'undefined') {
                cancelAnimationFrame(this.hoverRafId);
                this.hoverRafId = null;
            }
        };
        this.handleTopDragStartCapture = ()=>{
            this.clearCurrentDragSourceNode();
            this.dragStartSourceIds = [];
        };
        this.handleTopDragStart = (e)=>{
            if (e.defaultPrevented) {
                return;
            }
            const { dragStartSourceIds  } = this;
            this.dragStartSourceIds = null;
            const clientOffset = getEventClientOffset(e);
            // Avoid crashing if we missed a drop event or our previous drag died
            if (this.monitor.isDragging()) {
                this.actions.endDrag();
                this.cancelHover();
            }
            // Don't publish the source just yet (see why below)
            this.actions.beginDrag(dragStartSourceIds || [], {
                publishSource: false,
                getSourceClientOffset: this.getSourceClientOffset,
                clientOffset
            });
            const { dataTransfer  } = e;
            const nativeType = matchNativeItemType(dataTransfer);
            if (this.monitor.isDragging()) {
                if (dataTransfer && typeof dataTransfer.setDragImage === 'function') {
                    // Use custom drag image if user specifies it.
                    // If child drag source refuses drag but parent agrees,
                    // use parent's node as drag image. Neither works in IE though.
                    const sourceId = this.monitor.getSourceId();
                    const sourceNode = this.sourceNodes.get(sourceId);
                    const dragPreview = this.sourcePreviewNodes.get(sourceId) || sourceNode;
                    if (dragPreview) {
                        const { anchorX , anchorY , offsetX , offsetY  } = this.getCurrentSourcePreviewNodeOptions();
                        const anchorPoint = {
                            anchorX,
                            anchorY
                        };
                        const offsetPoint = {
                            offsetX,
                            offsetY
                        };
                        const dragPreviewOffset = getDragPreviewOffset(sourceNode, dragPreview, clientOffset, anchorPoint, offsetPoint);
                        dataTransfer.setDragImage(dragPreview, dragPreviewOffset.x, dragPreviewOffset.y);
                    }
                }
                try {
                    // Firefox won't drag without setting data
                    dataTransfer === null || dataTransfer === void 0 ? void 0 : dataTransfer.setData('application/json', {});
                } catch (err) {
                // IE doesn't support MIME types in setData
                }
                // Store drag source node so we can check whether
                // it is removed from DOM and trigger endDrag manually.
                this.setCurrentDragSourceNode(e.target);
                // Now we are ready to publish the drag source.. or are we not?
                const { captureDraggingState  } = this.getCurrentSourcePreviewNodeOptions();
                if (!captureDraggingState) {
                    // Usually we want to publish it in the next tick so that browser
                    // is able to screenshot the current (not yet dragging) state.
                    //
                    // It also neatly avoids a situation where render() returns null
                    // in the same tick for the source element, and browser freaks out.
                    setTimeout(()=>this.actions.publishDragSource()
                    , 0);
                } else {
                    // In some cases the user may want to override this behavior, e.g.
                    // to work around IE not supporting custom drag previews.
                    //
                    // When using a custom drag layer, the only way to prevent
                    // the default drag preview from drawing in IE is to screenshot
                    // the dragging state in which the node itself has zero opacity
                    // and height. In this case, though, returning null from render()
                    // will abruptly end the dragging, which is not obvious.
                    //
                    // This is the reason such behavior is strictly opt-in.
                    this.actions.publishDragSource();
                }
            } else if (nativeType) {
                // A native item (such as URL) dragged from inside the document
                this.beginDragNativeItem(nativeType);
            } else if (dataTransfer && !dataTransfer.types && (e.target && !e.target.hasAttribute || !e.target.hasAttribute('draggable'))) {
                // Looks like a Safari bug: dataTransfer.types is null, but there was no draggable.
                // Just let it drag. It's a native type (URL or text) and will be picked up in
                // dragenter handler.
                return;
            } else {
                // If by this time no drag source reacted, tell browser not to drag.
                e.preventDefault();
            }
        };
        this.handleTopDragEndCapture = ()=>{
            if (this.clearCurrentDragSourceNode() && this.monitor.isDragging()) {
                // Firefox can dispatch this event in an infinite loop
                // if dragend handler does something like showing an alert.
                // Only proceed if we have not handled it already.
                this.actions.endDrag();
            }
            this.cancelHover();
        };
        this.handleTopDragEnterCapture = (e)=>{
            this.dragEnterTargetIds = [];
            if (this.isDraggingNativeItem()) {
                var ref;
                (ref = this.currentNativeSource) === null || ref === void 0 ? void 0 : ref.loadDataTransfer(e.dataTransfer);
            }
            const isFirstEnter = this.enterLeaveCounter.enter(e.target);
            if (!isFirstEnter || this.monitor.isDragging()) {
                return;
            }
            const { dataTransfer  } = e;
            const nativeType = matchNativeItemType(dataTransfer);
            if (nativeType) {
                // A native item (such as file or URL) dragged from outside the document
                this.beginDragNativeItem(nativeType, dataTransfer);
            }
        };
        this.handleTopDragEnter = (e)=>{
            const { dragEnterTargetIds  } = this;
            this.dragEnterTargetIds = [];
            if (!this.monitor.isDragging()) {
                // This is probably a native item type we don't understand.
                return;
            }
            this.altKeyPressed = e.altKey;
            // If the target changes position as the result of `dragenter`, `dragover` might still
            // get dispatched despite target being no longer there. The easy solution is to check
            // whether there actually is a target before firing `hover`.
            if (dragEnterTargetIds.length > 0) {
                this.actions.hover(dragEnterTargetIds, {
                    clientOffset: getEventClientOffset(e)
                });
            }
            const canDrop = dragEnterTargetIds.some((targetId)=>this.monitor.canDropOnTarget(targetId)
            );
            if (canDrop) {
                // IE requires this to fire dragover events
                e.preventDefault();
                if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = this.getCurrentDropEffect();
                }
            }
        };
        this.handleTopDragOverCapture = (e)=>{
            this.dragOverTargetIds = [];
            if (this.isDraggingNativeItem()) {
                var ref;
                (ref = this.currentNativeSource) === null || ref === void 0 ? void 0 : ref.loadDataTransfer(e.dataTransfer);
            }
        };
        this.handleTopDragOver = (e)=>{
            const { dragOverTargetIds  } = this;
            this.dragOverTargetIds = [];
            if (!this.monitor.isDragging()) {
                // This is probably a native item type we don't understand.
                // Prevent default "drop and blow away the whole document" action.
                e.preventDefault();
                if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = 'none';
                }
                return;
            }
            this.altKeyPressed = e.altKey;
            this.lastClientOffset = getEventClientOffset(e);
            this.scheduleHover(dragOverTargetIds);
            const canDrop = (dragOverTargetIds || []).some((targetId)=>this.monitor.canDropOnTarget(targetId)
            );
            if (canDrop) {
                // Show user-specified drop effect.
                e.preventDefault();
                if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = this.getCurrentDropEffect();
                }
            } else if (this.isDraggingNativeItem()) {
                // Don't show a nice cursor but still prevent default
                // "drop and blow away the whole document" action.
                e.preventDefault();
            } else {
                e.preventDefault();
                if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = 'none';
                }
            }
        };
        this.handleTopDragLeaveCapture = (e)=>{
            if (this.isDraggingNativeItem()) {
                e.preventDefault();
            }
            const isLastLeave = this.enterLeaveCounter.leave(e.target);
            if (!isLastLeave) {
                return;
            }
            if (this.isDraggingNativeItem()) {
                setTimeout(()=>this.endDragNativeItem()
                , 0);
            }
            this.cancelHover();
        };
        this.handleTopDropCapture = (e)=>{
            this.dropTargetIds = [];
            if (this.isDraggingNativeItem()) {
                var ref;
                e.preventDefault();
                (ref = this.currentNativeSource) === null || ref === void 0 ? void 0 : ref.loadDataTransfer(e.dataTransfer);
            } else if (matchNativeItemType(e.dataTransfer)) {
                // Dragging some elements, like <a> and <img> may still behave like a native drag event,
                // even if the current drag event matches a user-defined type.
                // Stop the default behavior when we're not expecting a native item to be dropped.
                e.preventDefault();
            }
            this.enterLeaveCounter.reset();
        };
        this.handleTopDrop = (e)=>{
            const { dropTargetIds  } = this;
            this.dropTargetIds = [];
            this.actions.hover(dropTargetIds, {
                clientOffset: getEventClientOffset(e)
            });
            this.actions.drop({
                dropEffect: this.getCurrentDropEffect()
            });
            if (this.isDraggingNativeItem()) {
                this.endDragNativeItem();
            } else if (this.monitor.isDragging()) {
                this.actions.endDrag();
            }
            this.cancelHover();
        };
        this.handleSelectStart = (e)=>{
            const target = e.target;
            // Only IE requires us to explicitly say
            // we want drag drop operation to start
            if (typeof target.dragDrop !== 'function') {
                return;
            }
            // Inputs and textareas should be selectable
            if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
                return;
            }
            // For other targets, ask IE
            // to enable drag and drop
            e.preventDefault();
            target.dragDrop();
        };
        this.options = new OptionsReader(globalContext, options);
        this.actions = manager.getActions();
        this.monitor = manager.getMonitor();
        this.registry = manager.getRegistry();
        this.enterLeaveCounter = new EnterLeaveCounter(this.isNodeInDocument);
    }
}

let emptyImage;
function getEmptyImage() {
    if (!emptyImage) {
        emptyImage = new Image();
        emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    }
    return emptyImage;
}

const HTML5Backend = function createBackend(manager, context, options) {
    return new HTML5BackendImpl(manager, context, options);
};

const searchConfigs = {
  types: ['text', 'select'],
  typeEnum: {
    text: 'text',
    select: 'select'
  }
};
const resizeConfigs = {
  typeEnum: {
    resize: 'resize',
    autoSize: 'autoSize'
  }
};

const isStickyActive = (targetInfo, rootBoundsInfo, position) => position === 'left' ? Math.floor(targetInfo.right) <= rootBoundsInfo.left : position === 'right' ? Math.ceil(targetInfo.left) >= rootBoundsInfo.right : position === 'top' ? targetInfo.bottom >= rootBoundsInfo.top : position === 'bottom' ? targetInfo.bottom <= rootBoundsInfo.bottom : null;
const createStickyObserver = (container, stickyTarget, eventName, position, additionalInfo) => {
  const {
    offset
  } = additionalInfo;
  const observer = new IntersectionObserver(records => {
    for (const record of records) {
      const targetInfo = record.boundingClientRect;
      const rootBoundsInfo = record.rootBounds;
      const isSticky = isStickyActive(targetInfo, rootBoundsInfo, position);
      fireEvent(eventName, {
        isSticky,
        position,
        additionalInfo
      });
    }
  }, {
    threshold: 1,
    root: container,
    rootMargin: "0px -".concat(offset.right, "px 0px -").concat(offset.left, "px")
  });
  observer.observe(stickyTarget);
  return () => observer.unobserve(stickyTarget);
};
const fireEvent = (eventName, detail) => document.dispatchEvent(new CustomEvent(eventName, {
  detail
}));
const observeStickyInfo = (container, targets, eventName, additionalInfo) => Object.keys(targets).map(key => createStickyObserver(container, targets[key], eventName, key, additionalInfo));

function useGetColsInfo(initialValue, prefix) {
  const [colsInfo, setColsInfo] = useState(initialValue);
  const updateColsInfo = async (id, autoSizeOn, customWidth) => {
    /* TODO:
     * This is a temporary solution until we come up with something better
     * here we have a problem with rendering and using useEffect hook when we use
     * document.querySelectorAll elements are not generated and therefore we cannot
     * check their width
     */
    const autoSizeWidth = await Promise.resolve().then(() => {
      let width = 0;
      if (autoSizeOn) {
        const prevWidth = colsInfo[id].autoSizeWidth;
        const ArrayOfEls = Array.from(document.querySelectorAll("div[data-id=\"".concat(prefix, "-").concat(id, "\"]")));
        if (ArrayOfEls.length > 1) {
          width = Math.max(...ArrayOfEls.map(element => {
            element.style.width = 'fit-content';
            return element.clientWidth + 2;
          }));
          ArrayOfEls.forEach(element => {
            element.style.width = "".concat(width, "px");
          });
        } else if (prevWidth) {
          width = prevWidth;
        }
      }
      return width;
    });
    setColsInfo(prev => ({
      ...prev,
      [id]: {
        ...colsInfo[id],
        autoSizeWidth,
        customWidth,
        autoSizeOn
      }
    }));
  };
  const reInit = columns => {
    const newColumns = columns.reduce((acc, column) => {
      acc[column.id] = colsInfo[column.id] || {
        autoSizeOn: null,
        customWidth: null,
        autoSizeWidth: null,
        defaultCustomWidth: null
      };
      return acc;
    }, {});
    setColsInfo(newColumns);
  };
  return [colsInfo, updateColsInfo, reInit];
}

const arrayReorder = (array, from, to) => {
  const arrayCopy = [...array];
  const [removed] = arrayCopy.splice(from, 1);
  arrayCopy.splice(to, 0, removed);
  return arrayCopy;
};

const itemTypes = {
  th: 'TH',
  tr: 'TR'
};

const getOffsetValuesAndSubscriptions = function (state, index, updatedState) {
  let offsetLeft = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  let offSetRight = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
  let subscribe = arguments.length > 5 ? arguments[5] : undefined;
  let container = arguments.length > 6 ? arguments[6] : undefined;
  let eventName = arguments.length > 7 ? arguments[7] : undefined;
  const offset = Object.keys(updatedState).map(Number).reduce((offsetCalc, item) => {
    if (index > item) {
      offsetCalc.left += updatedState[item].target.clientWidth;
    } else if (index < item) {
      offsetCalc.right += updatedState[item].target.clientWidth;
    }
    return offsetCalc;
  }, {
    left: offsetLeft,
    right: offSetRight
  });
  const subscriptions = subscribe(container, {
    left: updatedState[index].left,
    right: updatedState[index].right
  }, eventName, {
    index,
    offset
  });
  return {
    ...state,
    [index]: {
      ...updatedState[index],
      offset,
      subscriptions
    }
  };
};

const hasStickyElements = columnsObject => {
  const keys = Object.keys(columnsObject);
  const stickyLeftExist = keys.some(column => columnsObject[column].isStickyLeft);
  const stickyRightExist = keys.some(column => columnsObject[column].isStickyRight);
  return {
    stickyLeftExist,
    stickyRightExist
  };
};

/**
 * A collection of shims that provide minimal functionality of the ES6 collections.
 *
 * These implementations are not meant to be used outside of the ResizeObserver
 * modules as they cover only a limited range of use cases.
 */
/* eslint-disable require-jsdoc, valid-jsdoc */
var MapShim = (function () {
    if (typeof Map !== 'undefined') {
        return Map;
    }
    /**
     * Returns index in provided array that matches the specified key.
     *
     * @param {Array<Array>} arr
     * @param {*} key
     * @returns {number}
     */
    function getIndex(arr, key) {
        var result = -1;
        arr.some(function (entry, index) {
            if (entry[0] === key) {
                result = index;
                return true;
            }
            return false;
        });
        return result;
    }
    return /** @class */ (function () {
        function class_1() {
            this.__entries__ = [];
        }
        Object.defineProperty(class_1.prototype, "size", {
            /**
             * @returns {boolean}
             */
            get: function () {
                return this.__entries__.length;
            },
            enumerable: true,
            configurable: true
        });
        /**
         * @param {*} key
         * @returns {*}
         */
        class_1.prototype.get = function (key) {
            var index = getIndex(this.__entries__, key);
            var entry = this.__entries__[index];
            return entry && entry[1];
        };
        /**
         * @param {*} key
         * @param {*} value
         * @returns {void}
         */
        class_1.prototype.set = function (key, value) {
            var index = getIndex(this.__entries__, key);
            if (~index) {
                this.__entries__[index][1] = value;
            }
            else {
                this.__entries__.push([key, value]);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.delete = function (key) {
            var entries = this.__entries__;
            var index = getIndex(entries, key);
            if (~index) {
                entries.splice(index, 1);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.has = function (key) {
            return !!~getIndex(this.__entries__, key);
        };
        /**
         * @returns {void}
         */
        class_1.prototype.clear = function () {
            this.__entries__.splice(0);
        };
        /**
         * @param {Function} callback
         * @param {*} [ctx=null]
         * @returns {void}
         */
        class_1.prototype.forEach = function (callback, ctx) {
            if (ctx === void 0) { ctx = null; }
            for (var _i = 0, _a = this.__entries__; _i < _a.length; _i++) {
                var entry = _a[_i];
                callback.call(ctx, entry[1], entry[0]);
            }
        };
        return class_1;
    }());
})();

/**
 * Detects whether window and document objects are available in current environment.
 */
var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined' && window.document === document;

// Returns global object of a current environment.
var global$1 = (function () {
    if (typeof global !== 'undefined' && global.Math === Math) {
        return global;
    }
    if (typeof self !== 'undefined' && self.Math === Math) {
        return self;
    }
    if (typeof window !== 'undefined' && window.Math === Math) {
        return window;
    }
    // eslint-disable-next-line no-new-func
    return Function('return this')();
})();

/**
 * A shim for the requestAnimationFrame which falls back to the setTimeout if
 * first one is not supported.
 *
 * @returns {number} Requests' identifier.
 */
var requestAnimationFrame$1 = (function () {
    if (typeof requestAnimationFrame === 'function') {
        // It's required to use a bounded function because IE sometimes throws
        // an "Invalid calling object" error if rAF is invoked without the global
        // object on the left hand side.
        return requestAnimationFrame.bind(global$1);
    }
    return function (callback) { return setTimeout(function () { return callback(Date.now()); }, 1000 / 60); };
})();

// Defines minimum timeout before adding a trailing call.
var trailingTimeout = 2;
/**
 * Creates a wrapper function which ensures that provided callback will be
 * invoked only once during the specified delay period.
 *
 * @param {Function} callback - Function to be invoked after the delay period.
 * @param {number} delay - Delay after which to invoke callback.
 * @returns {Function}
 */
function throttle (callback, delay) {
    var leadingCall = false, trailingCall = false, lastCallTime = 0;
    /**
     * Invokes the original callback function and schedules new invocation if
     * the "proxy" was called during current request.
     *
     * @returns {void}
     */
    function resolvePending() {
        if (leadingCall) {
            leadingCall = false;
            callback();
        }
        if (trailingCall) {
            proxy();
        }
    }
    /**
     * Callback invoked after the specified delay. It will further postpone
     * invocation of the original function delegating it to the
     * requestAnimationFrame.
     *
     * @returns {void}
     */
    function timeoutCallback() {
        requestAnimationFrame$1(resolvePending);
    }
    /**
     * Schedules invocation of the original function.
     *
     * @returns {void}
     */
    function proxy() {
        var timeStamp = Date.now();
        if (leadingCall) {
            // Reject immediately following calls.
            if (timeStamp - lastCallTime < trailingTimeout) {
                return;
            }
            // Schedule new call to be in invoked when the pending one is resolved.
            // This is important for "transitions" which never actually start
            // immediately so there is a chance that we might miss one if change
            // happens amids the pending invocation.
            trailingCall = true;
        }
        else {
            leadingCall = true;
            trailingCall = false;
            setTimeout(timeoutCallback, delay);
        }
        lastCallTime = timeStamp;
    }
    return proxy;
}

// Minimum delay before invoking the update of observers.
var REFRESH_DELAY = 20;
// A list of substrings of CSS properties used to find transition events that
// might affect dimensions of observed elements.
var transitionKeys = ['top', 'right', 'bottom', 'left', 'width', 'height', 'size', 'weight'];
// Check if MutationObserver is available.
var mutationObserverSupported = typeof MutationObserver !== 'undefined';
/**
 * Singleton controller class which handles updates of ResizeObserver instances.
 */
var ResizeObserverController = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserverController.
     *
     * @private
     */
    function ResizeObserverController() {
        /**
         * Indicates whether DOM listeners have been added.
         *
         * @private {boolean}
         */
        this.connected_ = false;
        /**
         * Tells that controller has subscribed for Mutation Events.
         *
         * @private {boolean}
         */
        this.mutationEventsAdded_ = false;
        /**
         * Keeps reference to the instance of MutationObserver.
         *
         * @private {MutationObserver}
         */
        this.mutationsObserver_ = null;
        /**
         * A list of connected observers.
         *
         * @private {Array<ResizeObserverSPI>}
         */
        this.observers_ = [];
        this.onTransitionEnd_ = this.onTransitionEnd_.bind(this);
        this.refresh = throttle(this.refresh.bind(this), REFRESH_DELAY);
    }
    /**
     * Adds observer to observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be added.
     * @returns {void}
     */
    ResizeObserverController.prototype.addObserver = function (observer) {
        if (!~this.observers_.indexOf(observer)) {
            this.observers_.push(observer);
        }
        // Add listeners if they haven't been added yet.
        if (!this.connected_) {
            this.connect_();
        }
    };
    /**
     * Removes observer from observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be removed.
     * @returns {void}
     */
    ResizeObserverController.prototype.removeObserver = function (observer) {
        var observers = this.observers_;
        var index = observers.indexOf(observer);
        // Remove observer if it's present in registry.
        if (~index) {
            observers.splice(index, 1);
        }
        // Remove listeners if controller has no connected observers.
        if (!observers.length && this.connected_) {
            this.disconnect_();
        }
    };
    /**
     * Invokes the update of observers. It will continue running updates insofar
     * it detects changes.
     *
     * @returns {void}
     */
    ResizeObserverController.prototype.refresh = function () {
        var changesDetected = this.updateObservers_();
        // Continue running updates if changes have been detected as there might
        // be future ones caused by CSS transitions.
        if (changesDetected) {
            this.refresh();
        }
    };
    /**
     * Updates every observer from observers list and notifies them of queued
     * entries.
     *
     * @private
     * @returns {boolean} Returns "true" if any observer has detected changes in
     *      dimensions of it's elements.
     */
    ResizeObserverController.prototype.updateObservers_ = function () {
        // Collect observers that have active observations.
        var activeObservers = this.observers_.filter(function (observer) {
            return observer.gatherActive(), observer.hasActive();
        });
        // Deliver notifications in a separate cycle in order to avoid any
        // collisions between observers, e.g. when multiple instances of
        // ResizeObserver are tracking the same element and the callback of one
        // of them changes content dimensions of the observed target. Sometimes
        // this may result in notifications being blocked for the rest of observers.
        activeObservers.forEach(function (observer) { return observer.broadcastActive(); });
        return activeObservers.length > 0;
    };
    /**
     * Initializes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.connect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already added.
        if (!isBrowser || this.connected_) {
            return;
        }
        // Subscription to the "Transitionend" event is used as a workaround for
        // delayed transitions. This way it's possible to capture at least the
        // final state of an element.
        document.addEventListener('transitionend', this.onTransitionEnd_);
        window.addEventListener('resize', this.refresh);
        if (mutationObserverSupported) {
            this.mutationsObserver_ = new MutationObserver(this.refresh);
            this.mutationsObserver_.observe(document, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true
            });
        }
        else {
            document.addEventListener('DOMSubtreeModified', this.refresh);
            this.mutationEventsAdded_ = true;
        }
        this.connected_ = true;
    };
    /**
     * Removes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.disconnect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already removed.
        if (!isBrowser || !this.connected_) {
            return;
        }
        document.removeEventListener('transitionend', this.onTransitionEnd_);
        window.removeEventListener('resize', this.refresh);
        if (this.mutationsObserver_) {
            this.mutationsObserver_.disconnect();
        }
        if (this.mutationEventsAdded_) {
            document.removeEventListener('DOMSubtreeModified', this.refresh);
        }
        this.mutationsObserver_ = null;
        this.mutationEventsAdded_ = false;
        this.connected_ = false;
    };
    /**
     * "Transitionend" event handler.
     *
     * @private
     * @param {TransitionEvent} event
     * @returns {void}
     */
    ResizeObserverController.prototype.onTransitionEnd_ = function (_a) {
        var _b = _a.propertyName, propertyName = _b === void 0 ? '' : _b;
        // Detect whether transition may affect dimensions of an element.
        var isReflowProperty = transitionKeys.some(function (key) {
            return !!~propertyName.indexOf(key);
        });
        if (isReflowProperty) {
            this.refresh();
        }
    };
    /**
     * Returns instance of the ResizeObserverController.
     *
     * @returns {ResizeObserverController}
     */
    ResizeObserverController.getInstance = function () {
        if (!this.instance_) {
            this.instance_ = new ResizeObserverController();
        }
        return this.instance_;
    };
    /**
     * Holds reference to the controller's instance.
     *
     * @private {ResizeObserverController}
     */
    ResizeObserverController.instance_ = null;
    return ResizeObserverController;
}());

/**
 * Defines non-writable/enumerable properties of the provided target object.
 *
 * @param {Object} target - Object for which to define properties.
 * @param {Object} props - Properties to be defined.
 * @returns {Object} Target object.
 */
var defineConfigurable = (function (target, props) {
    for (var _i = 0, _a = Object.keys(props); _i < _a.length; _i++) {
        var key = _a[_i];
        Object.defineProperty(target, key, {
            value: props[key],
            enumerable: false,
            writable: false,
            configurable: true
        });
    }
    return target;
});

/**
 * Returns the global object associated with provided element.
 *
 * @param {Object} target
 * @returns {Object}
 */
var getWindowOf = (function (target) {
    // Assume that the element is an instance of Node, which means that it
    // has the "ownerDocument" property from which we can retrieve a
    // corresponding global object.
    var ownerGlobal = target && target.ownerDocument && target.ownerDocument.defaultView;
    // Return the local global object if it's not possible extract one from
    // provided element.
    return ownerGlobal || global$1;
});

// Placeholder of an empty content rectangle.
var emptyRect = createRectInit(0, 0, 0, 0);
/**
 * Converts provided string to a number.
 *
 * @param {number|string} value
 * @returns {number}
 */
function toFloat(value) {
    return parseFloat(value) || 0;
}
/**
 * Extracts borders size from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @param {...string} positions - Borders positions (top, right, ...)
 * @returns {number}
 */
function getBordersSize(styles) {
    var positions = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        positions[_i - 1] = arguments[_i];
    }
    return positions.reduce(function (size, position) {
        var value = styles['border-' + position + '-width'];
        return size + toFloat(value);
    }, 0);
}
/**
 * Extracts paddings sizes from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @returns {Object} Paddings box.
 */
function getPaddings(styles) {
    var positions = ['top', 'right', 'bottom', 'left'];
    var paddings = {};
    for (var _i = 0, positions_1 = positions; _i < positions_1.length; _i++) {
        var position = positions_1[_i];
        var value = styles['padding-' + position];
        paddings[position] = toFloat(value);
    }
    return paddings;
}
/**
 * Calculates content rectangle of provided SVG element.
 *
 * @param {SVGGraphicsElement} target - Element content rectangle of which needs
 *      to be calculated.
 * @returns {DOMRectInit}
 */
function getSVGContentRect(target) {
    var bbox = target.getBBox();
    return createRectInit(0, 0, bbox.width, bbox.height);
}
/**
 * Calculates content rectangle of provided HTMLElement.
 *
 * @param {HTMLElement} target - Element for which to calculate the content rectangle.
 * @returns {DOMRectInit}
 */
function getHTMLElementContentRect(target) {
    // Client width & height properties can't be
    // used exclusively as they provide rounded values.
    var clientWidth = target.clientWidth, clientHeight = target.clientHeight;
    // By this condition we can catch all non-replaced inline, hidden and
    // detached elements. Though elements with width & height properties less
    // than 0.5 will be discarded as well.
    //
    // Without it we would need to implement separate methods for each of
    // those cases and it's not possible to perform a precise and performance
    // effective test for hidden elements. E.g. even jQuery's ':visible' filter
    // gives wrong results for elements with width & height less than 0.5.
    if (!clientWidth && !clientHeight) {
        return emptyRect;
    }
    var styles = getWindowOf(target).getComputedStyle(target);
    var paddings = getPaddings(styles);
    var horizPad = paddings.left + paddings.right;
    var vertPad = paddings.top + paddings.bottom;
    // Computed styles of width & height are being used because they are the
    // only dimensions available to JS that contain non-rounded values. It could
    // be possible to utilize the getBoundingClientRect if only it's data wasn't
    // affected by CSS transformations let alone paddings, borders and scroll bars.
    var width = toFloat(styles.width), height = toFloat(styles.height);
    // Width & height include paddings and borders when the 'border-box' box
    // model is applied (except for IE).
    if (styles.boxSizing === 'border-box') {
        // Following conditions are required to handle Internet Explorer which
        // doesn't include paddings and borders to computed CSS dimensions.
        //
        // We can say that if CSS dimensions + paddings are equal to the "client"
        // properties then it's either IE, and thus we don't need to subtract
        // anything, or an element merely doesn't have paddings/borders styles.
        if (Math.round(width + horizPad) !== clientWidth) {
            width -= getBordersSize(styles, 'left', 'right') + horizPad;
        }
        if (Math.round(height + vertPad) !== clientHeight) {
            height -= getBordersSize(styles, 'top', 'bottom') + vertPad;
        }
    }
    // Following steps can't be applied to the document's root element as its
    // client[Width/Height] properties represent viewport area of the window.
    // Besides, it's as well not necessary as the <html> itself neither has
    // rendered scroll bars nor it can be clipped.
    if (!isDocumentElement(target)) {
        // In some browsers (only in Firefox, actually) CSS width & height
        // include scroll bars size which can be removed at this step as scroll
        // bars are the only difference between rounded dimensions + paddings
        // and "client" properties, though that is not always true in Chrome.
        var vertScrollbar = Math.round(width + horizPad) - clientWidth;
        var horizScrollbar = Math.round(height + vertPad) - clientHeight;
        // Chrome has a rather weird rounding of "client" properties.
        // E.g. for an element with content width of 314.2px it sometimes gives
        // the client width of 315px and for the width of 314.7px it may give
        // 314px. And it doesn't happen all the time. So just ignore this delta
        // as a non-relevant.
        if (Math.abs(vertScrollbar) !== 1) {
            width -= vertScrollbar;
        }
        if (Math.abs(horizScrollbar) !== 1) {
            height -= horizScrollbar;
        }
    }
    return createRectInit(paddings.left, paddings.top, width, height);
}
/**
 * Checks whether provided element is an instance of the SVGGraphicsElement.
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
var isSVGGraphicsElement = (function () {
    // Some browsers, namely IE and Edge, don't have the SVGGraphicsElement
    // interface.
    if (typeof SVGGraphicsElement !== 'undefined') {
        return function (target) { return target instanceof getWindowOf(target).SVGGraphicsElement; };
    }
    // If it's so, then check that element is at least an instance of the
    // SVGElement and that it has the "getBBox" method.
    // eslint-disable-next-line no-extra-parens
    return function (target) { return (target instanceof getWindowOf(target).SVGElement &&
        typeof target.getBBox === 'function'); };
})();
/**
 * Checks whether provided element is a document element (<html>).
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
function isDocumentElement(target) {
    return target === getWindowOf(target).document.documentElement;
}
/**
 * Calculates an appropriate content rectangle for provided html or svg element.
 *
 * @param {Element} target - Element content rectangle of which needs to be calculated.
 * @returns {DOMRectInit}
 */
function getContentRect(target) {
    if (!isBrowser) {
        return emptyRect;
    }
    if (isSVGGraphicsElement(target)) {
        return getSVGContentRect(target);
    }
    return getHTMLElementContentRect(target);
}
/**
 * Creates rectangle with an interface of the DOMRectReadOnly.
 * Spec: https://drafts.fxtf.org/geometry/#domrectreadonly
 *
 * @param {DOMRectInit} rectInit - Object with rectangle's x/y coordinates and dimensions.
 * @returns {DOMRectReadOnly}
 */
function createReadOnlyRect(_a) {
    var x = _a.x, y = _a.y, width = _a.width, height = _a.height;
    // If DOMRectReadOnly is available use it as a prototype for the rectangle.
    var Constr = typeof DOMRectReadOnly !== 'undefined' ? DOMRectReadOnly : Object;
    var rect = Object.create(Constr.prototype);
    // Rectangle's properties are not writable and non-enumerable.
    defineConfigurable(rect, {
        x: x, y: y, width: width, height: height,
        top: y,
        right: x + width,
        bottom: height + y,
        left: x
    });
    return rect;
}
/**
 * Creates DOMRectInit object based on the provided dimensions and the x/y coordinates.
 * Spec: https://drafts.fxtf.org/geometry/#dictdef-domrectinit
 *
 * @param {number} x - X coordinate.
 * @param {number} y - Y coordinate.
 * @param {number} width - Rectangle's width.
 * @param {number} height - Rectangle's height.
 * @returns {DOMRectInit}
 */
function createRectInit(x, y, width, height) {
    return { x: x, y: y, width: width, height: height };
}

/**
 * Class that is responsible for computations of the content rectangle of
 * provided DOM element and for keeping track of it's changes.
 */
var ResizeObservation = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObservation.
     *
     * @param {Element} target - Element to be observed.
     */
    function ResizeObservation(target) {
        /**
         * Broadcasted width of content rectangle.
         *
         * @type {number}
         */
        this.broadcastWidth = 0;
        /**
         * Broadcasted height of content rectangle.
         *
         * @type {number}
         */
        this.broadcastHeight = 0;
        /**
         * Reference to the last observed content rectangle.
         *
         * @private {DOMRectInit}
         */
        this.contentRect_ = createRectInit(0, 0, 0, 0);
        this.target = target;
    }
    /**
     * Updates content rectangle and tells whether it's width or height properties
     * have changed since the last broadcast.
     *
     * @returns {boolean}
     */
    ResizeObservation.prototype.isActive = function () {
        var rect = getContentRect(this.target);
        this.contentRect_ = rect;
        return (rect.width !== this.broadcastWidth ||
            rect.height !== this.broadcastHeight);
    };
    /**
     * Updates 'broadcastWidth' and 'broadcastHeight' properties with a data
     * from the corresponding properties of the last observed content rectangle.
     *
     * @returns {DOMRectInit} Last observed content rectangle.
     */
    ResizeObservation.prototype.broadcastRect = function () {
        var rect = this.contentRect_;
        this.broadcastWidth = rect.width;
        this.broadcastHeight = rect.height;
        return rect;
    };
    return ResizeObservation;
}());

var ResizeObserverEntry = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObserverEntry.
     *
     * @param {Element} target - Element that is being observed.
     * @param {DOMRectInit} rectInit - Data of the element's content rectangle.
     */
    function ResizeObserverEntry(target, rectInit) {
        var contentRect = createReadOnlyRect(rectInit);
        // According to the specification following properties are not writable
        // and are also not enumerable in the native implementation.
        //
        // Property accessors are not being used as they'd require to define a
        // private WeakMap storage which may cause memory leaks in browsers that
        // don't support this type of collections.
        defineConfigurable(this, { target: target, contentRect: contentRect });
    }
    return ResizeObserverEntry;
}());

var ResizeObserverSPI = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback function that is invoked
     *      when one of the observed elements changes it's content dimensions.
     * @param {ResizeObserverController} controller - Controller instance which
     *      is responsible for the updates of observer.
     * @param {ResizeObserver} callbackCtx - Reference to the public
     *      ResizeObserver instance which will be passed to callback function.
     */
    function ResizeObserverSPI(callback, controller, callbackCtx) {
        /**
         * Collection of resize observations that have detected changes in dimensions
         * of elements.
         *
         * @private {Array<ResizeObservation>}
         */
        this.activeObservations_ = [];
        /**
         * Registry of the ResizeObservation instances.
         *
         * @private {Map<Element, ResizeObservation>}
         */
        this.observations_ = new MapShim();
        if (typeof callback !== 'function') {
            throw new TypeError('The callback provided as parameter 1 is not a function.');
        }
        this.callback_ = callback;
        this.controller_ = controller;
        this.callbackCtx_ = callbackCtx;
    }
    /**
     * Starts observing provided element.
     *
     * @param {Element} target - Element to be observed.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.observe = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is already being observed.
        if (observations.has(target)) {
            return;
        }
        observations.set(target, new ResizeObservation(target));
        this.controller_.addObserver(this);
        // Force the update of observations.
        this.controller_.refresh();
    };
    /**
     * Stops observing provided element.
     *
     * @param {Element} target - Element to stop observing.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.unobserve = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is not being observed.
        if (!observations.has(target)) {
            return;
        }
        observations.delete(target);
        if (!observations.size) {
            this.controller_.removeObserver(this);
        }
    };
    /**
     * Stops observing all elements.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.disconnect = function () {
        this.clearActive();
        this.observations_.clear();
        this.controller_.removeObserver(this);
    };
    /**
     * Collects observation instances the associated element of which has changed
     * it's content rectangle.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.gatherActive = function () {
        var _this = this;
        this.clearActive();
        this.observations_.forEach(function (observation) {
            if (observation.isActive()) {
                _this.activeObservations_.push(observation);
            }
        });
    };
    /**
     * Invokes initial callback function with a list of ResizeObserverEntry
     * instances collected from active resize observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.broadcastActive = function () {
        // Do nothing if observer doesn't have active observations.
        if (!this.hasActive()) {
            return;
        }
        var ctx = this.callbackCtx_;
        // Create ResizeObserverEntry instance for every active observation.
        var entries = this.activeObservations_.map(function (observation) {
            return new ResizeObserverEntry(observation.target, observation.broadcastRect());
        });
        this.callback_.call(ctx, entries, ctx);
        this.clearActive();
    };
    /**
     * Clears the collection of active observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.clearActive = function () {
        this.activeObservations_.splice(0);
    };
    /**
     * Tells whether observer has active observations.
     *
     * @returns {boolean}
     */
    ResizeObserverSPI.prototype.hasActive = function () {
        return this.activeObservations_.length > 0;
    };
    return ResizeObserverSPI;
}());

// Registry of internal observers. If WeakMap is not available use current shim
// for the Map collection as it has all required methods and because WeakMap
// can't be fully polyfilled anyway.
var observers = typeof WeakMap !== 'undefined' ? new WeakMap() : new MapShim();
/**
 * ResizeObserver API. Encapsulates the ResizeObserver SPI implementation
 * exposing only those methods and properties that are defined in the spec.
 */
var ResizeObserver = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback that is invoked when
     *      dimensions of the observed elements change.
     */
    function ResizeObserver(callback) {
        if (!(this instanceof ResizeObserver)) {
            throw new TypeError('Cannot call a class as a function.');
        }
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        var controller = ResizeObserverController.getInstance();
        var observer = new ResizeObserverSPI(callback, controller, this);
        observers.set(this, observer);
    }
    return ResizeObserver;
}());
// Expose public methods of ResizeObserver.
[
    'observe',
    'unobserve',
    'disconnect'
].forEach(function (method) {
    ResizeObserver.prototype[method] = function () {
        var _a;
        return (_a = observers.get(this))[method].apply(_a, arguments);
    };
});

var index = (function () {
    // Export existing implementation if available.
    if (typeof global$1.ResizeObserver !== 'undefined') {
        return global$1.ResizeObserver;
    }
    return ResizeObserver;
})();

const observeElementResize = (element, callback) => {
  const resizeObserver = new index(callback);
  if (element) {
    resizeObserver.observe(element);
    return {
      unobserve: () => resizeObserver.unobserve(element)
    };
  }
};

const sortHandler = (nextSortObject, setSortObject, onSortChange, sortableColumns, currentSortState) => {
  const {
    id,
    column,
    dataKey,
    index
  } = nextSortObject;
  const isSortable = typeof column.sortable === 'boolean' ? column.sortable : sortableColumns;
  const {
    id: currId,
    type: currType
  } = currentSortState;
  const type = id !== currId || !currType ? 'asc' : currType === 'asc' ? 'desc' : null;
  if (isSortable) {
    setSortObject({
      id,
      type,
      index
    });
    onSortChange(isSortable, id, index, dataKey, column, type);
  }
};

const getValue = (value, formatter) => formatter ? formatter(value) : value;
const useSortConfigs = (sortConfigs, columns, rows, columnKey) => {
  const {
    type,
    id
  } = sortConfigs;
  const isSortExists = type !== null;
  const column = columns.find(col => col[columnKey] === id) || {};
  const dataKey = isSortExists ? column.dataKey : null;
  const defaultSortFn = useCallback((prev, next, dataKey, type, formatter, removeSymbol) => {
    const first = getValue(prev.data[dataKey], formatter);
    const second = getValue(next.data[dataKey], formatter);
    const firstValue = removeSymbol ? parseFloat(first.replace(/[^\d.-]/g, '')) : first;
    const secondValue = removeSymbol ? parseFloat(second.replace(/[^\d.-]/g, '')) : second;
    if (firstValue === null) return 1;
    if (secondValue === null) return -1;
    const firstLowerCased = typeof firstValue === 'string' ? firstValue.toLowerCase() : firstValue;
    const secondLowerCased = typeof secondValue === 'string' ? secondValue.toLowerCase() : secondValue;
    if (type === 'asc') {
      return firstLowerCased > secondLowerCased ? 1 : firstLowerCased < secondLowerCased ? -1 : 0;
    }
    if (type === 'desc') {
      return firstLowerCased < secondLowerCased ? 1 : firstLowerCased > secondLowerCased ? -1 : 0;
    }
    return 0;
  }, []);
  const sortFn = useMemo(() => isSortExists ? (prev, next) => {
    const {
      sortFn,
      formatter,
      removeSymbol
    } = column;
    const fn = sortFn || defaultSortFn;
    return typeof prev === 'object' ? fn(prev, next, dataKey, type, formatter, removeSymbol) : fn(rows[prev], rows[next], dataKey, type, formatter, removeSymbol);
  } : undefined, [isSortExists, column, defaultSortFn, dataKey, type, rows]);
  return [type, column[columnKey], sortFn];
};

/* global Map:readonly, Set:readonly, ArrayBuffer:readonly */

var hasElementType = typeof Element !== 'undefined';
var hasMap = typeof Map === 'function';
var hasSet = typeof Set === 'function';
var hasArrayBuffer = typeof ArrayBuffer === 'function' && !!ArrayBuffer.isView;

// Note: We **don't** need `envHasBigInt64Array` in fde es6/index.js

function equal(a, b) {
  // START: fast-deep-equal es6/index.js 3.1.3
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;

    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (!equal(a[i], b[i])) return false;
      return true;
    }

    // START: Modifications:
    // 1. Extra `has<Type> &&` helpers in initial condition allow es6 code
    //    to co-exist with es5.
    // 2. Replace `for of` with es5 compliant iteration using `for`.
    //    Basically, take:
    //
    //    ```js
    //    for (i of a.entries())
    //      if (!b.has(i[0])) return false;
    //    ```
    //
    //    ... and convert to:
    //
    //    ```js
    //    it = a.entries();
    //    while (!(i = it.next()).done)
    //      if (!b.has(i.value[0])) return false;
    //    ```
    //
    //    **Note**: `i` access switches to `i.value`.
    var it;
    if (hasMap && (a instanceof Map) && (b instanceof Map)) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!b.has(i.value[0])) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!equal(i.value[1], b.get(i.value[0]))) return false;
      return true;
    }

    if (hasSet && (a instanceof Set) && (b instanceof Set)) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!b.has(i.value[0])) return false;
      return true;
    }
    // END: Modifications

    if (hasArrayBuffer && ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (a[i] !== b[i]) return false;
      return true;
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    // START: Modifications:
    // Apply guards for `Object.create(null)` handling. See:
    // - https://github.com/FormidableLabs/react-fast-compare/issues/64
    // - https://github.com/epoberezkin/fast-deep-equal/issues/49
    if (a.valueOf !== Object.prototype.valueOf && typeof a.valueOf === 'function' && typeof b.valueOf === 'function') return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString && typeof a.toString === 'function' && typeof b.toString === 'function') return a.toString() === b.toString();
    // END: Modifications

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;)
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    // END: fast-deep-equal

    // START: react-fast-compare
    // custom handling for DOM elements
    if (hasElementType && a instanceof Element) return false;

    // custom handling for React/Preact
    for (i = length; i-- !== 0;) {
      if ((keys[i] === '_owner' || keys[i] === '__v' || keys[i] === '__o') && a.$$typeof) {
        // React-specific: avoid traversing React elements' _owner
        // Preact-specific: avoid traversing Preact elements' __v and __o
        //    __v = $_original / $_vnode
        //    __o = $_owner
        // These properties contain circular references and are not needed when
        // comparing the actual elements (and not their owners)
        // .$$typeof and ._store on just reasonable markers of elements

        continue;
      }

      // all other properties should be traversed as usual
      if (!equal(a[keys[i]], b[keys[i]])) return false;
    }
    // END: react-fast-compare

    // START: fast-deep-equal
    return true;
  }

  return a !== a && b !== b;
}
// end fast-deep-equal

var reactFastCompare = function isEqual(a, b) {
  try {
    return equal(a, b);
  } catch (error) {
    if (((error.message || '').match(/stack|recursion/i))) {
      // warn on circular references, don't crash
      // browsers give this different errors name and messages:
      // chrome/safari: "RangeError", "Maximum call stack size exceeded"
      // firefox: "InternalError", too much recursion"
      // edge: "Error", "Out of stack space"
      console.warn('react-fast-compare cannot handle circular refs');
      return false;
    }
    // some other error. we should definitely know about these
    throw error;
  }
};

function ActionsWrapper(_ref) {
  let {
    children
  } = _ref;
  return reactIsExports.isFragment(children) || Array.isArray(children) ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: "ta-action ta-a-1"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "ta-item"
  }, /*#__PURE__*/React__default.createElement(Button, {
    icon: "bc-icon-more-horizontal",
    size: "big",
    appearance: "minimal"
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "ta-action ta-a-2"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "ta-item"
  }, children))) : /*#__PURE__*/React__default.createElement("div", {
    className: "ta-action ta-a-1"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "ta-item"
  }, children));
}

function ActionBar(_ref) {
  let {
    children,
    lastColRef,
    stickyRightExist
  } = _ref;
  return /*#__PURE__*/React__default.createElement("div", {
    className: classnames('ta-cell', 'right-actions-holder', 'a-square', {
      'sticky sticky-right now-sticky': stickyRightExist
    }),
    ref: lastColRef
  }, children && /*#__PURE__*/React__default.createElement(ActionsWrapper, null, children));
}
var ActionBar$1 = /*#__PURE__*/memo(ActionBar, reactFastCompare);

function Header$2(_ref) {
  let {
    columns
  } = _ref;
  return /*#__PURE__*/React__default.createElement("ul", {
    className: "nt-row nt-head"
  }, columns.map((column, index) => {
    const {
      render,
      text
    } = column;
    const children = render ? render(column, index) : text;
    return /*#__PURE__*/React__default.createElement("div", {
      key: index,
      title: children,
      className: "nt-cell"
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "ellipsis-text"
    }, children));
  }));
}

const Body = _ref => {
  let {
    rows,
    columns,
    isEditActive
  } = _ref;
  return rows.length && rows.map((row, index) => row.render ? row.render(row, index, columns.dataKey) : /*#__PURE__*/React__default.createElement("div", {
    className: "nt-row",
    key: index
  }, columns.map((column, index) => /*#__PURE__*/React__default.createElement("div", {
    key: index,
    className: "nt-cell"
  }, typeof row.data[column.dataKey] === 'function' ? row.data[column.dataKey](isEditActive, index, column.dataKey) : row.data[column.dataKey]))));
};
var Body$1 = /*#__PURE__*/memo(Body, reactFastCompare);

var css_248z$3 = "[data-gene-ui-version=\"2.15.1\"] .nested-table{border:1px solid rgba(var(--background-sc-rgb),.1);border-radius:.4rem;display:flex;flex-direction:column;font:600 1.4rem/2rem var(--font-family);overflow:hidden;width:100%}[data-gene-ui-version=\"2.15.1\"] .nt-row{display:flex;width:100%}[data-gene-ui-version=\"2.15.1\"] .nt-row~.nt-row{border-top:1px solid rgba(var(--background-sc-rgb),.1)}[data-gene-ui-version=\"2.15.1\"] .nt-head{background:rgba(var(--background-sc-rgb),.04);font-weight:700}[data-gene-ui-version=\"2.15.1\"] .nt-cell{display:flex;overflow:hidden;padding:1.1rem 2rem;position:relative;width:100%;word-break:break-word}[data-gene-ui-version=\"2.15.1\"] .nt-cell~.nt-cell:before{background:var(--background);border-right:1px solid rgba(var(--background-sc-rgb),.15);bottom:1rem;content:\"\";display:block;position:absolute;top:1rem}html:not([dir=rtl]) .nt-cell~.nt-cell:before{left:0}html[dir=rtl] .nt-cell~.nt-cell:before{right:0}";
styleInject(css_248z$3);

function Nested(_ref) {
  let {
    rows,
    columns,
    isEditActive,
    ...restProps
  } = _ref;
  return /*#__PURE__*/React__default.createElement("div", _extends({
    className: "nested-table"
  }, restProps), /*#__PURE__*/React__default.createElement(Header$2, {
    columns: columns
  }), /*#__PURE__*/React__default.createElement(Body$1, {
    rows: rows,
    columns: columns,
    isEditActive: isEditActive
  }));
}
Nested.defaultProps = {
  columns: [],
  rows: []
};

function Col(_ref) {
  let {
    id,
    row,
    text,
    index,
    getter,
    prefix,
    colIndex,
    copyable,
    formatter,
    autoSizeOn,
    colRenderer,
    isEditActive,
    customWidth,
    autoSizeWidth,
    copyableValue,
    stickyColumns,
    copyTooltipText,
    copiedTooltipText,
    initialColWidth,
    disabledColumnPin,
    defaultCustomWidth,
    ...restProps
  } = _ref;
  const mainRef = useRef(null);
  const guidRef = useRef(null);
  const mounted = useRef(null);
  const {
    [colIndex]: colStickyInfo = {}
  } = stickyColumns;
  const {
    offset = {},
    isStickyLeft,
    isStickyRight
  } = colStickyInfo;
  const stickyStatus = (String(colIndex) in stickyColumns);
  const {
    left = 0,
    right = 0
  } = offset;
  const style = {
    left,
    right
  };
  const getterValue = getter ? getter(row, index, isEditActive) : text;
  const formattedValue = formatter ? formatter(getterValue, row, index, isEditActive) : getterValue;
  const inlineStyle = {
    width: autoSizeOn ? autoSizeWidth : customWidth || defaultCustomWidth || initialColWidth
  };
  const isValuePromise = formattedValue instanceof Promise;
  const [promiseValue, setPromiseValue] = useState(() => {
    if (isValuePromise) {
      guidRef.current = guid();
      return guidRef.current;
    }
  });
  useEffect(() => {
    mounted.current = true;
    isValuePromise && formattedValue.then(result => mounted.current && setPromiseValue(result));
    return () => {
      mounted.current = false;
    };
  }, [formattedValue, isValuePromise, mounted.current]);
  const value = isValuePromise ? promiseValue : formattedValue;
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, stickyStatus && isStickyLeft && /*#__PURE__*/React__default.createElement("sub", null), stickyStatus && isStickyRight && /*#__PURE__*/React__default.createElement("sup", null), /*#__PURE__*/React__default.createElement("div", _extends({
    className: classnames('ta-cell', {
      ...(!disabledColumnPin ? {
        sticky: stickyStatus,
        'sticky-left': isStickyLeft,
        'sticky-right': isStickyRight
      } : {}),
      'now-sticky': isStickyRight || isStickyLeft
    }),
    "data-id": "".concat(prefix, "-").concat(id)
  }, restProps, {
    style: {
      ...style,
      ...inlineStyle
    },
    title: colRenderer ? '' : value,
    ref: mainRef
  }), /*#__PURE__*/React__default.createElement(SkeletonLoader, {
    height: 20,
    isBusy: guidRef.current && promiseValue === guidRef.current
  }, copyable && value && /*#__PURE__*/React__default.createElement(Copy, {
    value: value,
    size: "small",
    className: "cursor-pointer copy-icon",
    copyTooltipText: copyTooltipText,
    copiedTooltipText: copiedTooltipText
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "ellipsis-text"
  }, colRenderer ? colRenderer(value, index, row, isEditActive, formatter) : value))));
}
Col.defaultProps = {
  copyTooltipText: 'Copy',
  copiedTooltipText: 'Copied!'
};
var Col$1 = /*#__PURE__*/React__default.memo(Col, reactFastCompare);

function Row(_ref) {
  let {
    style,
    index,
    prefix,
    opened,
    rowKey,
    orders,
    onClick,
    columns,
    eventRef,
    rowCount,
    colsInfo,
    moveItem,
    hasFooter,
    actionBar,
    rowsHover,
    sortedRows,
    rowsLength,
    lastColRef,
    isEditActive,
    CustomPreview,
    stickyColumns,
    setToggledRefs,
    rowExtraClick,
    toggleTooltips,
    rowExtraClickIconTooltip,
    selectedRowKey,
    withLeftCorner,
    rowOrderNumber,
    selectableRows,
    handleRowClick,
    rowHeightCache,
    stickyLeftExist,
    handleSelectRow,
    virtualizedList,
    initialColWidth,
    stickyRightExist,
    hasNestedElements,
    getDragAcceptType,
    disabledColumnPin,
    rowExtraClickNeeded,
    setToggledRowsKeys,
    renderRowNestedChildren,
    getExpandIconDisableState,
    // TODO: change prop name to getExpandDisableState
    ...row
  } = _ref;
  const {
    data,
    nestedTable,
    sortType,
    hasHover,
    dragDisable,
    className,
    disabled,
    render
  } = row;
  const ref = useRef(null);
  const nestedRef = useRef(null);
  const isExpandDisabled = useMemo(() => getExpandIconDisableState && getExpandIconDisableState(row, index), [getExpandIconDisableState, index, row]);
  const handleNestedUpdate = useCallback(() => {
    rowHeightCache.clear(rowOrderNumber);
    virtualizedList.recomputeRowHeights(rowOrderNumber + 1);
  }, [setToggledRefs, rowHeightCache, rowOrderNumber, virtualizedList]);
  useMutationObserver(nestedRef, handleNestedUpdate, {
    childList: true
  });
  const [_, drop] = useDrop({
    accept: "".concat(itemTypes.tr).concat(eventRef.current),
    drop(item) {
      const dragIndex = item.index;
      const hoverIndex = index;
      moveItem(dragIndex, hoverIndex);
    }
  });
  const [ignored, drag, preview] = useDrag({
    item: {
      index
    },
    type: "".concat(itemTypes.tr).concat(eventRef.current),
    canDrag: () => !opened && !dragDisable && !sortType && !isEditActive && rowsLength > 0,
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });
  useEffect(() => {
    setToggledRefs(prev => {
      const {
        [index]: removableKey,
        ...refs
      } = prev;
      return opened ? {
        ...refs,
        [index]: nestedRef
      } : refs;
    });
  }, [opened, index, nestedRef.current]);
  useUpdate(() => {
    handleNestedUpdate();
  }, [handleNestedUpdate]);
  const rowSelected = rowKey === selectedRowKey;
  const extraClickNeeded = typeof rowExtraClickNeeded === 'function' ? rowExtraClickNeeded(row, index) : rowExtraClickNeeded;
  const rowToggleHandler = useCallback(e => {
    stopEvent(e);
    if (!disabled) {
      handleRowClick(index, row);
      if (!isExpandDisabled) {
        hasNestedElements && setToggledRowsKeys(prev => {
          if (prev.includes(rowKey)) {
            return prev.filter(key => key !== rowKey);
          }
          return [...prev, rowKey];
        }, [hasNestedElements]);
        handleNestedUpdate();
      }
    }
  }, [isExpandDisabled, handleNestedUpdate, setToggledRowsKeys, handleRowClick, nestedRef, disabled, rowKey, index, row]);
  const onExtraClick = useCallback(e => {
    stopEvent(e);
    extraClickNeeded && rowExtraClick && rowExtraClick(e, data, index, row);
  }, [index, data, row, rowExtraClick, extraClickNeeded]);
  const handleClick = useCallback(() => {
    !opened && handleSelectRow(row, index, rowKey);
    onClick && onClick(data, index, row);
  }, [handleSelectRow, row, index, rowKey, onClick, data]);
  !dragDisable && drag(drop(ref));
  const customNestedElement = useMemo(() => renderRowNestedChildren && renderRowNestedChildren(row, index), [renderRowNestedChildren, index, row]);
  const hasNestedElement = useMemo(() => !!nestedTable || !!customNestedElement, [nestedTable, customNestedElement]);
  const isHoverable = useMemo(() => typeof hasHover === 'boolean' ? hasHover : rowsHover, [hasHover, rowsHover]);
  useEffect(() => {
    CustomPreview && preview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [CustomPreview]);
  useEffect(() => {
    eventRef.current && getDragAcceptType("".concat(itemTypes.tr).concat(eventRef.current));
  }, [eventRef.current, getDragAcceptType]);
  if (render) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: classnames('ta-group', className),
      ref: ref,
      style: style
    }, render(row, index));
  }
  return /*#__PURE__*/React__default.createElement("div", {
    className: classnames('ta-group', className),
    ref: ref,
    style: style,
    "data-index": index
  }, /*#__PURE__*/React__default.createElement("div", {
    className: classnames('ta-row', {
      'even-coloring': rowOrderNumber % 2,
      'hover-able': isHoverable || !!onClick || !!nestedTable || !!actionBar,
      selected: rowSelected || opened,
      'border-bottom': !(rowCount === index + 1 && hasFooter),
      'cursor-grab': !dragDisable,
      disabled
    }),
    onClick: handleClick,
    onDoubleClick: rowToggleHandler
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "ta-gr-left-line"
  }), withLeftCorner && /*#__PURE__*/React__default.createElement("div", {
    className: classnames('ta-cell a-square', {
      'sticky sticky-left now-sticky': stickyLeftExist,
      'left-actions-holder': !stickyLeftExist
    })
  }, rowExtraClick ? extraClickNeeded ? /*#__PURE__*/React__default.createElement(Icon, {
    title: rowExtraClickIconTooltip,
    onClick: onExtraClick,
    type: "bc-icon-arrow-right",
    className: "tl-bc-icon-action"
  }) : /*#__PURE__*/React__default.createElement("div", {
    className: "ta-cell a-square"
  }) : null, hasNestedElements ? hasNestedElement ? /*#__PURE__*/React__default.createElement(Icon, {
    type: "bc-icon-arrow-down",
    disabled: isExpandDisabled,
    className: classnames('tl-bc-icon-action', {
      active: opened
    }),
    title: toggleTooltips[Number(opened)],
    onClick: rowToggleHandler
  }) : /*#__PURE__*/React__default.createElement("div", {
    className: "ta-cell a-square"
  }) : null), orders.map((_ref2, colIndex) => {
    let {
      dataKey,
      id
    } = _ref2;
    const {
      hide,
      getter,
      copyable,
      formatter,
      colRenderer,
      copyableValue,
      copyTooltipText,
      copiedTooltipText
    } = columns.find(column => column.uid === id) || {};
    const {
      autoSizeOn,
      customWidth,
      autoSizeWidth,
      defaultCustomWidth
    } = colsInfo[id];
    if (hide) return null;
    return /*#__PURE__*/React__default.createElement(Col$1, {
      id: id,
      key: id,
      row: row,
      prefix: prefix,
      index: index,
      getter: getter,
      copyable: copyable,
      colIndex: colIndex,
      text: data[dataKey],
      formatter: formatter,
      autoSizeOn: autoSizeOn,
      colRenderer: colRenderer,
      customWidth: customWidth,
      isEditActive: isEditActive,
      copyableValue: copyableValue,
      autoSizeWidth: autoSizeWidth,
      stickyColumns: stickyColumns,
      disabledColumnPin: disabledColumnPin,
      initialColWidth: initialColWidth,
      copyTooltipText: copyTooltipText,
      copiedTooltipText: copiedTooltipText,
      defaultCustomWidth: defaultCustomWidth
    });
  }), actionBar && /*#__PURE__*/React__default.createElement(ActionBar$1, {
    lastColRef: lastColRef,
    stickyRightExist: stickyRightExist
  }, actionBar(row, index, isEditActive))), opened && /*#__PURE__*/React__default.createElement("div", {
    className: "ta-nested-child-holder"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "ta-nested-child",
    ref: nestedRef
  }, nestedTable ? /*#__PURE__*/React__default.createElement(Nested, _extends({}, nestedTable, {
    isEditActive: isEditActive
  })) : customNestedElement && customNestedElement(row, index))));
}
var Row$1 = /*#__PURE__*/memo(Row, reactFastCompare);

const resizeHandler = (element, setWidth, defaultWidth, setResizeMode, extraHandler, hasStickyElements, setStickyColumns, stickyContainer, eventRef, firstColRef, lastColRef, returnSizeHandler) => {
  setResizeMode(true);
  const {
    classList
  } = document.body;
  const {
    addEventListener,
    removeEventListener
  } = window;
  const {
    right,
    width,
    left
  } = element.getBoundingClientRect();
  const resizeStart = e => {
    stopEvent(e);
    const {
      pageX
    } = e;
    !classList.contains('vertical-resizing-cursor') && classList.add('vertical-resizing-cursor');
    ReactDOM__default.unstable_batchedUpdates(() => {
      let newWidth = width + pageX - right;
      if (pageX > right || left + width < pageX) {
        setWidth(newWidth);
        extraHandler(newWidth);
      } else if (pageX < left + width) {
        if (newWidth < defaultWidth) {
          newWidth = defaultWidth;
        }
        setWidth(newWidth);
        extraHandler(newWidth);
      }
    });
  };
  const resizeEnd = e => {
    stopEvent(e);

    // timout is needed to not allow `sortClick` function work, as
    // there is no workarround for preventing `click` event firing.

    callAfterDelay(() => {
      if (hasStickyElements) {
        setStickyColumns(prev => {
          const prevValues = Object.values(prev);
          const {
            current: firstCol
          } = firstColRef;
          const {
            current: lastCol
          } = lastColRef;
          const {
            clientWidth: clientWidthLeft
          } = firstCol || {};
          const {
            clientWidth: clientWidthRight
          } = lastCol || {};
          prevValues.forEach(item => item.subscriptions.map(unsubscribe => unsubscribe()));
          const result = Object.keys(prev).map(Number).reduce((acc, itemIndex) => {
            const pinnedItem = getOffsetValuesAndSubscriptions(acc, itemIndex, prev, clientWidthLeft, clientWidthRight, observeStickyInfo, stickyContainer.current, eventRef.current);
            return {
              ...acc,
              ...pinnedItem
            };
          }, {});
          return result;
        });
      }
      setResizeMode(false);
    }, 0);
    const {
      pageX
    } = e;
    ReactDOM__default.unstable_batchedUpdates(() => {
      let newWidth = width + pageX - right;
      if (pageX > right || left + width < pageX || pageX < left + width) {
        if (pageX < left + width && newWidth < defaultWidth) {
          newWidth = defaultWidth;
        }
        returnSizeHandler(resizeConfigs.typeEnum.resize, newWidth);
      }
    });
    removeEventListener('mousemove', resizeStart);
    removeEventListener('mouseup', resizeEnd);
    classList.remove('vertical-resizing-cursor');
  };
  addEventListener('mousemove', resizeStart);
  addEventListener('mouseup', resizeEnd);
};

function HeaderItem(_ref) {
  let {
    id,
    index,
    thStyle,
    centered,
    scrollBar,
    sortOrder,
    prefix,
    moveItem,
    className,
    isEditActive,
    sortActiveId,
    stickyColumns,
    updateColsInfo,
    handleStickyClick,
    changeSortColumn,
    onMenuActionClick,
    popoverOpenedId,
    onToggleColumnAutoSize,
    setPopoverOpenedId,
    setStickyColumns,
    initialColWidth,
    tableRef,
    eventRef,
    lastColRef,
    firstColRef,
    sortableColumns,
    resizableColumns,
    draggableColumns,
    sortedRowsLength,
    disabledColumnPin,
    columnSizeChangeHandler,
    searchHandler,
    lastIndexOf,
    colsInfo: {
      [id]: {
        autoSizeOn: currColAutoSizeOn,
        customWidth,
        autoSizeWidth,
        defaultCustomWidth
      }
    },
    ...column
  } = _ref;
  const {
    text,
    render,
    dataKey,
    draggable,
    width: propWidth = null,
    sortable,
    resizable,
    hasOptions,
    searchInColumns
  } = column;
  const {
    [index]: colStickyInfo = {}
  } = stickyColumns;
  const {
    offset = {},
    isStickyLeft,
    isStickyRight
  } = colStickyInfo;
  const stickyStatus = stickyColumns.hasOwnProperty(String(index));
  const isDraggable = typeof draggable === 'boolean' ? draggable : draggableColumns;
  const isResizable = typeof resizable === 'boolean' ? resizable : resizableColumns;
  const leftStickyRef = useRef(null);
  const rightStickyRef = useRef(null);
  const mainRef = useRef(null);
  const rightResizeRef = useRef(null);
  const isSortActive = id === sortActiveId;
  const [resizeMode, setResizeMode] = useState(false);
  const [{
    isOver
  }, drop] = useDrop({
    accept: "".concat(itemTypes.th).concat(eventRef.current),
    drop(item) {
      const dragIndex = item.index;
      const hoverIndex = index;
      moveItem(dragIndex, hoverIndex);
    },
    canDrop: () => !stickyStatus,
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  });
  const [_, drag] = useDrag({
    item: {
      index
    },
    type: "".concat(itemTypes.th).concat(eventRef.current),
    canDrag: () => !resizeMode && isDraggable && !stickyStatus
  });
  const [width, setWidth] = useState(propWidth);
  useMount(() => {
    updateColsInfo(id, false, width);
  });
  useEffect(() => {
    updateColsInfo(id, false, defaultCustomWidth || initialColWidth);
  }, [initialColWidth]);
  drag(drop(mainRef));
  const handleColInfoUpdate = useCallback(width => updateColsInfo(id, false, width), [id]);
  const onSortClick = useCallback(() => column.sortable !== false && !resizeMode && changeSortColumn({
    id,
    index,
    column,
    dataKey
  }), [resizeMode, changeSortColumn, id, index, column, dataKey]);
  const onStickyClick = useCallback(() => {
    setPopoverOpenedId(null);
    handleStickyClick(index, leftStickyRef.current, rightStickyRef.current, mainRef.current);
  }, [handleStickyClick, index, leftStickyRef.current, rightStickyRef.current, mainRef.current]);
  const isPopoverOpened = id === popoverOpenedId;
  const handleToggle = useCallback(() => setPopoverOpenedId(prevId => prevId === id ? null : id), [id]);
  const inlineStyle = {
    width: currColAutoSizeOn ? autoSizeWidth : customWidth || defaultCustomWidth || width || initialColWidth || 0
  };
  const {
    left = 0,
    right = 0
  } = offset;
  const style = {
    left,
    right,
    ...inlineStyle,
    ...thStyle
  };
  const toggleAutoSizing = () => {
    setPopoverOpenedId(null);
    callAfterDelay(() => {
      onToggleColumnAutoSize(id, !currColAutoSizeOn, width);
      returnSizeHandler(resizeConfigs.typeEnum.autoSize);
    }, 180);
  };
  const anySortActive = isSortActive && !!sortOrder;
  const isSortable = typeof sortable === 'boolean' ? sortable : sortableColumns;
  const returnSizeHandler = useCallback(function (type) {
    let width = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return columnSizeChangeHandler && columnSizeChangeHandler({
      type,
      width,
      key: dataKey
    });
  }, [columnSizeChangeHandler, dataKey]);
  const onMouseDownHandler = useCallback(e => {
    stopEvent(e);
    resizeHandler(mainRef.current, setWidth, propWidth || initialColWidth, setResizeMode, handleColInfoUpdate, Object.keys(stickyColumns).length, setStickyColumns, tableRef, eventRef, firstColRef, lastColRef, returnSizeHandler);
  }, [mainRef.current, propWidth, initialColWidth, stickyColumns, tableRef.current, eventRef, firstColRef, lastColRef, returnSizeHandler]);
  const dataChangeHandler = useCallback(_ref2 => {
    let {
      action,
      type
    } = _ref2;
    const filter = {
      value: null,
      dataKey,
      type
    };
    if (type === searchConfigs.typeEnum.select) {
      filter.value = action.map(i => i.value);
    } else if (type === searchConfigs.typeEnum.text) {
      filter.value = action;
    }
    searchHandler(filter);
  }, [dataKey, searchHandler]);
  const debounceHandler = useCallback(_ref3 => {
    let {
      action
    } = _ref3;
    return debounce(dataChangeHandler({
      action: action.target.value,
      type: searchInColumns.type
    }), 500);
  }, [dataChangeHandler, debounce]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, stickyStatus && isStickyLeft && /*#__PURE__*/React__default.createElement("sub", null), stickyStatus && isStickyRight && /*#__PURE__*/React__default.createElement("sup", null), /*#__PURE__*/React__default.createElement("div", {
    ref: mainRef,
    className: classnames('ta-cell ta-header-cell', className, {
      ...(!disabledColumnPin ? {
        'now-sticky': isStickyLeft || isStickyRight,
        sticky: stickyStatus,
        'sticky-left': isStickyLeft,
        'sticky-right': isStickyRight
      } : {}),
      'is-dragged-over': isOver,
      'cursor-pointer': isSortable,
      'cursor-grab': isDraggable,
      centered
    }),
    "data-id": "".concat(prefix, "-").concat(id),
    style: style,
    onClick: onSortClick,
    title: render ? '' : text
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "ta-cell-block"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "ta-cell-header-tittle ta-cell-header-item"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "sticky-viewport-checker",
    ref: leftStickyRef,
    style: {
      width: 1,
      left: -1
    }
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "table-heading"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: classnames('table-heading-text', 'ellipsis-text', {
      active: anySortActive
    })
  }, render ? render(column, index, isEditActive) : text), isSortable && /*#__PURE__*/React__default.createElement("div", {
    className: classnames('ta-filter-icon', {
      ["active-".concat(sortOrder)]: anySortActive
    })
  })), hasOptions && /*#__PURE__*/React__default.createElement("div", {
    onClick: stopEvent
  }, /*#__PURE__*/React__default.createElement(Popover, {
    align: "end",
    padding: 0,
    disableReposition: false,
    isOpen: isPopoverOpened,
    toggleHandler: handleToggle,
    extendTargetWidth: false,
    Content: /*#__PURE__*/React__default.createElement(Menu, {
      data: [{
        title: "".concat(stickyStatus ? 'Unpin' : 'Pin', " column"),
        slug: 'pin',
        onClick: onStickyClick,
        disabled: !sortedRowsLength || disabledColumnPin
      }, {
        title: currColAutoSizeOn ? 'Unautosize' : 'Autosize',
        slug: 'autosize',
        onClick: toggleAutoSizing,
        disabled: !sortedRowsLength
      }],
      onSelect: handleToggle
    })
  }, /*#__PURE__*/React__default.createElement(Icon, {
    type: "bc-icon-drag",
    className: "cell-options"
  })))), searchInColumns && /*#__PURE__*/React__default.createElement("div", {
    className: "ta-cell-search-block ta-cell-header-item"
  }, searchInColumns.type === searchConfigs.typeEnum.text && /*#__PURE__*/React__default.createElement(ExtendedInput, {
    onChange: action => debounceHandler({
      action
    }),
    onClick: stopEvent,
    placeholder: text
  }), searchInColumns.type === searchConfigs.typeEnum.select && /*#__PURE__*/React__default.createElement(Dropdown, {
    className: "search-dropdown",
    onChange: action => dataChangeHandler({
      action,
      type: searchInColumns.type
    }),
    data: searchInColumns.data || [],
    isMultiSelect: true
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "th-divider-holder",
    onClick: stopEvent
  }, isResizable && /*#__PURE__*/React__default.createElement("i", {
    className: "table-resize-handle",
    ref: rightResizeRef,
    onMouseDown: onMouseDownHandler
  }), !!lastIndexOf && /*#__PURE__*/React__default.createElement("i", {
    className: "th-divider"
  })), /*#__PURE__*/React__default.createElement("div", {
    className: "sticky-viewport-checker",
    ref: rightStickyRef,
    style: {
      width: 1,
      right: -1
    }
  })), isDraggable && /*#__PURE__*/React__default.createElement("div", {
    className: classnames('drop-placeholder', {
      'drop-placeholder-scale': isOver
    })
  }));
}
HeaderItem.defaultProps = {
  sortableColumns: false,
  resizableColumns: false,
  draggableColumns: false,
  hasOptions: true
};
var HeaderItem$1 = /*#__PURE__*/memo(HeaderItem, reactFastCompare);

var css_248z$2 = "[data-gene-ui-version=\"2.15.1\"] .ta-header{top:0}[data-gene-ui-version=\"2.15.1\"] .ta-header .ta-cell{align-items:normal;flex-direction:column}[data-gene-ui-version=\"2.15.1\"] .ta-header .ta-cell.centered .table-heading{justify-content:center}@media (hover:hover){[data-gene-ui-version=\"2.15.1\"] .ta-header .ta-cell:hover{background:rgba(var(--background-sc-rgb),.02)}[data-gene-ui-version=\"2.15.1\"] .ta-header .ta-cell.sticky:hover{background:rgba(var(--background-sc-rgb),.06)}}[data-gene-ui-version=\"2.15.1\"] .ta-header .ta-cell.is-dragged-over{background-color:rgba(var(--hero-rgb),.05)}[data-gene-ui-version=\"2.15.1\"] .ta-header .ta-cell .ta-cell-header-item{padding:5px 0}[data-gene-ui-version=\"2.15.1\"] .ta-header .ta-cell .ta-cell-header-tittle{display:flex}[data-gene-ui-version=\"2.15.1\"] .sticky-viewport-checker{bottom:0;position:absolute;top:0;visibility:hidden;width:1px}[data-gene-ui-version=\"2.15.1\"] .table-heading{align-items:center;display:flex;flex-shrink:0;flex:auto;overflow:hidden;width:100%}[data-gene-ui-version=\"2.15.1\"] .ta-filter-icon{color:rgba(var(--background-sc-rgb),.38);cursor:pointer;flex-shrink:0;margin:0 .4rem;position:relative}[data-gene-ui-version=\"2.15.1\"] .ta-filter-icon:after,[data-gene-ui-version=\"2.15.1\"] .ta-filter-icon:before{display:block;font:2.4rem/1 betcore;transition:color .3s}[data-gene-ui-version=\"2.15.1\"] .ta-filter-icon:before{content:\"\\e92c\"}[data-gene-ui-version=\"2.15.1\"] .ta-filter-icon:after{content:\"\\e92b\";left:0;position:absolute;top:0}[data-gene-ui-version=\"2.15.1\"] .ta-filter-icon.active-asc:before,[data-gene-ui-version=\"2.15.1\"] .ta-filter-icon.active-desc:after{color:var(--hero)}[data-gene-ui-version=\"2.15.1\"] .table-heading-text{transition:color .3s}[data-gene-ui-version=\"2.15.1\"] .table-heading-text.active{color:var(--hero)}[data-gene-ui-version=\"2.15.1\"] .cell-options{transition:opacity .4s}[data-gene-ui-version=\"2.15.1\"] .ta-cell:not(:hover) .cell-options:not(.popover-opened),[data-gene-ui-version=\"2.15.1\"] .vertical-resizing-cursor .cell-options{opacity:0}[data-gene-ui-version=\"2.15.1\"] .th-divider-holder{bottom:0;display:flex;padding:.9rem;position:absolute;top:0;transform:translateZ(0);transform:translateX(-50%);z-index:10}html:not([dir=rtl]) .th-divider-holder{left:100%}html[dir=rtl] .th-divider-holder{right:100%;transform:translateX(50%)}[data-gene-ui-version=\"2.15.1\"] .th-divider-holder i{transition:opacity .3s}[data-gene-ui-version=\"2.15.1\"] .th-divider{background:rgba(var(--background-sc-rgb),.15);width:1px}[data-gene-ui-version=\"2.15.1\"] .sticky .th-divider,[data-gene-ui-version=\"2.15.1\"] .ta-cell:hover .th-divider:nth-child(2),[data-gene-ui-version=\"2.15.1\"] .table-resize-handle:active~.th-divider{opacity:0}[data-gene-ui-version=\"2.15.1\"] .table-resize-handle{bottom:0;cursor:col-resize;display:flex;left:0;padding:.4rem .9rem;position:absolute;top:0}[data-gene-ui-version=\"2.15.1\"] .ta-cell:not(:hover) .table-resize-handle:not(:active){opacity:0}[data-gene-ui-version=\"2.15.1\"] .table-resize-handle:before{border-left:1px dashed rgba(var(--background-sc-rgb),.18);content:\"\";display:block;width:1px}[data-gene-ui-version=\"2.15.1\"] .table-resize-handle:active:before{border-color:var(--hero)}";
styleInject(css_248z$2);

const Header = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    orders,
    groups,
    columns,
    disabled,
    hasDoubleHeader,
    changeOrders,
    withLeftCorners,
    withRightCorner,
    stickyLeftExist,
    colsInfo,
    ...rest
  } = _ref;
  const moveItem = useCallback((dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) return;
    if (hoverIndex === 0 && columns[0].draggable === false) return;
    changeOrders(arrayReorder(orders, dragIndex, hoverIndex));
  }, [orders, changeOrders]);
  const findColumn = id => columns.find(column => column.uid === id);
  const groupSteps = useMemo(() => groups && groups.length > 0 && groups.reduce((acc, _ref2, index) => {
    let {
      innerColCount
    } = _ref2;
    return [...acc, index ? acc[index - 1] + innerColCount : innerColCount];
  }, []), [groups]);
  const innerColWidth = useCallback(index => {
    const width = [...columns].slice(index ? groupSteps[index - 1] : 0, groupSteps[index]).reduce((prev, _ref3) => {
      let {
        uid
      } = _ref3;
      if (!colsInfo[uid]) return prev;
      const {
        customWidth,
        autoSizeWidth,
        defaultCustomWidth = 150
      } = colsInfo[uid];
      return prev + (autoSizeWidth || customWidth || defaultCustomWidth);
    }, 0);
    return {
      [index]: {
        customWidth: width,
        autoSizeWidth: 0,
        defaultCustomWidth: 0,
        autoSizeOn: false
      }
    };
  }, [colsInfo, columns, groupSteps]);
  const templateRenderer = content => /*#__PURE__*/React__default.createElement("div", {
    className: "ta-row border-bottom"
  }, withLeftCorners.map((item, index) => item && /*#__PURE__*/React__default.createElement("div", _extends({
    key: index,
    className: classnames('ta-cell a-square', {
      'sticky sticky-left now-sticky': stickyLeftExist
    })
  }, index === 0 && {
    ref
  }))), content, withRightCorner && /*#__PURE__*/React__default.createElement(ActionBar$1, null));
  return /*#__PURE__*/React__default.createElement("div", {
    className: classnames('ta-n-body ta-header', {
      'pointer-events-none': disabled
    })
  }, hasDoubleHeader && groups && groups.length > 0 && templateRenderer(groups.map((_ref4, index) => {
    let {
      text
    } = _ref4;
    return /*#__PURE__*/React__default.createElement(HeaderItem$1, _extends({
      key: index,
      id: index,
      text: text,
      index: index,
      centered: true,
      sortable: false,
      draggable: false,
      resizable: false,
      hasOptions: false,
      sortableColumns: false,
      resizableColumns: false,
      draggableColumns: false
    }, rest, {
      stickyColumns: [],
      colsInfo: innerColWidth(index),
      lastIndexOf: groups.length - (index + 1)
    }));
  })), templateRenderer(orders.map((_ref5, index) => {
    var _findColumn;
    let {
      id
    } = _ref5;
    const hide = (_findColumn = findColumn(id)) === null || _findColumn === void 0 ? void 0 : _findColumn.hide;
    if (hide) return null;
    return /*#__PURE__*/React__default.createElement(HeaderItem$1, _extends({
      key: id,
      id: id,
      index: index
    }, findColumn(id), {
      moveItem: moveItem,
      colsInfo: colsInfo,
      lastIndexOf: orders.length - (index + 1)
    }, rest));
  })));
});
Header.defaultProps = {
  columns: [],
  withLeftCorners: []
};
var Header$1 = /*#__PURE__*/memo(Header, reactFastCompare);

function FooterItem(_ref) {
  let {
    id,
    text,
    index,
    tdStyle,
    initialColWidth,
    colsInfo: {
      [id]: {
        autoSizeOn,
        customWidth,
        autoSizeWidth,
        defaultCustomWidth
      }
    },
    stickyColumns
  } = _ref;
  const {
    [index]: colStickyInfo = {}
  } = stickyColumns;
  const {
    offset = {},
    isStickyRight,
    isStickyLeft
  } = colStickyInfo;
  const stickyStatus = (String(index) in stickyColumns);
  const {
    left = 0,
    right = 0
  } = offset;
  const style = {
    left,
    right
  };
  const inlineStyle = {
    width: autoSizeOn ? autoSizeWidth : customWidth || defaultCustomWidth || initialColWidth
  };
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, stickyStatus && isStickyLeft && /*#__PURE__*/React__default.createElement("sub", null), stickyStatus && isStickyRight && /*#__PURE__*/React__default.createElement("sup", null), /*#__PURE__*/React__default.createElement("div", {
    key: id,
    className: classnames('ta-cell ta-footer', {
      sticky: stickyStatus,
      'sticky-left': isStickyLeft,
      'sticky-right': isStickyRight,
      'now-sticky': isStickyRight || isStickyLeft
    }),
    style: {
      ...style,
      ...tdStyle,
      ...inlineStyle
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "ellipsis-text",
    title: text || ''
  }, text)));
}
var FooterItem$1 = /*#__PURE__*/memo(FooterItem, reactFastCompare);

var css_248z$1 = "[data-gene-ui-version=\"2.15.1\"] .ta-footer .ellipsis-text{-webkit-user-select:all;user-select:all}";
styleInject(css_248z$1);

function Footer(_ref) {
  let {
    values,
    orders,
    withRightCorner,
    withLeftCorners,
    stickyLeftExist,
    ...rest
  } = _ref;
  return /*#__PURE__*/React__default.createElement("div", {
    className: "ta-row border-top"
  }, withLeftCorners.map((item, index) => item && /*#__PURE__*/React__default.createElement("div", {
    key: index,
    className: classnames('ta-cell a-square', {
      'sticky sticky-left now-sticky': stickyLeftExist
    })
  })), orders.map((_ref2, index) => {
    let {
      id
    } = _ref2;
    return /*#__PURE__*/React__default.createElement(FooterItem$1, _extends({
      id: id,
      key: index,
      index: index,
      text: values[id]
    }, rest));
  }), withRightCorner && /*#__PURE__*/React__default.createElement(ActionBar$1, null));
}
Footer.defaultProps = {
  values: {},
  withLeftCorners: []
};

var css_248z = "[data-gene-ui-version=\"2.15.1\"] .table-loader-container{position:relative;width:100%}[data-gene-ui-version=\"2.15.1\"] .table-holder{display:flex;flex-direction:column;font:600 1.4rem/1.8rem var(--font-family);position:relative;width:100%}[data-gene-ui-version=\"2.15.1\"] .table-loader-container .table-holder{position:static}[data-gene-ui-version=\"2.15.1\"] .table-holder .ReactVirtualized__Grid{direction:inherit!important;min-width:100%;overflow:visible!important;width:auto!important}[data-gene-ui-version=\"2.15.1\"] .table-holder .ReactVirtualized__Grid .ReactVirtualized__Grid__innerScrollContainer{max-width:inherit!important;min-width:100%;overflow:visible!important;width:auto!important}[data-gene-ui-version=\"2.15.1\"] .table-holder:not(.content-empty){max-height:calc(100vh - var(--header-height, 0px) - var(--table-max-height-additional-calc, 0px))}[data-gene-ui-version=\"2.15.1\"] .ta-body{display:inline-block;min-width:100%;position:relative;z-index:0}[data-gene-ui-version=\"2.15.1\"] .content-empty .ta-body{position:-webkit-sticky;position:sticky}html:not([dir=rtl]) .content-empty .ta-body{left:0}html[dir=rtl] .content-empty .ta-body{right:0}[data-gene-ui-version=\"2.15.1\"] .ta-n-body{background:var(--background);display:inline-block;font-weight:700;min-width:100%;position:-webkit-sticky;position:sticky;-webkit-user-select:none;user-select:none;z-index:10}[data-gene-ui-version=\"2.15.1\"] .ta-n-body .ta-row{background:rgba(var(--background-sc-rgb),.04);display:inline-flex;min-width:100%}[data-gene-ui-version=\"2.15.1\"] .ta-n-body .ta-row.disabled{opacity:.5;pointer-events:none}[data-gene-ui-version=\"2.15.1\"] .ta-group{display:inline-flex;flex-direction:column;min-width:100%;position:relative;width:auto!important}html:not([dir=rtl]) .ta-group{right:auto!important}html[dir=rtl] .ta-group{left:auto!important}html:not([dir=rtl]) .ta-group{left:0!important}html[dir=rtl] .ta-group{right:0!important}[data-gene-ui-version=\"2.15.1\"] .ta-gr-left-line{bottom:0;display:flex;pointer-events:none;position:absolute;top:-1px;width:100%;z-index:12}html:not([dir=rtl]) .ta-gr-left-line{left:0}html[dir=rtl] .ta-gr-left-line{right:0}[data-gene-ui-version=\"2.15.1\"] .ta-gr-left-line:before{background:var(--hero);content:\"\";display:block;height:100%;position:-webkit-sticky;position:sticky;transform:translateX(-110%);transition:transform .3s;width:.3rem}html:not([dir=rtl]) .ta-gr-left-line:before{left:0}html[dir=rtl] .ta-gr-left-line:before{right:0;transform:translateX(110%)}[data-gene-ui-version=\"2.15.1\"] .hover-able:hover .ta-gr-left-line:before,[data-gene-ui-version=\"2.15.1\"] .selected .ta-gr-left-line:before{transform:translateX(0)}[data-gene-ui-version=\"2.15.1\"] .ta-row{align-items:center;background-clip:content-box;display:flex;justify-content:flex-start;min-width:100%}[data-gene-ui-version=\"2.15.1\"] .ta-row.border-bottom{border-bottom:1px solid rgba(var(--background-sc-rgb),.1)}[data-gene-ui-version=\"2.15.1\"] .ta-row.border-top{border-top:1px solid rgba(var(--background-sc-rgb),.1)}[data-gene-ui-version=\"2.15.1\"] .ta-row.even-coloring{background-color:rgba(var(--background-sc-rgb),.02)}[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:hover,[data-gene-ui-version=\"2.15.1\"] .ta-row.selected{background-color:rgba(var(--hero-rgb),.08)}[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:hover .tl-bc-icon-action,[data-gene-ui-version=\"2.15.1\"] .ta-row.selected .tl-bc-icon-action{color:var(--hero)}[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:not(.cursor-grab){cursor:pointer}[data-gene-ui-version=\"2.15.1\"] .ta-row.disabled{opacity:.5;pointer-events:none}[data-gene-ui-version=\"2.15.1\"] .ta-cell{align-items:center;display:flex;flex-shrink:0;min-height:4.2rem;min-width:4.2rem;position:relative;will-change:width,background-color,position}[data-gene-ui-version=\"2.15.1\"] .ta-cell>.ellipsis-text{width:100%}[data-gene-ui-version=\"2.15.1\"] .ta-n-body .ta-cell:last-child,[data-gene-ui-version=\"2.15.1\"] .ta-n-body .ta-cell:nth-last-child(2){overflow:hidden}[data-gene-ui-version=\"2.15.1\"] .ta-cell a{color:var(--hero);font-weight:600}[data-gene-ui-version=\"2.15.1\"] .ta-cell:not(.a-square){padding:0 1.2rem 0 2rem}html[dir=rtl] .ta-cell:not(.a-square){padding:0 2rem 0 1.2rem}[data-gene-ui-version=\"2.15.1\"] .ta-cell.a-square{justify-content:center}[data-gene-ui-version=\"2.15.1\"] .ta-cell.a-square:empty{pointer-events:none}[data-gene-ui-version=\"2.15.1\"] .ta-cell.a-square:not(:empty){position:-webkit-sticky;position:sticky;transform:translateZ(0);z-index:11}[data-gene-ui-version=\"2.15.1\"] .ta-cell.sticky{background-color:inherit;box-shadow:1px 0 0 rgba(var(--background-sc-rgb),.1),-1px 0 0 rgba(var(--background-sc-rgb),.1),inset 0 0 0 10rem var(--background);position:-webkit-sticky;position:sticky;transition:none;z-index:11}[data-gene-ui-version=\"2.15.1\"] .ta-cell.sticky:before{background:inherit;content:\"\";height:100%;left:0;position:absolute;top:0;width:100%;z-index:-1}[data-gene-ui-version=\"2.15.1\"] .ta-cell.sticky:after{bottom:-1px;content:\"\";display:block;opacity:0;pointer-events:none;position:absolute;top:0;transition:opacity .3s,visibility .3s;visibility:hidden;width:2rem}[data-gene-ui-version=\"2.15.1\"] sub:last-of-type+.ta-cell.sticky:after,[data-gene-ui-version=\"2.15.1\"] sup:first-of-type+.ta-cell.sticky:after{opacity:1;visibility:visible}[data-gene-ui-version=\"2.15.1\"] .ta-cell.sticky.now-sticky{box-shadow:inset 0 0 0 10rem var(--background)}html:not([dir=rtl]) .ta-cell.sticky.sticky-left{left:0}html[dir=rtl] .ta-cell.sticky.sticky-left{right:0}[data-gene-ui-version=\"2.15.1\"] .ta-cell.sticky.sticky-left:after{background:linear-gradient(to right,#0000000d,rgba(var(--background-rgb),0))}html:not([dir=rtl]) .ta-cell.sticky.sticky-left:after{left:100%}html[dir=rtl] .ta-cell.sticky.sticky-left:after{background:linear-gradient(to left,#0000000d,rgba(var(--background-rgb),0));right:100%}[data-gene-ui-version=\"2.15.1\"] sub:last-of-type+.ta-cell.sticky.sticky-left{box-shadow:1px 0 0 rgba(var(--background-sc-rgb),.1),inset 0 0 0 10rem var(--background)}html:not([dir=rtl]) .ta-cell.sticky.sticky-right{right:0}html[dir=rtl] .ta-cell.sticky.sticky-right{left:0}[data-gene-ui-version=\"2.15.1\"] .ta-cell.sticky.sticky-right:after{background:linear-gradient(to left,#0000000d,rgba(var(--background-rgb),0))}html:not([dir=rtl]) .ta-cell.sticky.sticky-right:after{right:100%}html[dir=rtl] .ta-cell.sticky.sticky-right:after{background:linear-gradient(to right,#0000000d,rgba(var(--background-rgb),0));left:100%}[data-gene-ui-version=\"2.15.1\"] sup:first-of-type+.ta-cell.sticky.sticky-right{box-shadow:-1px 0 0 rgba(var(--background-sc-rgb),.1),inset 0 0 0 10rem var(--background)}[data-gene-ui-version=\"2.15.1\"] .ta-cell:hover .copy-icon{opacity:1}[data-gene-ui-version=\"2.15.1\"] .ta-cell .copy-icon{font-size:14px;margin-right:6px;opacity:0}[data-gene-ui-version=\"2.15.1\"] .ta-cell .copy-icon:focus{color:var(--hero)}[data-gene-ui-version=\"2.15.1\"] .ta-cell .crs-holder{display:flex}[data-gene-ui-version=\"2.15.1\"] .ta-nested-child-holder{cursor:auto;max-width:var(--table-max-width);overflow:hidden;position:-webkit-sticky;position:sticky;-webkit-user-select:text;user-select:text;width:100%}html:not([dir=rtl]) .ta-nested-child-holder{left:0}html[dir=rtl] .ta-nested-child-holder{right:0}[data-gene-ui-version=\"2.15.1\"] .ta-nested-child-holder:not(.table-empty-holder){border-bottom:1px solid rgba(var(--background-sc-rgb),.1)}[data-gene-ui-version=\"2.15.1\"] .ta-nested-child{padding:1.2rem 1.4rem;width:100%}[data-gene-ui-version=\"2.15.1\"] .tl-bc-icon-action{cursor:pointer;position:relative;text-align:center;transition:color .3s,transform .3s;width:100%;z-index:1}[data-gene-ui-version=\"2.15.1\"] .tl-bc-icon-action.active{transform:rotate(180deg)}[data-gene-ui-version=\"2.15.1\"] .table-splash-loader{background:rgba(var(--background-rgb),.6);border-radius:2rem;height:100%;left:0;position:absolute;top:0;width:100%;z-index:11}[data-gene-ui-version=\"2.15.1\"] .table-empty-holder{align-items:center;display:flex;justify-content:center;min-height:42rem;padding:2rem;-webkit-user-select:none;user-select:none}[data-gene-ui-version=\"2.15.1\"] .right-actions-holder{width:5.2rem}html:not([dir=rtl]) .right-actions-holder{right:0}html[dir=rtl] .right-actions-holder{left:0}[data-gene-ui-version=\"2.15.1\"] .ta-n-body .right-actions-holder:not(.sticky){opacity:0;pointer-events:none}html:not([dir=rtl]) .left-actions-holder{left:0}html[dir=rtl] .left-actions-holder{right:0}[data-gene-ui-version=\"2.15.1\"] .left-actions-holder .icon{margin:0 .9rem}[data-gene-ui-version=\"2.15.1\"] .left-actions-holder:after,[data-gene-ui-version=\"2.15.1\"] .left-actions-holder:before{bottom:0;content:\"\";pointer-events:none;position:absolute;top:0}html:not([dir=rtl]) .left-actions-holder:after,html:not([dir=rtl]) .left-actions-holder:before{right:-2rem}html[dir=rtl] .left-actions-holder:after,html[dir=rtl] .left-actions-holder:before{left:-2rem}html:not([dir=rtl]) .left-actions-holder:after,html:not([dir=rtl]) .left-actions-holder:before{left:0}html[dir=rtl] .left-actions-holder:after,html[dir=rtl] .left-actions-holder:before{right:0}[data-gene-ui-version=\"2.15.1\"] .left-actions-holder:before{background:linear-gradient(to right,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}html[dir=rtl] .left-actions-holder:before{background:linear-gradient(to left,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}[data-gene-ui-version=\"2.15.1\"] .even-coloring .left-actions-holder:before{background:linear-gradient(to right,rgba(var(--background-sc-rgb),.02) 65%,rgba(var(--background-sc-rgb),0) 100%),linear-gradient(to right,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}html[dir=rtl] .even-coloring .left-actions-holder:before{background:linear-gradient(to left,rgba(var(--background-sc-rgb),.02) 65%,rgba(var(--background-sc-rgb),0) 100%),linear-gradient(to left,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}[data-gene-ui-version=\"2.15.1\"] .left-actions-holder:after{background:linear-gradient(to right,rgba(var(--hero-rgb),.08) 65%,rgba(var(--hero-rgb),0) 100%),linear-gradient(to right,var(--background) 65%,rgba(var(--background-rgb),0) 100%);opacity:0}html[dir=rtl] .left-actions-holder:after{background:linear-gradient(to left,rgba(var(--hero-rgb),.08) 65%,rgba(var(--hero-rgb),0) 100%),linear-gradient(to left,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:hover .left-actions-holder:before,[data-gene-ui-version=\"2.15.1\"] .ta-row.selected .left-actions-holder:before{opacity:0}[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:hover .left-actions-holder:after,[data-gene-ui-version=\"2.15.1\"] .ta-row.selected .left-actions-holder:after{opacity:1}[data-gene-ui-version=\"2.15.1\"] .ta-action{align-items:center;bottom:0;display:flex;padding:0 .6rem;position:absolute;top:0}html:not([dir=rtl]) .ta-action{right:0}html[dir=rtl] .ta-action{left:0}[data-gene-ui-version=\"2.15.1\"] .ta-action:after,[data-gene-ui-version=\"2.15.1\"] .ta-action:before{bottom:0;content:\"\";position:absolute;top:0}html:not([dir=rtl]) .ta-action:after,html:not([dir=rtl]) .ta-action:before{left:-3rem}html[dir=rtl] .ta-action:after,html[dir=rtl] .ta-action:before{right:-3rem}html:not([dir=rtl]) .ta-action:after,html:not([dir=rtl]) .ta-action:before{right:0}html[dir=rtl] .ta-action:after,html[dir=rtl] .ta-action:before{left:0}[data-gene-ui-version=\"2.15.1\"] .ta-action:before{background:linear-gradient(to left,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}html[dir=rtl] .ta-action:before{background:linear-gradient(to right,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}[data-gene-ui-version=\"2.15.1\"] .even-coloring .ta-action:before{background:linear-gradient(to left,rgba(var(--background-sc-rgb),.02) 65%,rgba(var(--background-sc-rgb),0) 100%),linear-gradient(to left,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}html[dir=rtl] .even-coloring .ta-action:before{background:linear-gradient(to right,rgba(var(--background-sc-rgb),.02) 65%,rgba(var(--background-sc-rgb),0) 100%),linear-gradient(to right,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}[data-gene-ui-version=\"2.15.1\"] .ta-action:after{background:linear-gradient(to left,rgba(var(--hero-rgb),.08) 65%,rgba(var(--hero-rgb),0) 100%),linear-gradient(to left,var(--background) 65%,rgba(var(--background-rgb),0) 100%);opacity:0}html[dir=rtl] .ta-action:after{background:linear-gradient(to right,rgba(var(--hero-rgb),.08) 65%,rgba(var(--hero-rgb),0) 100%),linear-gradient(to right,var(--background) 65%,rgba(var(--background-rgb),0) 100%)}[data-gene-ui-version=\"2.15.1\"] .ta-action.ta-a-2,[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:hover .ta-action.ta-a-1:not(:only-child),[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:hover .ta-action:before,[data-gene-ui-version=\"2.15.1\"] .ta-row.selected .ta-action.ta-a-1:not(:only-child),[data-gene-ui-version=\"2.15.1\"] .ta-row.selected .ta-action:before{opacity:0}[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:hover .ta-action.ta-a-2,[data-gene-ui-version=\"2.15.1\"] .ta-row.hover-able:hover .ta-action:after,[data-gene-ui-version=\"2.15.1\"] .ta-row.selected .ta-action.ta-a-2,[data-gene-ui-version=\"2.15.1\"] .ta-row.selected .ta-action:after{opacity:1}[data-gene-ui-version=\"2.15.1\"] .now-sticky .ta-action:before{content:none}[data-gene-ui-version=\"2.15.1\"] .ta-item{align-items:center;display:flex;position:relative;z-index:1}[data-gene-ui-version=\"2.15.1\"] .edit-save-button-holder{align-items:center;display:flex}[data-gene-ui-version=\"2.15.1\"] .edit-save-button-holder:not(:only-child){margin-inline-end:1rem}[data-gene-ui-version=\"2.15.1\"] .edit-save-button-holder>*+*{margin-inline-start:1rem}";
styleInject(css_248z);

function Table(props) {
  const {
    page,
    rows,
    rowKey,
    loader,
    loading,
    sortType,
    hasDoubleHeader,
    groups,
    sortedColumn,
    getRowsAsync,
    currentPage,
    columnKey,
    rowsHover,
    pagination,
    onRowMove,
    onRowClick,
    selectedRow,
    onSortChange,
    footerValues,
    emptyContent,
    rowActionBar,
    isEditActive,
    rowExtraClick,
    columns: cols,
    selectableRows,
    toggleTooltips,
    rowExtraClickIconTooltip,
    closeExpandedAfterFilter,
    handleRowSelect,
    defaultColWidth,
    defaultSortType,
    defaultSortedColumn,
    infiniteLoader,
    toggledRows,
    sortableColumns,
    CustomPreview,
    resizableColumns,
    draggableColumns,
    rowToggleHandler,
    getDragAcceptType,
    handleColumnSorting,
    rowExtraClickNeeded,
    stickyColumnsLimitCount,
    renderRowNestedChildren,
    getExpandIconDisableState,
    columnSizeChangeHandler,
    searchHandler,
    hasSearch,
    customToggledRowsKeys
  } = props;
  if (!('columnKey' in props)) {
    ger.deprecate('"columnKey" prop is required for Table component. In the next major release the default value will be removed(now default to "dataKey").');
  }
  const rowSelectIsControlled = ('selectedRow' in props);
  const isSortControlled = ('handleColumnSorting' in props);
  const columns = useMemo(() => cols.map(item => ({
    ...item,
    uid: item[columnKey]
  })), [cols, columnKey]);
  const holderRef = useRef(null);
  const eventRef = useRef(Table.eventName + Table.eventCounter++);
  const firstColRef = useRef(null);
  const lastColRef = useRef(null);
  const scrollRef = useRef(null);
  const virtualizedList = useRef(null);
  const [toggledRowsKeys, setToggledRowsKeys] = useState([]);
  const [holderWidth, setHolderWidth] = useState(0);
  const [sortConfigs, setSortConfigs] = useState(() => ({
    id: defaultSortedColumn || null,
    type: defaultSortType || null,
    index: null
  }));
  const [popoverOpenedId, setPopoverOpenedId] = useState(null);
  const [toggledRefs, setToggledRefs] = useState({});
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [columnsOrder, setColumnsOrder] = useState(() => columns.map(_ref => {
    let {
      uid,
      dataKey,
      sizeParams
    } = _ref;
    return {
      id: uid,
      dataKey,
      sizeParams
    };
  }));

  // Unique prefix for every table, needed for autosize
  const prefix = useMemo(() => guid(), []);
  const [colsInfo, updateColsInfo, reInitColsInfo] = useGetColsInfo(() => columnsOrder.reduce((acc, _ref2) => {
    let {
      id,
      sizeParams
    } = _ref2;
    return {
      ...acc,
      [id]: {
        autoSizeOn: null,
        customWidth: null,
        autoSizeWidth: null,
        ...sizeParams
      }
    };
  }, {}), prefix);
  const [rowsOrder, setRowsOrder] = useState(() => rows.map((ignored, index) => index));
  const [stickyColumns, setStickyColumns] = useState({});
  const hasNestedElements = useMemo(() => !!renderRowNestedChildren || rows.some(row => !!row.children), [rows, renderRowNestedChildren]);
  const withLeftCorners = useMemo(() => [!!rowExtraClick && !!rowExtraClickNeeded, hasNestedElements], [rowExtraClickNeeded, rowExtraClick, hasNestedElements]);
  const withRightCorner = useMemo(() => !!rowActionBar, [rowActionBar]);
  const initialColWidth = useMemo(() => {
    if (holderWidth) {
      const colLength = columns.length;
      if (holderWidth / defaultColWidth > colLength) {
        const firsColWidth = firstColRef.current ? withLeftCorners.reduce((acc, curr) => {
          if (curr) return acc + firstColRef.current.clientWidth;
          return acc;
        }, 0) : 0;
        const lastColWidth = lastColRef.current ? lastColRef.current.clientWidth : 0;
        const colWidth = (holderWidth - firsColWidth - lastColWidth) / colLength;
        return colWidth;
      }
      return defaultColWidth;
    }
  }, [holderWidth, columns.length, defaultColWidth, withLeftCorners, firstColRef.current, lastColRef.current]);
  const handleStickyClick = useCallback((index, left, right, target, unpin) => {
    const {
      current: firstCol
    } = firstColRef;
    const {
      current: lastCol
    } = lastColRef;
    const {
      clientWidth: clientWidthLeft
    } = firstCol || {};
    const {
      clientWidth: clientWidthRight
    } = lastCol || {};
    setStickyColumns(prev => {
      const prevValues = Object.values(prev);
      const belowLimit = prevValues.length < stickyColumnsLimitCount;
      prevValues.forEach(item => item.subscriptions.map(unsubscribe => unsubscribe()));
      if (prev.hasOwnProperty(index) || unpin) {
        const {
          [index]: ignored,
          ...next
        } = prev;
        const result = Object.keys(next).map(Number).reduce((acc, itemIndex) => getOffsetValuesAndSubscriptions(acc, itemIndex, next, clientWidthLeft, clientWidthRight, observeStickyInfo, holderRef.current, eventRef.current), {});
        return result;
      }
      if (belowLimit) {
        const newPinned = getOffsetValuesAndSubscriptions(prev, index, {
          ...prev,
          [index]: {
            left,
            right,
            target
          }
        }, clientWidthLeft, clientWidthRight, observeStickyInfo, holderRef.current, eventRef.current);
        const result = Object.keys(newPinned).map(Number).reduce((acc, itemIndex) => {
          if (itemIndex !== index) {
            return getOffsetValuesAndSubscriptions(acc, itemIndex, newPinned, clientWidthLeft, clientWidthRight, observeStickyInfo, holderRef.current, eventRef.current);
          }
          return {
            ...acc,
            [itemIndex]: newPinned[itemIndex]
          };
        }, {});
        return result;
      }
      return prev;
    });
  }, [stickyColumnsLimitCount, holderRef.current, lastColRef.current, firstColRef.current]);
  const cache = useMemo(() => new CellMeasurerCache({
    defaultHeight: 43,
    fixedWidth: true
  }), [rows]);
  useEffect(() => {
    closeExpandedAfterFilter && (setToggledRefs({}) || setToggledRowsKeys([]));
    setRowsOrder(rows.map((ignored, index) => index));
    cache.clearAll();
  }, [rows.length]);
  useEffect(() => {
    toggledRows && setToggledRowsKeys(indexes => [...indexes, ...toggledRows]);
  }, [toggledRows]);
  useEffect(() => {
    scrollRef.current && scrollRef.current.scrollTo({
      top: 0
    });
  }, [page, currentPage, scrollRef.current]);
  useEffect(() => {
    customToggledRowsKeys && setToggledRowsKeys(customToggledRowsKeys);
  }, [customToggledRowsKeys]);
  useUpdate(() => {
    const newOrder = columns.map(_ref3 => {
      let {
        dataKey,
        uid
      } = _ref3;
      return {
        id: uid,
        dataKey
      };
    });
    setColumnsOrder(newOrder);
    reInitColsInfo(newOrder);
  }, [columns]);
  useMount(() => {
    const observed = observeElementResize(holderRef.current, debounce(_ref4 => {
      let [ResizeObserver] = _ref4;
      return setHolderWidth(ResizeObserver.contentRect.width);
    }, 0, true));
    const handler = e => {
      stopEvent(e);
      const {
        isSticky,
        position,
        additionalInfo: {
          index
        }
      } = e.detail;
      setStickyColumns(prev => {
        const {
          [index]: {
            isStickyRight: stickyFromRight,
            isStickyLeft: stickyFromLeft
          }
        } = prev;
        return {
          ...prev,
          [index]: {
            ...prev[index],
            isStickyLeft: position === 'left' ? isSticky : stickyFromLeft,
            isStickyRight: position === 'right' ? isSticky : stickyFromRight
          }
        };
      });
    };
    document.addEventListener(eventRef.current, handler);
    return () => {
      document.removeEventListener(eventRef.current, handler);
      observed.unobserve();
    };
  });
  const changeSortColumn = useCallback(nextSortObject => {
    sortHandler(nextSortObject, setSortConfigs, onSortChange, sortableColumns, sortConfigs);
  }, [onSortChange, sortableColumns, sortConfigs]);
  const changeSortColumnThrottled = useThrottle(isSortControlled ? handleColumnSorting : changeSortColumn, 50);
  const sortConfig = useSortConfigs(sortConfigs, columns, rows, columnKey);
  const [type, columnDataKey, sortFn] = isSortControlled ? [sortType, sortedColumn] : sortConfig;
  const isEqual = infiniteLoader || rowsOrder.length === rows.length;
  const sortedRows = useMemo(() => {
    const ordered = rowsOrder.length === rows.length ? rowsOrder : rows.map((ignored, index) => index);
    if (isSortControlled) return ordered;
    if (type !== null && isEqual) return [...ordered].sort(sortFn);
    return ordered;
  }, [isSortControlled, type, rows.length, rowsOrder, sortFn, isEqual]);
  const sortedRowsLength = sortedRows.length;
  const moveItem = useCallback((dragIndex, hoverIndex) => {
    const newRowsOrder = arrayReorder(rowsOrder, rowsOrder.indexOf(dragIndex), rowsOrder.indexOf(hoverIndex));
    onRowMove(dragIndex, hoverIndex);
    setRowsOrder(newRowsOrder);
  }, [rowsOrder, arrayReorder, onRowMove]);
  const hasFooter = !!Object.keys(footerValues).length;
  const {
    stickyRightExist,
    stickyLeftExist
  } = useMemo(() => hasStickyElements(stickyColumns), [stickyColumns]);
  const handleSelectRow = useCallback((row, index, rowKey) => {
    if (selectableRows) {
      rowSelectIsControlled ? handleRowSelect(row, index, rowKey) : setSelectedRowKey(prev => prev === rowKey ? null : rowKey);
    }
  }, [selectableRows, rowSelectIsControlled, handleRowSelect]);
  const expandedHeight = useMemo(() => Object.keys(toggledRefs).reduce((acc, key) => acc += toggledRefs[key].current ? toggledRefs[key].current.clientHeight : 0, 0), [toggledRefs]);
  const withLeftCorner = useMemo(() => withLeftCorners.some(some => some), [withLeftCorners]);
  const tableWidth = useMemo(() => holderWidth || 0, [holderWidth]);
  const disabledColumnPin = useMemo(() => scrollRef && scrollRef.current && scrollRef.current.scrollWidth && scrollRef.current.scrollWidth <= tableWidth, [scrollRef && scrollRef.current && scrollRef.current.scrollWidth, tableWidth]);
  const hasData = rows.length && sortedRowsLength && scrollRef.current && isEqual;

  // This effect needed for recalculated auto sized columns width
  // when user change page ( because next page values can be bigger then prev page)
  useEffect(() => {
    Object.entries(colsInfo).forEach(_ref5 => {
      let [id, data] = _ref5;
      return data.autoSizeOn && updateColsInfo(id, true, data.customWidth);
    });
  }, [currentPage, page, type, columnDataKey, rows]);
  useEffect(() => {
    rows && setRowsOrder(rows.map((ignored, index) => index));
  }, [rows]);

  // initialize row in the arguments.
  const onRowsRendered = _ref6 => {
    let {
      index,
      parent,
      style,
      nonExisting: row = rows[sortedRows[index]]
    } = _ref6;
    return /*#__PURE__*/React__default.createElement(CellMeasurer, {
      cache: cache,
      columnIndex: 0,
      key: row.data[rowKey],
      parent: parent,
      rowIndex: index
    }, /*#__PURE__*/React__default.createElement(Row$1, _extends({}, row, {
      style: {
        ...style
      },
      prefix: prefix,
      sortType: type,
      columns: columns,
      colsInfo: colsInfo,
      moveItem: moveItem,
      eventRef: eventRef,
      onClick: onRowClick,
      CustomPreview: CustomPreview,
      orders: columnsOrder,
      rowsHover: rowsHover,
      hasFooter: hasFooter,
      rowOrderNumber: index,
      rowCount: rows.length,
      rowHeightCache: cache,
      lastColRef: lastColRef,
      sortedRows: sortedRows,
      actionBar: rowActionBar,
      index: sortedRows[index],
      rowKey: row.data[rowKey],
      rowsLength: sortedRowsLength,
      isEditActive: isEditActive,
      stickyColumns: stickyColumns,
      rowExtraClick: rowExtraClick,
      setToggledRefs: setToggledRefs,
      toggleTooltips: toggleTooltips,
      getDragAcceptType: getDragAcceptType,
      rowExtraClickIconTooltip: rowExtraClickIconTooltip,
      withLeftCorner: withLeftCorner,
      selectableRows: selectableRows,
      selectedRowKey: rowSelectIsControlled ? selectedRow : selectedRowKey,
      initialColWidth: initialColWidth,
      handleSelectRow: handleSelectRow,
      stickyLeftExist: stickyLeftExist,
      handleRowClick: rowToggleHandler,
      stickyRightExist: stickyRightExist,
      disabledColumnPin: disabledColumnPin,
      setSelectedRowKey: setSelectedRowKey,
      hasNestedElements: hasNestedElements,
      virtualizedList: virtualizedList.current,
      rowExtraClickNeeded: rowExtraClickNeeded,
      setToggledRowsKeys: setToggledRowsKeys,
      renderRowNestedChildren: renderRowNestedChildren,
      getExpandIconDisableState: getExpandIconDisableState,
      opened: toggledRowsKeys.includes(row.data[rowKey])
    })));
  };
  const onToggleColumnAutoSize = useCallback((id, autoSizeOn, width) => {
    updateColsInfo(id, autoSizeOn, width);
  }, [updateColsInfo]);
  return /*#__PURE__*/React__default.createElement(DndProvider, {
    backend: HTML5Backend,
    context: window
  }, CustomPreview && /*#__PURE__*/React__default.createElement(CustomPreview, null), /*#__PURE__*/React__default.createElement("div", {
    className: classnames('table-holder', {
      'content-empty': !sortedRowsLength
    }),
    style: {
      '--table-max-width': "".concat(tableWidth, "px")
    },
    ref: holderRef
  }, /*#__PURE__*/React__default.createElement(CustomScrollbar, {
    ref: scrollbar => {
      if (!scrollRef.current) {
        scrollRef.current = scrollbar.view;
      }
    },
    onScrollStart: () => popoverOpenedId && setPopoverOpenedId(null),
    autoHeight: !sortedRowsLength,
    style: {
      height: sortedRowsLength ? "".concat(((sortedRowsLength + (hasFooter ? 2 : 1) + (hasDoubleHeader ? 1 : 0)) * cache._defaultHeight + expandedHeight) / 10, "rem") : null
    }
  }, /*#__PURE__*/React__default.createElement(Header$1, {
    disabledColumnPin: disabledColumnPin,
    sortedRowsLength: sortedRowsLength,
    hasDoubleHeader: hasDoubleHeader,
    groups: groups,
    prefix: prefix,
    sortOrder: type,
    ref: firstColRef,
    columns: columns,
    eventRef: eventRef,
    disabled: !hasData && !hasSearch,
    colsInfo: colsInfo,
    tableRef: holderRef,
    orders: columnsOrder,
    lastColRef: lastColRef,
    actionBar: rowActionBar,
    firstColRef: firstColRef,
    isEditActive: isEditActive,
    sortActiveId: columnDataKey,
    stickyColumns: stickyColumns,
    searchHandler: searchHandler,
    changeOrders: setColumnsOrder,
    withRightCorner: withRightCorner,
    withLeftCorners: withLeftCorners,
    updateColsInfo: updateColsInfo,
    stickyLeftExist: stickyLeftExist,
    initialColWidth: initialColWidth,
    sortableColumns: sortableColumns,
    resizableColumns: resizableColumns,
    setStickyColumns: setStickyColumns,
    draggableColumns: draggableColumns,
    popoverOpenedId: popoverOpenedId,
    handleStickyClick: handleStickyClick,
    setPopoverOpenedId: setPopoverOpenedId,
    changeSortColumn: changeSortColumnThrottled,
    onToggleColumnAutoSize: onToggleColumnAutoSize,
    columnSizeChangeHandler: columnSizeChangeHandler
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "ta-body"
  }, hasData ? infiniteLoader ? /*#__PURE__*/React__default.createElement(InfiniteLoader, {
    isRowLoaded: _ref7 => {
      let {
        index
      } = _ref7;
      return !!rows[index + 1];
    },
    loadMoreRows: getRowsAsync,
    rowCount: sortedRowsLength
  }, _ref8 => {
    let {
      onRowsRendered: onRowsRend,
      registerChild
    } = _ref8;
    return /*#__PURE__*/React__default.createElement(WindowScroller, {
      scrollElement: scrollRef.current
    }, _ref9 => {
      let {
        height,
        isScrolling,
        scrollTop
      } = _ref9;
      return /*#__PURE__*/React__default.createElement(AutoSizer, {
        disableHeight: true
      }, () => /*#__PURE__*/React__default.createElement(List, {
        onRowsRendered: onRowsRend,
        autoHeight: true,
        height: height,
        width: tableWidth,
        ref: ref => {
          virtualizedList.current = ref;
          registerChild.current = ref;
        },
        scrollTop: scrollTop,
        isScrolling: isScrolling,
        rowCount: sortedRowsLength,
        rowHeight: cache.rowHeight,
        rowRenderer: onRowsRendered
      }));
    });
  }) : /*#__PURE__*/React__default.createElement(WindowScroller, {
    scrollElement: scrollRef.current
  }, _ref10 => {
    let {
      height = 0,
      isScrolling,
      scrollTop
    } = _ref10;
    return /*#__PURE__*/React__default.createElement(List, {
      autoHeight: true,
      height: height,
      width: tableWidth,
      ref: virtualizedList,
      scrollTop: scrollTop,
      isScrolling: isScrolling,
      rowCount: sortedRowsLength,
      rowHeight: cache.rowHeight,
      rowRenderer: onRowsRendered
    });
  }) : /*#__PURE__*/React__default.createElement("div", {
    className: "ta-nested-child-holder table-empty-holder"
  }, emptyContent)), !!sortedRowsLength && hasFooter && /*#__PURE__*/React__default.createElement("div", {
    className: "ta-n-body ta-footer"
  }, /*#__PURE__*/React__default.createElement(Footer, {
    colsInfo: colsInfo,
    columnKey: columnKey,
    orders: columnsOrder,
    values: footerValues,
    actionBar: rowActionBar,
    stickyColumns: stickyColumns,
    withLeftCorners: withLeftCorners,
    withRightCorner: withRightCorner,
    stickyLeftExist: stickyLeftExist,
    initialColWidth: initialColWidth
  }))), pagination, loading && /*#__PURE__*/React__default.createElement("div", {
    className: "table-splash-loader"
  }, loader)));
}
Table.eventCounter = 0;
Table.eventName = 'sticky-update';
Table.propTypes = {
  /**
   * sortFn: Custom sort function for columns.((prev: PropTypes.rows[item], next: PropTypes.rows[item], rows: PropTypes.rows, dataKey: string) => {
   *   if (prev is less than next by some ordering criterion) {
   *      return -1;
   *   }
   *    if (prev is greater than next by the ordering criterion) {
   *      return 1;
   *    }
   *    prev is equal to next
   *   return 0;
   *  }
   * })
   *
   * text: Text value for columns
   *
   * render: Function to render custom text.((column: PropTypes.columns[item], index: number, isEditActive: boolean) =>  return any)
   *
   * sortable: Allows sorting if true
   *
   * exportDisabled: Hide that column from export
   *
   * copyTooltipText: Text for the tooltip when you hover on the element that can be copied.
   *
   * copiedTooltipText: Text for the tooltip when you already pressed copy button on the element that can be copied.
   *
   * resizable: Allows resizing if true
   *
   * current column's data key
   *
   * draggable: Allows dragging if true
   *
   * colRenderer: Render custom component on certain column of each row. ((value: string || number, index: number, row: PropTypes.rows[item], isEditActive: boolean) => {
   *  return <div>Hello World</div>})
   *
   * getter: Function to define custom text. ((row: PropTypes.rows[item], index: number, isEditActive: boolean)) => {
   *  return some string
   * })
   *
   * formatter: Function tp format displaying text. ((middleText: string, row: PropTypes.rows[item], index: number, isEditActive: boolean) => {
   *  return some string
   * })
   * hasOptions: Renders dropdown of options in header items
   * searchInColumns: Property for the searching in column. There are  two types for search: "text" && "select". Table should not have the rowsCount property.
   */
  columns: PropTypes.arrayOf(PropTypes.shape({
    sortFn: PropTypes.func,
    ...oneIsRequired({
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      render: PropTypes.func
    }),
    copyable: PropTypes.bool,
    exportDisabled: PropTypes.bool,
    copyTooltipText: PropTypes.string,
    copiedTooltipText: PropTypes.string,
    copyableValue: PropTypes.string,
    sortable: PropTypes.bool,
    resizable: PropTypes.bool,
    dataKey: PropTypes.string,
    draggable: PropTypes.bool,
    colRenderer: PropTypes.func,
    getter: PropTypes.func,
    formatter: PropTypes.func,
    hasOptions: PropTypes.bool,
    searchInColumns: {
      type: PropTypes.oneOf(searchConfigs.types),
      data: PropTypes.array
    },
    sizeParams: {
      autoSizeOn: PropTypes.bool,
      customWidth: PropTypes.number,
      autoSizeWidth: PropTypes.number,
      defaultCustomWidth: PropTypes.number
    }
  })).isRequired,
  /**
   * Called after move row.((dragIndex: number, hoverIndex: number) => custom logic)
   */
  onRowMove: PropTypes.func,
  /**
   * Called after clicking on row.((data: object, index: number, row: PropTypes.rows[item]) => custom logic)
   */
  onRowClick: PropTypes.func,
  /**
   * Pass this function to catch sorting event and get related data
   * ((type: string, dataKey: sting, column: PropTypes.columns[item]) => void
   */
  onSortChange: PropTypes.func,
  /**
   * Custom loader component
   */
  loader: PropTypes.node,
  /**
   * Will render when there are no rows to render
   */
  emptyContent: PropTypes.node,
  /**
   * Key from row's data  which value is surely unique
   */
  rowKey: PropTypes.string.isRequired,
  /**
   * Key from column's data which value is surely unique
   */
  columnKey: PropTypes.string.isRequired,
  /**
   * If all rows should have hover effect you can pass rowsHover true instead of controlling hovNer effect on each rows individually
   */
  rowsHover: PropTypes.bool,
  /**
   * If true then expanded rows will be closed after rows count change
   */
  closeExpandedAfterFilter: PropTypes.bool,
  /**
   * hasHover: Allow hovering
   * data: rows column's data
   * nestedTable: object of columns: array of objects and rows: array of objects
   * className: additional className for row element
   * disabled: disable row
   * render: Render custom component on (row: PropTypes.rows[item] index: number) => {
   *  return <div>Hello World</div>})
   */
  rows: PropTypes.arrayOf(PropTypes.shape({
    hasHover: PropTypes.bool,
    data: PropTypes.object,
    nestedTable: PropTypes.any,
    className: PropTypes.string,
    render: PropTypes.func,
    disabled: PropTypes.bool
  })),
  footer: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
  /**
   *
   */
  selectableRows: PropTypes.bool,
  /** Default minimum width for columns */
  defaultColWidth: PropTypes.number,
  /**
   * Maximum limit for sticky columns.
   */
  stickyColumnsLimitCount: PropTypes.number,
  /**
   * Extra click function for rows. ((e: event object, data: object, index: number, row: PropTypes.rows[item]) => custom logic)
   */
  rowExtraClick: PropTypes.func,
  /**
   * Function for determining which rows should have extra click. ((e: event object, data: object, index: number, row: PropTypes.rows[item]) => return true or false)
   */
  rowExtraClickNeeded: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /**
   * Function which will return React components as row's action bar on the right corner of the row. ((row: PropTypes.rows[item] ,index: number) =>
   *  <button>custom component</button>)
   */
  rowActionBar: PropTypes.func,
  /**
   * Function which should return null or another
   * Function which will return valid node
   */
  renderRowNestedChildren: PropTypes.func,
  /**
   * Array of toggled rows unique keys
   */
  toggledRows: PropTypes.arrayOf(PropTypes.number),
  /**
   * Function which should return boolean
   */
  getExpandIconDisableState: PropTypes.func,
  /**
   * Search Handler
   */
  columnSizeChangeHandler: PropTypes.func,
  /**
   * columnSizeChangeHandler: (params:object) => void
   * with the help of this function you can get column key, size and action type after size changing.
   */
  searchHandler: PropTypes.func,
  /**
   * there is a search in the table if hasSearch is true
   */
  hasSearch: PropTypes.bool,
  /**
   * controls whether columns should be sortable or not
   */
  sortableColumns: PropTypes.bool,
  /**
   * controls whether columns should be resizable or not
   */
  resizableColumns: PropTypes.bool,
  /**
   * controls whether columns should be draggable or not
   */
  draggableColumns: PropTypes.bool,
  /**
   * Value of row data[rowKey]
   */
  selectedRow: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   *  Function to control row selection. ((row: PropTypes.rows[item], index: number, rowKey: string) => void
   */
  handleRowSelect: PropTypes.func,
  /**
   *  Function for controlling column sorting. ((isSortable: boolean, sortData: object) => void
   */
  handleColumnSorting: PropTypes.func,
  /**
   * Type of sorting when it's controlled
   */
  sortType: PropTypes.oneOf(['asc', 'desc', null]),
  /**
   * DataKey of column which was sorted
   */
  sortedColumn: PropTypes.string,
  /**
   * Array of two strings, which will be applied as tooltips to arrow icons of collapsable rows
   */
  toggleTooltips: PropTypes.arrayOf(PropTypes.string),
  /**
   * Tooltips for arrow icons of extra click rows
   */
  rowExtraClickIconTooltip: PropTypes.string,
  /**
   * Initial sort state
   */
  defaultSortType: PropTypes.oneOf(['asc', 'desc']),
  /**
   * Initially sorted column
   */
  defaultSortedColumn: PropTypes.string,
  /**
   * True if u want to load elements when scroll
   */
  infiniteLoader: PropTypes.bool,
  /**
   * Function for fetching new rows
   */
  getRowsAsync: PropTypes.func,
  /**
   * Function for getting draggable element ref
   */
  getDragAcceptType: PropTypes.func,
  /**
   * Custom node for drag preview
   */
  CustomPreview: PropTypes.node,
  /**
   * custom toggle rows
   */
  customToggledRowsKeys: PropTypes.array | undefined
};
Table.defaultProps = {
  footerValues: {},
  rows: [],
  loading: false,
  loader: /*#__PURE__*/React__default.createElement(BusyLoader, {
    className: "p-absolute"
  }),
  onRowMove: noop,
  onSortChange: noop,
  getDragAcceptType: noop,
  emptyContent: /*#__PURE__*/React__default.createElement(Empty, {
    title: "No data to display"
  }),
  columnKey: 'dataKey',
  selectableRows: false,
  stickyColumnsLimitCount: 3,
  defaultColWidth: 150,
  rowToggleHandler: noop,
  toggleTooltips: [],
  rowExtraClickIconTooltip: '',
  defaultSortType: null,
  defaultSortedColumn: null
};

export { Table as T, sortHandler as a, searchConfigs as s, useSortConfigs as u };
